<template>
  <div class="map-page-container">
    <!-- Control Widget Bar -->
    <ControlWidgetBar
      :visible-site-count="visibleSiteCount"
      :total-site-count="totalSiteCount"
      :projects="projectsData"
      :loading-projects="loadingProjects"
      :control-panel-collapsed="controlPanelCollapsed"
      :active-layers-count="activeLayersCount"
      :active-filters-count="selectedAgsVariables.length"
      :active-filters-panel-open="rightPanelMode === 'filters'"
      @toggle-control-panel="onToggleControlPanel"
      @toggle-active-filters-panel="onToggleActiveFiltersPanel"
      @mode-changed="onModeChanged"
      @project-selected="onProjectSelected"
    />

    <!-- Floating Header - Removed, functionality moved to ControlWidgetBar -->
    <!-- <FloatingHeader
      :visible-site-count="visibleSiteCount"
      :total-site-count="totalSiteCount"
      :projects="projectsData"
      @mode-changed="onModeChanged"
      @project-selected="onProjectSelected"
    /> -->

    <!-- Theme Toggle (positioned top-right) -->
    <div class="absolute top-6 right-6 z-20">
      <slot name="theme-toggle">
        <!-- Theme toggle component will be inserted here -->
      </slot>
    </div>

    <!-- Control Panel -->
    <ControlPanel
      :collapsed="controlPanelCollapsed"
      :active-layers-count="activeLayersCount"
      :active-filters-count="activeFiltersCount"
      :dataset-filters-mode="rightPanelMode === 'filters'"
      @toggle="onControlPanelToggle"
      @tab-changed="onTabChanged"
    >
      <!-- Location Search Slot -->
      <template #location-search>
        <LocationSearch
          @location-selected="onLocationSelected"
        />
      </template>

      <!-- Layers Panel Slot -->
      <template #layers-panel>
        <LayerPanel
          :current-zoom="currentZoom"
          :active-layers="activeLayers"
          :loading-layers="loadingLayers"
          @layer-toggled="onLayerToggled"
        />
      </template>

      <!-- Filters Panel Slot -->
      <template #filters-panel>
        <FilterPanel
          :selected-crime-types="selectedCrimeTypes"
          :selected-ags-variables="selectedAgsVariables"
          :selected-ags-category="selectedAgsCategory"
          :comparison-enabled="comparisonEnabled"
          :comparison-scope="comparisonScope"
          :ags-data-opacity="agsDataOpacity"
          :reverse-colors="reverseColors"
          @ags-variables-updated="onAgsVariablesUpdated"
          @ags-category-changed="onAgsCategoryChanged"
          @opacity-changed="onOpacityChanged"
          @comparison-toggled="onComparisonToggled"
          @reverse-colors-toggled="onReverseColorsToggled"
          @scale-direction-changed="onScaleDirectionChanged"
          @weights-changed="onWeightsChanged"
          @location-detected="onLocationDetected"
        />
      </template>

      <!-- Dataset Active Filters Slot -->
      <template #dataset-filters-panel>
        <DatasetActiveFilters
          :dataset-variables="selectedAgsVariables"
          :dataset-category-label="datasetCategoryLabel"
          :comparison-enabled="comparisonEnabled"
          :comparison-scope="comparisonScope"
          :ags-opacity-value="agsDataOpacity * 100"
          @remove-dataset-variable="onRemoveDatasetVariable"
          @scale-direction-changed="onScaleDirectionChanged"
          @weights-changed="onWeightsChanged"
          @comparison-toggled="onComparisonToggled"
          @opacity-changed="onOpacityChanged"
          @clear-all="onClearAllDatasetFilters"
        />
      </template>
    </ControlPanel>

    <!-- Zoom Controls -->
    <ZoomControls
      :current-zoom="currentZoom"
      :min-zoom="minZoom"
      :max-zoom="maxZoom"
      :initial-center="initialMapCenter"
      :has-active-filters="selectedAgsVariables.length > 0"
      :show-legend="showAgsLegend"
      @zoom-changed="onZoomChanged"
      @center-changed="onCenterChanged"
      @toggle-legend="showLegend"
    />

    <!-- Map Legend -->
    <MapLegend
      :crime-color-scale="crimeColorScale"
      :ags-color-scale="agsColorScale"
      :show-crime-legend="showCrimeLegend"
      :show-ags-legend="showAgsLegend"
      :comparison-enabled="comparisonEnabled"
      :comparison-scope="comparisonScope"
      :selected-ags-variables="selectedAgsVariables"
      :reverse-colors="reverseColors"
      @ags-legend-hidden="hideAgsLegend"
    />

    <!-- Google Maps Container -->
    <div
      id="cresiteiq2-map"
      ref="mapContainer"
      class="absolute inset-0 w-full h-full"
      :class="{ 'map-loading': loading }"
      style="z-index: 0 !important;"
      @mousemove="onMapMouseMove"
      @mouseleave="onMapMouseLeave"
    ></div>

    <!-- Floating Tooltip for Directions Mode -->
    <div
      v-if="showDirectionTooltip"
      :style="tooltipStyle"
      class="fixed pointer-events-none z-[2000] px-3 py-1 rounded text-white text-xs font-semibold shadow-lg border"
      style="background-color: #1e3a8a; border-color: #93c5fd;"
    >
      {{ directionTooltipText }}
    </div>

    <!-- Zoom Level Indicator (Only show on layers tab) -->
    <ZoomNotice :show="activeTab === 'layers'" />

    <!-- Transit Layer Dark Mode Notice (Centered) -->
    <TransitNotice :show="activeLayers.includes('transit') && isDarkMode" />

    <!-- AGS Data Popup (includes crime data as AGS variables) -->
    <AgsPopup
      :show="showAgsPopup || showCrimePopup"
      :feature="selectedFeature"
      :selected-variables="selectedAgsVariables"
      :comparison-scope="comparisonScope"
      :layer-type="currentLayerType"
      :reverse-colors="reverseColors"
      @close="closeAgsPopup"
    />

    <!-- Customer Popup -->
    <CustomerPopup
      v-if="showCustomerPopup && selectedCustomer"
      :customer="selectedCustomer"
      :show="showCustomerPopup"
      @close="closeCustomerPopup"
    />

    <!-- Site Popup -->
    <SitePopup
      v-if="showSitePopup && selectedSite"
      :site="selectedSite"
      :show="showSitePopup"
      @close="closeSitePopup"
    />

    <!-- Comp Popup -->
    <CompPopup
      v-if="showCompPopup && selectedComp"
      :comp="selectedComp"
      :show="showCompPopup"
      @close="closeCompPopup"
    />

    <!-- Airport Popup -->
    <AirportPopup
      v-if="showAirportPopup && selectedAirport"
      :airport="selectedAirport"
      :show="showAirportPopup"
      @close="closeAirportPopup"
    />

    <!-- Port Popup -->
    <PortPopup
      v-if="showPortPopup && selectedPort"
      :port="selectedPort"
      :show="showPortPopup"
      @close="closePortPopup"
    />

    <!-- Team Popup -->
    <TeamPopup
      v-if="showTeamPopup && selectedTeamMember"
      :member="selectedTeamMember"
      :show="showTeamPopup"
      @close="closeTeamPopup"
    />

    <!-- Wildfire Popup -->
    <WildfirePopup
      v-if="showWildfirePopup && selectedWildfire"
      :wildfire="selectedWildfire"
      :show="showWildfirePopup"
      @close="closeWildfirePopup"
    />

    <!-- Protest Popup -->
    <ProtestPopup
      v-if="showProtestPopup && selectedProtest"
      :protest="selectedProtest"
      :show="showProtestPopup"
      :filters="protestFilters"
      @close="closeProtestPopup"
    />

    <!-- Protest Filter Panel -->
    <!-- Never show - filters should only appear in Active Filters Panel -->
    <div v-if="false" class="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-30 w-[800px]">
      <ProtestFilterPanel
        :filters="protestFilters"
        :show="showProtestFilters"
        :protest-data="protestsData ? protestsData.features : []"
        @update:filters="updateProtestFilters"
      />
    </div>

    <!-- Active Filters Panel (layer filters only — left side) -->
    <ActiveFiltersPanel
      :active-filters="activeFiltersState"
      :collapsed="activeFiltersPanelCollapsed"
      @toggle="onActiveFiltersPanelToggle"
      @clear-all="clearAllActiveFilters"
      @filter-updated="() => {}"
      @load-highways="updateHighwayStates"
    >
      <template #filter-accordion="{ filter, isExpanded, toggle }">
        <FilterAccordionItem
          :filter-type="filter.type"
          :filter-label="filter.label"
          :filter-icon="filter.icon"
          :filter-data="filter.data"
          :is-expanded="isExpanded"
          @toggle="toggle"
          @filter-updated="(data) => handleFilterUpdated({ filterType: filter.type, filterData: data })"
          v-on="$listeners"
        >
          <!-- Embed filter panel components -->
          <TravelTimeFilterPanel
            v-if="filter.type === 'travelTime'"
            :show="true"
            :filters="travelTimeFilters"
            :buildings="availableBuildings"
            :loading="travelTimeLayerLoading"
            :active-layers="activeLayers"
            @update:filters="updateTravelTimeFilters"
            @layer-toggled="onLayerToggled"
            @close="() => {}"
          />
          <HighwayFilterPanel
            v-if="filter.type === 'highway'"
            :selected-states="selectedHighwayStates"
            :available-states="availableStates"
            :on-load-highways="updateHighwayStates"
          />
          <ProtestFilterPanel
            v-if="filter.type === 'protest'"
            :filters="protestFilters"
            :show="true"
            :protest-data="protestsData ? protestsData.features : []"
            @update:filters="updateProtestFilters"
          />
          <DirectionsPanel
            v-if="filter.type === 'directions'"
          />
          <TrafficFilterPanel
            v-if="filter.type === 'traffic'"
            :show="true"
            :filters="trafficFilters"
            @update:filters="updateTrafficFilters"
            @close="() => {}"
          />
        </FilterAccordionItem>
      </template>
    </ActiveFiltersPanel>

    <!-- Highway Filter Panel -->
    <!-- Never show - filters should only appear in Active Filters Panel -->
    <div v-if="false" class="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-30">
      <HighwayFilterPanel
        :show="showHighwayFilter"
        :selected-states="selectedHighwayStates"
        :available-states="availableStates"
        @load-highways="loadHighwaysForStates"
        @close="showHighwayFilter = false"
      />
    </div>

    <!-- Highway Popup -->
    <HighwayPopup
      :show="showHighwayPopup"
      :highway="selectedHighway"
      @close="closeHighwayPopup"
    />

    <!-- Waterway Popup -->
    <WaterwayPopup
      v-if="showWaterwayPopup && selectedWaterway"
      :waterway="selectedWaterway"
      :show="showWaterwayPopup"
      @close="closeWaterwayPopup"
    />

    <!-- Rail Popup -->
    <RailPopup
      v-if="showRailPopup && selectedRail"
      :rail="selectedRail"
      :show="showRailPopup"
      @close="closeRailPopup"
    />

    <!-- Travel Time Filter Panel Toggle Button -->
    <!-- Never show - filters should only appear in Active Filters Panel -->
    <button
      v-if="false"
      @click="travelTimeFiltersCollapsed = false"
      type="button"
      class="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-30 px-6 py-3 bg-white rounded-lg shadow-lg border border-gray-300 hover:shadow-xl transition-all duration-200 flex items-center gap-2"
    >
      <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10"/>
        <path d="M12 8v8m-4-4h8"/>
      </svg>
      <span class="font-semibold text-gray-700">Travel Time Filters</span>
    </button>

    <!-- Travel Time Filter Panel -->
    <!-- Never show - filters should only appear in Active Filters Panel -->
    <div v-if="false" class="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-30 w-[600px]">
      <TravelTimeFilterPanel
        :filters="travelTimeFilters"
        :show="showTravelTimeFilters"
        :buildings="availableBuildings"
        :loading="travelTimeLayerLoading"
        :collapsed="travelTimeFiltersCollapsed"
        @update:filters="updateTravelTimeFilters"
        @close="travelTimeFiltersCollapsed = true"
      />
    </div>

    <!-- Hover Tooltip -->
    <AgsHoverTooltip
      :show="showHoverTooltip"
      :feature="hoverFeature"
      :tooltip-style="hoverTooltipStyle"
      :selected-variables="selectedAgsVariables"
      :layer-type="currentLayerType"
    />



    <!-- Loading Overlay -->
    <div
      v-if="loading"
      class="absolute inset-0 flex items-center justify-center z-50"
      style="background: rgba(0,0,0,0.45);"
    >
      <div class="text-center" style="width: 320px; background: rgba(255,255,255,0.08); backdrop-filter: blur(8px); border: 1px solid rgba(255,255,255,0.15); border-radius: 12px; padding: 24px 28px;">
        <p class="text-white font-semibold mb-4" style="font-size: 14px; letter-spacing: 0.02em;">Loading map data...</p>
        <!-- Progress bar track -->
        <div class="rounded-full overflow-hidden" style="height: 10px; background: rgba(255,255,255,0.15);">
          <div
            class="h-full rounded-full transition-all ease-out"
            :style="{
              width: loadProgress + '%',
              background: 'linear-gradient(90deg, #16a34a, #4ade80)',
              boxShadow: '0 0 12px rgba(74,222,128,0.8)',
              transitionDuration: loadProgress === 100 ? '200ms' : '300ms'
            }"
          ></div>
        </div>
        <!-- Percentage -->
        <p class="mt-3 font-bold" style="font-size: 15px; color: #4ade80;">{{ Math.round(loadProgress) }}%</p>
      </div>
    </div>

    <!-- Layer Loading Overlay -->
    <div
      v-if="layerLoading || travelTimeLayerLoading || protestLayerLoading || customerLayerLoading || siteLayerLoading"
      class="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center z-40 pointer-events-none"
    >
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center">
        <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
        <p class="mt-3 text-sm font-medium text-gray-700 dark:text-gray-300">Loading layer...</p>
      </div>
    </div>

    <!-- Error Notification -->
    <div
      v-if="error"
      class="absolute top-20 left-1/2 transform -translate-x-1/2 z-50 max-w-md"
    >
      <div class="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg shadow-lg">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div class="ml-3 flex-1">
            <p class="text-sm text-red-800">{{ error }}</p>
          </div>
          <button
            @click="dismissError"
            class="ml-3 flex-shrink-0 text-red-400 hover:text-red-600"
          >
            <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import cresiteiq2Api from '../../../cresiteiq2/services/cresiteiq2Api.js';
import {
  DEFAULT_MAP_SETTINGS,
  DARK_MAP_STYLES,
  ZOOM_LEVELS,
  LAYER_TYPES,
  COMPARISON_SCOPES,
  LOCATION_ZOOM_LEVELS,
  ERROR_MESSAGES
} from './constants.js';
import { debounce, formatAgsValue, getRiskLevelBadgeClass } from './utils/mapHelpers.js';

export default {
  name: 'MapPage',

  components: {
    // KAN-173: All components loaded async for code splitting.
    // Core UI (ControlWidgetBar, ControlPanel) load immediately.
    // Popups only mount on user interaction via v-if, so async is safe.
    ControlWidgetBar: () => import('./ControlWidgetBar.vue'),
    ControlPanel: () => import('./ControlPanel.vue'),
    LocationSearch: () => import('./LocationSearch.vue'),
    LayerPanel: () => import('./LayerPanel.vue'),
    FilterPanel: () => import('./FilterPanel.vue'),
    DatasetActiveFilters: () => import('./DatasetActiveFilters.vue'),
    ZoomControls: () => import('./ZoomControls.vue'),
    MapLegend: () => import('./MapLegend.vue'),
    AgsPopup: () => import('./AgsPopup.vue'),
    AgsHoverTooltip: () => import('./AgsHoverTooltip.vue'),
    CustomerPopup: () => import('./CustomerPopup.vue'),
    SitePopup: () => import('./SitePopup.vue'),
    CompPopup: () => import('./CompPopup.vue'),
    AirportPopup: () => import('./AirportPopup.vue'),
    PortPopup: () => import('./PortPopup.vue'),
    TeamPopup: () => import('./TeamPopup.vue'),
    WildfirePopup: () => import('./WildfirePopup.vue'),
    ProtestPopup: () => import('./ProtestPopup.vue'),
    ProtestFilterPanel: () => import('./ProtestFilterPanel.vue'),
    HighwayFilterPanel: () => import('./HighwayFilterPanel.vue'),
    HighwayPopup: () => import('./HighwayPopup.vue'),
    WaterwayPopup: () => import('./WaterwayPopup.vue'),
    RailPopup: () => import('./RailPopup.vue'),
    TravelTimeFilterPanel: () => import('./TravelTimeFilterPanel.vue'),
    TrafficFilterPanel: () => import('./TrafficFilterPanel.vue'),
    DirectionsPanel: () => import('./DirectionsPanel.vue'),
    ActiveFiltersPanel: () => import('./ActiveFiltersPanel.vue'),
    FilterAccordionItem: () => import('./FilterAccordionItem.vue'),
    TransitNotice: () => import('./TransitNotice.vue'),
    ZoomNotice: () => import('./ZoomNotice.vue'),
  },
  created() {

    // Vue does not proxy keys beginning with an underscore, so these were

    // never reactive state. They are plain instance properties.

    this._progressTimer = null;

  },
  data() {
    return {
      // Google Maps instance
      map: null,

      // Theme state
      isDarkMode: false,

      // Map state
      currentZoom: DEFAULT_MAP_SETTINGS.zoom,
      mapCenter: { ...DEFAULT_MAP_SETTINGS.center },
      initialMapCenter: { ...DEFAULT_MAP_SETTINGS.center },
      currentBounds: null,
      minZoom: ZOOM_LEVELS.MIN,
      maxZoom: ZOOM_LEVELS.MAX,

      // API Response Cache
      apiResponseCache: new Map(),
      maxCacheSize: 50, // Limit cache size to prevent memory issues
      cacheExpiryMs: 5 * 60 * 1000, // 5 minutes

      // UI state
      controlPanelCollapsed: true, // Start with panel closed
      loading: true,
      loadProgress: 0,
      layerLoading: false, // Loading state for layer operations
      error: null,
      activeTab: 'layers', // Track active tab

      // Layer state
      activeLayers: [],
      loadingLayers: [], // Layers currently fetching data
      trafficLayer: null, // Google Maps TrafficLayer instance
      transitLayer: null, // Google Maps TransitLayer instance
      airportMarkers: [], // Array of Google Maps marker instances for airports
      airportsData: null, // Cached airport data from API
      loadingAirports: false, // Loading state for airport data
      portMarkers: [], // Array of Google Maps marker instances for ports
      portsData: null, // Cached port data from API
      currentPortInfoWindow: null, // Currently open port info window
      currentAirportInfoWindow: null, // Currently open airport info window
      teamMarkers: [], // Array of Google Maps marker instances for team members
      teamMembersData: null, // Cached employee data from API
      loadingTeamMembers: false, // Loading state for team data
      currentTeamInfoWindow: null, // Currently open team info window
      customerMarkers: [], // Array of Google Maps marker instances for customers
      customerData: [], // Raw customer data from API
      customerMarkerClusterer: null, // MarkerClusterer instance for customers
      selectedCustomer: null, // Currently selected customer
      showCustomerPopup: false, // Customer popup visibility
      customerLayerLoading: false, // Loading state for customer layer
      siteMarkers: [], // Array of Google Maps marker instances for sites
      sitesData: null, // Cached site data from API
      siteMarkerClusterer: null, // MarkerClusterer instance for sites
      selectedSite: null, // Currently selected site
      showSitePopup: false, // Site popup visibility
      siteLayerLoading: false, // Loading state for site layer

      // Site/Project toggle state
      viewMode: 'sites', // Current mode: 'sites' or 'projects' - defaults to sites
      selectedProjectId: null, // Currently selected project ID
      projectsData: [], // Cached projects data from API
      projectCompsData: [], // Comps for the selected project
      filteredSitesData: null, // Filtered sites based on project selection
      projectMarker: null, // Google Maps marker instance for selected project
      compMarkers: [], // Array of Google Maps marker instances for comps
      projectCompsMarkers: [], // Array of objects with marker and comp data
      currentCompInfoWindow: null, // Currently open comp info window
      loadingSites: false, // Loading state for sites data
      loadingProjects: false, // Loading state for projects list
      loadingComps: false, // Loading state for project comps
      selectedComp: null, // Currently selected comp
      showCompPopup: false, // Comp popup visibility

      selectedAirport: null, // Currently selected airport
      showAirportPopup: false, // Airport popup visibility
      selectedPort: null, // Currently selected port
      showPortPopup: false, // Port popup visibility
      selectedTeamMember: null, // Currently selected team member
      showTeamPopup: false, // Team popup visibility
      wildfireMarkers: [], // Array of Google Maps marker instances for wildfires
      wildfiresData: null, // Cached wildfire GeoJSON data
      selectedWildfire: null, // Currently selected wildfire
      showWildfirePopup: false, // Wildfire popup visibility
      protestMarkers: [], // Array of Google Maps marker instances for protests
      protestsData: null, // Cached protest GeoJSON data
      selectedProtest: null, // Currently selected protest
      showProtestPopup: false, // Protest popup visibility
      protestLayerLoading: false, // Loading state for protest layer
      protestFilters: {
        dateRange: null,
        searchText: '',
        selectedFactors: []
      },
      showProtestFilters: false, // Protest filter panel visibility

      // Highway layer state
      allHighwaysData: { type: 'FeatureCollection', features: [] }, // All loaded highway features
      loadedHighwayStates: {}, // Track which states have been loaded: { 'CA': true, 'NY': true }
      selectedHighwayStates: [], // Array of selected state codes
      showHighwayFilter: false, // Controls HighwayFilterPanel visibility
      selectedHighway: null, // Currently selected highway for popup
      showHighwayPopup: false, // Controls HighwayPopup visibility
      
      // AI Search - Track if location was detected
      aiDetectedLocation: null, // Stores location detected from AI search
      skipNextDataAvailabilityZoom: false, // Flag to prevent handleDataAvailability from overriding auto-zoom
      availableStates: [], // List of US states for highway filter
      highwayColor: '#6366f1', // Highway stroke color
      highwayStrokeWeight: 3, // Highway stroke weight

      // Waterways layer state
      waterwayPolylines: [], // Array of Google Maps Polyline instances
      waterwaysData: null, // Cached waterways GeoJSON data
      selectedWaterway: null, // Currently selected waterway for popup
      showWaterwayPopup: false, // Waterway popup visibility

      // Rail layer state
      railPolylines: [], // Array of Google Maps Polyline instances
      railData: null, // Cached rail GeoJSON data
      selectedRail: null, // Currently selected rail line for popup
      showRailPopup: false, // Rail popup visibility
      railColor: '#b45309', // Rail stroke color (amber-700)
      railStrokeWeight: 3, // Rail stroke weight

      // Travel Time layer state
      showTravelTimeFilters: false, // Travel time filter panel visibility
      travelTimeFiltersCollapsed: true, // Travel time filter panel collapsed state
      travelTimeFilters: {
        searchMode: 'building',
        travelMode: 'driving',
        travelTime: 1800,
        buildingId: null,
        locationQuery: '',
        locationCoordinates: null,
        arriveOrDepart: 'depart',
        arriveOrDepartTime: '7',
        trafficMultiplier: 0.65 // Traffic condition multiplier (0.5 = light traffic/large area, 0.65 = normal, 1.0 = heavy traffic/small area)
      },

      // Traffic layer state
      trafficFilters: {
        trafficType: 'live',
        dayOfWeek: 1, // Monday
        timeOfDay: 9 // 9:00 AM
      },

      // Directions layer state
      directionsRenderer: null, // Google Maps DirectionsRenderer instance
      travelTimePolygons: [], // Array of Google Maps polygon instances
      availableBuildings: [], // Buildings for dropdown selection
      travelTimeLayerLoading: false, // Loading state for travel time layer
      locationAutocomplete: null, // Google Maps Places Autocomplete instance

      // Directions tooltip state
      tooltipX: 0, // Mouse X position for tooltip
      tooltipY: 0, // Mouse Y position for tooltip
      tooltipVisible: false, // Tooltip visibility state

      // Filter state
      selectedCrimeTypes: [],
      selectedAgsVariables: [],
      selectedAgsCategory: '', // Persisted category selection across panel toggles
      comparisonEnabled: false,
      comparisonScope: COMPARISON_SCOPES.STATE,

      // Active Filters Panel state
      activeFiltersPanelCollapsed: false, // Start expanded when filters are active

      // Right panel mode: 'controls' = Map Controls, 'filters' = Dataset Active Filters
      rightPanelMode: 'controls',

      // AGS scale directions and weights
      agsScaleDirections: {}, // path -> boolean (true = higher is better)
      agsWeights: {}, // path -> decimal (0-1)

      // Opacity state
      agsDataOpacity: 0.7,
      reverseColors: false,

      // Legend state
      showCrimeLegend: false,
      showAgsLegend: false,
      crimeColorScale: [],
      agsColorScale: [],

      // Map data layers
      polygonLayers: [],

      // Popup state
      showCrimePopup: false,
      showAgsPopup: false,
      selectedFeature: null,
      selectedPolygonColor: null,
      currentLayerType: 'states',

      // Hover tooltip state
      showHoverTooltip: false,
      hoverFeature: null,
      hoverTooltipStyle: {
        left: '0px',
        top: '0px'
      },

      // Wildfire hover tooltip state
      showWildfireTooltip: false,
      hoverWildfire: null,
      wildfireTooltipStyle: {
        left: '0px',
        top: '0px'
      },

      // Available crime types for popup
      availableCrimeTypes: [
        { value: 'violent_crime', label: 'Violent Crime' },
        { value: 'murder', label: 'Murder' },
        { value: 'rape', label: 'Rape' },
        { value: 'robbery', label: 'Robbery' },
        { value: 'assault', label: 'Assault' },
        { value: 'property_crime', label: 'Property Crime' },
        { value: 'burglary', label: 'Burglary' },
        { value: 'larceny', label: 'Larceny' },
        { value: 'motor_vehicle_theft', label: 'Motor Vehicle Theft' }
      ],

      // Responsive state
      isMobile: false,

      // Debug state
      showDebugInfo: false,

      // Active layers tracking

      // Request cancellation
      currentRequestController: null,

      // Debounced methods
      debouncedUpdateMapLayers: null,

      // Performance optimizations
      cachedBoundsKey: null, // Cached bounds string to avoid repeated serialization
      apiCallCount: 0, // Track number of API calls for monitoring
      lastApiParams: null, // Track last API params to detect duplicates
      lastApiCallTime: null, // Track last API call timestamp
      pendingApiRequest: null, // Track pending API request promise for deduplication
      lastSuccessfulParams: null // Track last successful API params to avoid refetching same data
    };
  },

  computed: {
    /**
     * Count of active layers
     */
    activeLayersCount() {
      return this.activeLayers.length;
    },

    /**
     * Count of active filters
     */
    activeFiltersCount() {
      return this.selectedCrimeTypes.length + this.selectedAgsVariables.length;
    },

    /**
     * Active filters state for ActiveFiltersPanel
     */
    activeFiltersState() {
      const filters = {};

      // Travel Time filter - only show when layer is active
      if (this.activeLayers.includes('travel_time')) {
        filters.travelTime = {
          enabled: true,
          label: 'Travel Time',
          icon: 'clock',
          data: this.travelTimeFilters
        };
      }

      // Highway filter - only show when layer is active
      if (this.activeLayers.includes('highways')) {
        filters.highway = {
          enabled: true,
          label: 'Highway',
          icon: 'road',
          data: {
            selectedStates: this.selectedHighwayStates,
            availableStates: this.availableStates
          }
        };
      }

      // Protest filter - only show when layer is active
      if (this.activeLayers.includes('protests')) {
        filters.protest = {
          enabled: true,
          label: 'Protest',
          icon: 'alert',
          data: this.protestFilters
        };
      }

      // Directions filter - only show when layer is active
      if (this.activeLayers.includes('directions')) {
        filters.directions = {
          enabled: true,
          label: 'Route Planning',
          icon: 'navigation',
          data: {}
        };
      }

      // Traffic filter - only show when layer is active
      if (this.activeLayers.includes('traffic')) {
        filters.traffic = {
          enabled: true,
          label: 'Traffic',
          icon: 'lightning',
          data: this.trafficFilters
        };
      }

      return filters;
    },

    /**
     * Check if crime filters should be shown at current zoom
     */
    showCrimeFilters() {
      return cresiteiq2Api.shouldShowCrimeFilters(this.currentZoom);
    },

    /**
     * Check if zoom level is too low for travel time visualization
     * Travel time works best at zoom level 10 or higher
     */
    needsZoomForTravelTime() {
      return this.currentZoom < 10;
    },

    /**
     * Check if directions layer is active
     */
    isDirectionsLayerActive() {
      return this.activeLayers.includes('directions');
    },

    /**
     * Show direction tooltip when directions layer is active and mouse is over map
     */
    showDirectionTooltip() {
      return this.activeLayers.includes('directions') && this.tooltipVisible;
    },

    /**
     * Direction tooltip text based on current state
     */
    directionTooltipText() {
      if (!this.$store.state.reatlas?.directions?.stops[0] || !this.$store.state.reatlas.directions.stops[0].latLng) {
        return 'Click to select Origin';
      } else {
        return 'Click to select Destination';
      }
    },

    /**
     * Tooltip position style
     */
    tooltipStyle() {
      return {
        left: this.tooltipX + 16 + 'px',
        top: this.tooltipY + 16 + 'px'
      };
    },

    /**
     * Count of visible site markers on the map
     */
    visibleSiteCount() {
      return this.siteMarkers.length;
    },

    /**
     * Total count of sites (filtered or all)
     */
    totalSiteCount() {
      const activeSites = this.getActiveSitesData();
      return activeSites ? activeSites.length : 0;
    },

    /**
     * Consolidated computed property that tracks all map data dependencies
     * When any of these values change, the watcher will trigger a single debounced update
     * This prevents multiple simultaneous API calls
     */
    mapDataDependencies() {
      return {
        zoom: this.currentZoom,
        bounds: this.cachedBoundsKey, // Use cached bounds string instead of recalculating
        crimeTypes: this.selectedCrimeTypes, // Direct reference - Vue reactivity will detect changes
        agsVariables: this.selectedAgsVariables, // Direct reference
        comparison: this.comparisonEnabled,
        comparisonScope: this.comparisonScope,
        scaleDirections: this.agsScaleDirections, // Direct reference
        weights: this.agsWeights, // Direct reference
        activeTab: this.activeTab
      };
    },

    /**
     * Category label for the dataset active filters panel header
     * Shows the category name of the first selected variable
     */
    datasetCategoryLabel() {
      if (this.selectedAgsVariables.length === 0) return '';
      const first = this.selectedAgsVariables[0];
      return first.category_name || '';
    }
  },

  watch: {
    /**
     * Consolidated watcher for all map data dependencies
     * This replaces multiple individual watchers to prevent duplicate API calls
     */
    mapDataDependencies: {
      handler(newVal, oldVal) {
        // Only trigger update if we're on the filters tab
        if (this.activeTab !== 'filters') {
          return;
        }

        // Skip if nothing actually changed (Vue sometimes triggers on same values)
        if (oldVal && JSON.stringify(newVal) === JSON.stringify(oldVal)) {
          console.log('[MapPage] Skipping update - no actual changes detected');
          return;
        }
        
        // Check if zoom actually changed (special handling)
        const zoomChanged = newVal.zoom !== oldVal?.zoom;
        
        if (zoomChanged) {
          console.log('[MapPage] Zoom changed to', newVal.zoom, '- reloading layers');
        }
        
        // Use debounced update with longer delay for better batching
        this.debouncedUpdateMapLayers();
      },
      deep: true
    },

    /**
     * Watch for theme changes and update highway colors
     */
    isDarkMode() {
      // Update highway color based on theme
      this.highwayColor = this.isDarkMode ? '#6366f1' : '#4f46e5';

      // Redraw highways with new color if they're displayed
      if (this.activeLayers.includes('highways') && this.selectedHighwayStates.length > 0) {
        this.filterAndDrawHighways();
      }

      // Update waterway polyline colors when theme changes
      if (this.activeLayers.includes('waterways') && this.waterwayPolylines.length > 0) {
        this.waterwayPolylines.forEach(polyline => {
          const waterwayData = polyline.get('waterwayData');
          if (waterwayData) {
            const classification = waterwayData.classification || 2;
            polyline.setOptions({
              strokeColor: this.getWaterwayColor(classification)
            });
          }
        });
      }

      // Update rail polyline colors when theme changes
      if (this.activeLayers.includes('rail') && this.railPolylines.length > 0) {
        const newColor = this.getRailColor();
        this.railPolylines.forEach(polyline => {
          polyline.setOptions({ strokeColor: newColor });
        });
      }
    },

    /**
     * Watch for active filters changes to auto-expand the panel.
     * KAN-173: Watch the count (primitive) instead of deep-scanning the object.
     */
    activeFiltersCount: {
      handler(newCount) {
        if (newCount > 0 && this.activeFiltersPanelCollapsed) {
          this.activeFiltersPanelCollapsed = false;
        }
      },
      immediate: true
    }
  },

  mounted() {
    console.log('[MapPage] mounted - onLayerToggled method exists:', typeof this.onLayerToggled);

    // Initialize debounced methods
    this.debouncedUpdateMapLayers = debounce(
      this.updateMapLayers.bind(this),
      500 // Increased from 300ms to 500ms for better batching
    );

    // Check if mobile
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);

    // Detect initial dark mode
    this.detectDarkMode();

    // Watch for theme changes
    this.observeThemeChanges();

    // Initialize Google Maps
    this.initializeMap();
  },

  beforeDestroy() {
    // Clean up map event listeners
    if (this.map) {
      google.maps.event.clearInstanceListeners(this.map);
    }

    // Clear polygon layers
    this.clearAllLayers();

    // Clean up traffic layer
    if (this.trafficLayer) {
      this.trafficLayer.setMap(null);
      this.trafficLayer = null;
    }

    // Clean up transit layer
    if (this.transitLayer) {
      this.transitLayer.setMap(null);
      this.transitLayer = null;
    }

    // Clean up airport markers
    this.clearAirportMarkers();

    // Clean up port markers
    this.clearPortMarkers();

    // Clean up team markers
    this.clearTeamMarkers();

    // Clean up customer markers
    this.clearCustomerMarkers();

    // Clean up site markers
    this.clearSiteMarkers();

    // Clean up project marker
    this.clearProjectMarker();

    // Clean up wildfire markers
    if (this.wildfireMarkers && this.wildfireMarkers.length > 0) {
      this.wildfireMarkers.forEach(marker => {
        if (marker) {
          google.maps.event.clearInstanceListeners(marker);
          marker.setMap(null);
        }
      });
      this.wildfireMarkers = [];
    }

    // Clean up highway data features
    this.removeHighwayDataFeatures();

    // Clean up waterway polylines
    this.clearWaterwayPolylines();

    // Clean up rail polylines
    this.clearRailPolylines();

    // Clean up directions renderer
    if (this.directionsRenderer) {
      this.directionsRenderer.setMap(null);
      this.directionsRenderer = null;
      console.log('[Directions] DirectionsRenderer cleaned up');
    }

    // Clean up location autocomplete
    this.destroyLocationAutocomplete();

    // Remove window event listeners
    window.removeEventListener('resize', this.checkMobile);

    // Disconnect theme observer
    if (this._themeObserver) {
      this._themeObserver.disconnect();
    }

    // Clean up progress bar timer
    clearInterval(this._progressTimer);
    this._progressTimer = null;
  },

  methods: {
    /**
     * Initialize Google Maps
     */
    initializeMap() {
      // Check if Google Maps is loaded
      if (!window.google || !window.google.maps) {
        console.log('Google Maps API not loaded yet, waiting...');

        // Wait for Google Maps to load
        const checkGoogleMaps = setInterval(() => {
          if (window.google && window.google.maps) {
            clearInterval(checkGoogleMaps);
            this.createMap();
          }
        }, 100);

        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkGoogleMaps);
          if (!window.google || !window.google.maps) {
            this.error = ERROR_MESSAGES.MAP_INIT_FAILED;
            this.loading = false;
            console.error('Google Maps API failed to load within timeout');
          }
        }, 10000);

        return;
      }

      this.createMap();
    },

    /**
     * Create the map instance
     */
    createMap() {
      try {
        // Create map instance with theme-aware styles
        this.map = new google.maps.Map(this.$refs.mapContainer, {
          center: this.mapCenter,
          zoom: this.currentZoom,
          mapTypeId: DEFAULT_MAP_SETTINGS.mapTypeId,
          styles: this.isDarkMode ? DARK_MAP_STYLES : [],
          ...DEFAULT_MAP_SETTINGS.options
        });

        // Add event listeners
        this.map.addListener('zoom_changed', this.onMapZoomChanged);
        this.map.addListener('bounds_changed', this.onMapBoundsChanged);

        // Add data layer click listener for highways
        this.map.data.addListener('click', this.onHighwayDataLayerClick);

        // Store map instance in Vuex for access by DirectionForm and other components
        if (this.$store && this.$store.state.reatlas) {
          this.$store.state.reatlas.map = this.map;
          console.log('[Map] Map instance stored in Vuex store');
        }

        // Map initialized successfully
        this.loading = false;
        console.log('[MapPage] Map initialized successfully');
        console.log('[MapPage] Current viewMode:', this.viewMode);
        console.log('[MapPage] About to check if should load site markers...');

        // Load site markers on initial load (default view is 'sites')
        if (this.viewMode === 'sites') {
          console.log('[MapPage] Calling loadSiteMarkers()...');
          this.loadSiteMarkers();
        } else {
          console.log('[MapPage] NOT loading site markers, viewMode is:', this.viewMode);
        }
      } catch (error) {
        this.error = ERROR_MESSAGES.MAP_INIT_FAILED;
        this.loading = false;
        console.error('Failed to initialize map:', error);
      }
    },

    /**
     * Handle active filters panel toggle
     */
    onActiveFiltersPanelToggle(collapsed) {
      this.activeFiltersPanelCollapsed = collapsed;
      console.log('[Active Filters] Panel toggled, collapsed:', collapsed);
    },

    setLayerLoading(layerId) {
      if (!this.loadingLayers.includes(layerId)) {
        this.loadingLayers.push(layerId);
      }
    },

    clearLayerLoading(layerId) {
      const idx = this.loadingLayers.indexOf(layerId);
      if (idx > -1) this.loadingLayers.splice(idx, 1);
    },

    /**
     * Update map theme based on dark mode
     */
    updateMapTheme(isDark) {
      if (!this.map) return;

      // Only apply theme styles if satellite layer is not active
      // Satellite imagery has its own appearance and doesn't need theme styling
      if (!this.activeLayers.includes('satellite')) {
        this.map.setOptions({
          styles: isDark ? DARK_MAP_STYLES : []
        });
      }
    },

    /**
     * Handle map zoom changes
     */
    onMapZoomChanged() {
      const newZoom = this.map.getZoom();
      console.log('[MapPage] Map zoom changed:', newZoom, 'Active tab:', this.activeTab);

      if (newZoom !== this.currentZoom) {
        const oldZoom = this.currentZoom;
        this.currentZoom = newZoom;

        // Check if zoom level changed enough to trigger layer type change
        const oldLayerType = cresiteiq2Api.getLayerForZoom(oldZoom);
        const newLayerType = cresiteiq2Api.getLayerForZoom(newZoom);

        // Close popup if layer type changed (e.g., from states to tracts)
        if (oldLayerType !== newLayerType && (this.showAgsPopup || this.showCrimePopup)) {
          console.log('[MapPage] Closing popups due to layer type change:', oldLayerType, '->', newLayerType);
          this.closeAgsPopup();
          this.closeCrimePopup();
        }

        // Zoom change is now handled by the consolidated mapDataDependencies watcher
        // No need to call debouncedUpdateMapLayers here
      }
    },

    /**
     * Handle map bounds changes
     */
    onMapBoundsChanged() {
      const bounds = this.map.getBounds();

      if (!bounds) {
        console.log('[MapPage] Bounds not available yet');
        return;
      }

      this.currentBounds = bounds;

      // Cache the serialized bounds string to avoid repeated serialization in computed property
      this.cachedBoundsKey = `${bounds.getNorthEast().lat()},${bounds.getNorthEast().lng()},${bounds.getSouthWest().lat()},${bounds.getSouthWest().lng()}`;

      console.log('[MapPage] Map bounds changed:', {
        ne: { lat: bounds.getNorthEast().lat(), lng: bounds.getNorthEast().lng() },
        sw: { lat: bounds.getSouthWest().lat(), lng: bounds.getSouthWest().lng() }
      });

      // Update available buildings for travel time layer if it's active
      // BUT don't reload if a building is already selected (to preserve the full list)
      if (this.activeLayers.includes('travel_time') && this.showTravelTimeFilters && !this.travelTimeFilters.buildingId) {
        console.log('[MapPage] Travel time layer active - updating available buildings');
        this.loadAvailableBuildings();
      }

      // Reload layers when bounds change (panning) if filter tab is active
      // The consolidated watcher will handle this via cachedBoundsKey change
      if (this.activeTab === 'filters') {
        console.log('[MapPage] Bounds changed - will trigger via watcher');
      }
    },

    /**
     * Handle control panel toggle
     */
    onControlPanelToggle(collapsed) {
      this.controlPanelCollapsed = collapsed;
    },

    /**
     * Handle toggle control panel from widget bar
     */
    onToggleControlPanel() {
      // If active filters panel is open, close it and open controls
      if (this.rightPanelMode === 'filters') {
        this.rightPanelMode = 'controls';
        this.controlPanelCollapsed = false;
        return;
      }
      this.controlPanelCollapsed = !this.controlPanelCollapsed;
    },

    /**
     * Toggle the dataset active filters panel (right side)
     */
    onToggleActiveFiltersPanel() {
      if (this.rightPanelMode === 'filters') {
        // Close active filters, go back to controls
        this.rightPanelMode = 'controls';
        this.controlPanelCollapsed = false;
      } else {
        // Open active filters panel
        this.rightPanelMode = 'filters';
        this.controlPanelCollapsed = false;
      }
    },

    /**
     * Clear all dataset filters from the right panel
     */
    onClearAllDatasetFilters() {
      this.onAgsVariablesUpdated({ variables: [] });
      this.selectedAgsCategory = '';
      this.rightPanelMode = 'controls';
    },

    /**
     * Handle tab change in control panel
     */
    onTabChanged({ tab }) {
      this.activeTab = tab;

      // When filter tab is selected, the consolidated watcher will handle loading
      // When switching away from filters, clear all layers
      if (tab !== 'filters') {
        this.clearAllLayers();
      }
    },

    /**
     * Handle location selected from search
     */
    onLocationSelected(location) {
      if (!this.map || !location) return;

      // Update map center and zoom
      this.map.setCenter({ lat: location.lat, lng: location.lng });

      if (location.zoom) {
        this.map.setZoom(location.zoom);
      }
    },


    /**
     * Load projects list for dropdown
     */
    async loadProjectsData() {
      console.log('[Load Projects] Loading projects list');

      try {
        const response = await axios.get('/api/projects');
        this.projectsData = response.data;
        console.log('[Load Projects] Loaded', this.projectsData.length, 'projects');
      } catch (error) {
        console.error('[Load Projects] Error loading projects:', error);
        this.$toast?.error('Failed to load projects list');
      }
    },

    /**
     * Load project data from API and render markers
     * @param {number} projectId - The project ID to load
     */
    async loadProjectData(projectId) {
      console.log('[Load Project] Loading project data for ID:', projectId);

      try {
        // Set loading state
        this.layerLoading = true;

        // Fetch project data from API
        console.log('[Load Project] Fetching project details from /api/project/' + projectId);
        const response = await axios.get(`/api/project/${projectId}`);
        const projectData = response.data;

        console.log('[Load Project] Project data received:', projectData);
        console.log('[Load Project] Project data type:', typeof projectData);
        console.log('[Load Project] Has comps property:', 'comps' in projectData);

        // Store project data
        this.selectedProjectData = projectData;

        // Extract project coordinates
        let projectCoords = null;

        // Try to get coordinates from project properties first
        if (projectData.latitude && projectData.longitude) {
          projectCoords = {
            lat: parseFloat(projectData.latitude),
            lng: parseFloat(projectData.longitude)
          };
        }
        // Otherwise, try to get from first site's building
        else if (projectData.sites && projectData.sites.length > 0) {
          const firstSite = projectData.sites[0];
          if (firstSite.building && firstSite.building.latitude && firstSite.building.longitude) {
            projectCoords = {
              lat: parseFloat(firstSite.building.latitude),
              lng: parseFloat(firstSite.building.longitude)
            };
          }
        }

        // Create project marker if we have coordinates
        if (projectCoords) {
          console.log('[Load Project] Creating project marker at:', projectCoords);
          this.createProjectMarker(projectData, projectCoords);

          // Center map on project location
          this.map.setCenter(projectCoords);
          this.map.setZoom(12);
        } else {
          console.warn('[Load Project] No coordinates found for project');
          this.$toast?.warning('Project location not available');
        }

        // Render project comps if they exist
        try {
          // Ensure comps is always an array
          const comps = Array.isArray(projectData.comps) ? projectData.comps : [];

          console.log('[Load Project] Checking comps:', {
            hasComps: !!projectData.comps,
            compsType: typeof projectData.comps,
            compsIsArray: Array.isArray(projectData.comps),
            compsLength: comps.length,
            compsValue: projectData.comps
          });

          if (comps.length > 0) {
            console.log('[Load Project] Rendering', comps.length, 'comps');
            this.renderProjectComps(comps);
          } else {
            console.log('[Load Project] No comps found for this project');
          }
        } catch (compsError) {
          console.error('[Load Project] Error rendering comps:', compsError);
          console.error('[Load Project] Comps data that caused error:', projectData.comps);
          this.$toast?.error('Failed to render project comps');
        }

      } catch (error) {
        console.error('[Load Project] Error loading project data:', error);

        // Handle specific error types
        if (error.response) {
          if (error.response.status === 404) {
            this.$toast?.error('Project not found');
          } else if (error.response.status === 403) {
            this.$toast?.error('You don\'t have permission to view this project');
          } else if (error.response.status === 500) {
            this.$toast?.error('Server error occurred. Please try again later');
          } else {
            this.$toast?.error('Failed to load project data');
          }
        } else {
          this.$toast?.error('Failed to load project data. Please check your connection');
        }

        // Clear any partial data
        this.clearProjectMarker();
        this.clearProjectCompsMarkers();
      } finally {
        // Clear loading state
        this.layerLoading = false;
      }
    },

    /**
     * Handle layer toggle
     */
    onLayerToggled({ layerId, enabled }) {
      // Handle satellite layer toggle
      if (layerId === 'satellite') {
        this.toggleSatelliteLayer(enabled);
        return;
      }

      // Handle traffic layer toggle
      if (layerId === 'traffic') {
        this.toggleTrafficLayer(enabled);
        return;
      }

      // Handle transit layer toggle
      if (layerId === 'transit') {
        this.toggleTransitLayer(enabled);
        return;
      }

      // Handle airport layer toggle
      if (layerId === 'airports') {
        this.toggleAirportLayer(enabled);
        return;
      }

      // Handle ports layer toggle
      if (layerId === 'ports') {
        this.togglePortsLayer(enabled);
        return;
      }

      // Handle team layer toggle
      if (layerId === 'team') {
        this.toggleTeamLayer(enabled);
        return;
      }

      // Handle customer layer toggle
      if (layerId === 'customers') {
        this.toggleCustomerLayer(enabled);
        return;
      }

      // Handle wildfire layer toggle
      if (layerId === 'wildfires') {
        this.toggleWildfireLayer(enabled);
        return;
      }

      // Handle protests layer toggle
      if (layerId === 'protests') {
        this.toggleProtestsLayer(enabled);
        return;
      }

      // Handle highway layer toggle
      if (layerId === 'highways') {
        this.toggleHighwayLayer(enabled);
        return;
      }

      // Handle waterways layer toggle
      if (layerId === 'waterways') {
        this.toggleWaterwaysLayer(enabled);
        return;
      }

      // Handle rail layer toggle
      if (layerId === 'rail') {
        this.toggleRailLayer(enabled);
        return;
      }

      // Handle travel time layer toggle
      if (layerId === 'travel_time') {
        this.toggleTravelTimeLayer(enabled);
        return;
      }

      // Handle directions layer toggle
      if (layerId === 'directions') {
        if (enabled) {
          this.enableDirectionsLayer();
        } else {
          this.disableDirectionsLayer();
        }
        return;
      }

      if (layerId === LAYER_TYPES.CRIME) {

        if (enabled) {
          if (!this.activeLayers.includes(layerId)) {
            this.activeLayers.push(layerId);
          }
        } else {
          this.activeLayers = this.activeLayers.filter(id => id !== layerId);
          this.clearCrimeLayers();
        }
      } else if (layerId === LAYER_TYPES.AGS) {

        if (enabled) {
          if (!this.activeLayers.includes(layerId)) {
            this.activeLayers.push(layerId);
          }
        } else {
          this.activeLayers = this.activeLayers.filter(id => id !== layerId);
          this.clearAgsLayers();
        }
      }

      // The consolidated watcher will handle updating map layers when activeLayers changes
      // No need to call debouncedUpdateMapLayers here
    },

    /**
     * Toggle satellite layer on/off
     * @param {Boolean} enabled - Whether to enable or disable satellite view
     */
    toggleSatelliteLayer(enabled) {
      // Check if map instance is available
      if (!this.map) {
        console.warn('Map instance not available, cannot toggle satellite layer');
        return;
      }

      try {
        this.layerLoading = true;

        if (enabled) {
          // Use hybrid mode instead of pure satellite for better overlay visibility
          // Hybrid shows satellite imagery with road labels, providing better contrast
          // for all custom layers (traffic, markers, polylines, polygons, etc.)
          this.map.setMapTypeId('hybrid');
          console.log('Satellite layer enabled with hybrid map type for better overlay visibility');

          // Add to active layers if not already present
          if (!this.activeLayers.includes('satellite')) {
            this.activeLayers.push('satellite');
          }
        } else {
          // Switch back to roadmap view
          this.map.setMapTypeId('roadmap');
          console.log('Satellite layer disabled');

          // Remove from active layers
          this.activeLayers = this.activeLayers.filter(id => id !== 'satellite');

          // Reapply theme styles to roadmap if needed
          this.updateMapTheme(this.isDarkMode);
        }

        // Hide loader after a short delay to allow map tiles to start loading
        setTimeout(() => {
          this.layerLoading = false;
        }, 500);
      } catch (error) {
        console.error('Failed to toggle satellite layer:', error);
        this.error = 'Failed to change map view. Please try again.';
        this.layerLoading = false;

        // Revert the toggle state by removing/adding from activeLayers
        if (enabled) {
          this.activeLayers = this.activeLayers.filter(id => id !== 'satellite');
        }
      }
    },

    /**
     * Toggle traffic layer on/off
     * @param {Boolean} enabled - Whether to enable or disable traffic layer
     */
    toggleTrafficLayer(enabled) {
      // Check if map instance is available
      if (!this.map) {
        console.warn('Map instance not available, cannot toggle traffic layer');
        return;
      }

      try {
        this.layerLoading = true;

        if (enabled) {
          console.log('Creating traffic layer...');
          console.log('Google Maps available:', typeof google !== 'undefined');
          console.log('TrafficLayer available:', typeof google.maps.TrafficLayer !== 'undefined');

          // Create new TrafficLayer instance
          this.trafficLayer = new google.maps.TrafficLayer();

          console.log('TrafficLayer instance created:', this.trafficLayer);
          console.log('Map instance:', this.map);

          // Add to map
          this.trafficLayer.setMap(this.map);

          // Check if travel time layer is active
          const isTravelTimeActive = this.activeLayers.includes('travel_time');

          // Check if a building or location is selected in travel time filters
          const hasBuildingSelected = this.travelTimeFilters.buildingId !== null;
          const hasLocationSelected = this.travelTimeFilters.locationCoordinates !== null;
          const hasTravelTimeSelection = hasBuildingSelected || hasLocationSelected;

          // Only zoom to level 5 if:
          // 1. Travel time layer is NOT active, OR
          // 2. Travel time layer IS active but NO building/location is selected
          const shouldZoom = !isTravelTimeActive || (isTravelTimeActive && !hasTravelTimeSelection);

          if (shouldZoom && this.currentZoom !== 5) {
            console.log('Setting zoom level to 5 for traffic layer');
            this.map.setZoom(5);
            this.currentZoom = 5;
          } else if (isTravelTimeActive && hasTravelTimeSelection) {
            console.log('Travel time layer is active with selection, preserving current zoom and position');
          }

          console.log('Traffic layer enabled and added to map');
          console.log('Current zoom level:', this.currentZoom);
          console.log('Map center:', this.map.getCenter().toJSON());

          // Add to active layers if not already present
          if (!this.activeLayers.includes('traffic')) {
            this.activeLayers.push('traffic');
          }
        } else {
          // Remove from map
          if (this.trafficLayer) {
            this.trafficLayer.setMap(null);
          }

          console.log('Traffic layer disabled');

          // Remove from active layers
          this.activeLayers = this.activeLayers.filter(id => id !== 'traffic');
        }

        // Hide loader after a short delay
        setTimeout(() => {
          this.layerLoading = false;
        }, 500);
      } catch (error) {
        console.error('Failed to toggle traffic layer:', error);
        console.error('Error details:', error.message, error.stack);
        this.error = 'Failed to toggle traffic layer. Please try again.';
        this.layerLoading = false;

        // Revert the toggle state
        if (enabled) {
          this.activeLayers = this.activeLayers.filter(id => id !== 'traffic');
          if (this.trafficLayer) {
            this.trafficLayer.setMap(null);
          }
        }
      }
    },

    /**
     * Toggle transit layer on/off
     * @param {Boolean} enabled - Whether to enable or disable transit layer
     */
    toggleTransitLayer(enabled) {
      // Check if map instance is available
      if (!this.map) {
        console.warn('Map instance not available, cannot toggle transit layer');
        return;
      }

      try {
        this.layerLoading = true;

        if (enabled) {
          console.log('Creating transit layer...');
          console.log('Google Maps available:', typeof google !== 'undefined');
          console.log('TransitLayer available:', typeof google.maps.TransitLayer !== 'undefined');

          // Create new TransitLayer instance
          this.transitLayer = new google.maps.TransitLayer();

          console.log('TransitLayer instance created:', this.transitLayer);
          console.log('Map instance:', this.map);

          // Add to map
          this.transitLayer.setMap(this.map);

          console.log('Transit layer enabled and added to map');
          console.log('Current zoom level:', this.currentZoom);
          console.log('Map center:', this.map.getCenter().toJSON());

          // Add to active layers if not already present
          if (!this.activeLayers.includes('transit')) {
            this.activeLayers.push('transit');
          }
        } else {
          console.log('Disabling transit layer...');

          // Remove from map
          if (this.transitLayer) {
            this.transitLayer.setMap(null);
          }

          console.log('Transit layer disabled');

          // Remove from active layers
          this.activeLayers = this.activeLayers.filter(id => id !== 'transit');
        }

        // Hide loader after a short delay
        setTimeout(() => {
          this.layerLoading = false;
        }, 500);
      } catch (error) {
        console.error('Failed to toggle transit layer:', error);
        console.error('Error details:', error.message, error.stack);
        this.error = 'Failed to toggle transit layer. Please try again.';
        this.layerLoading = false;

        // Revert the toggle state
        if (enabled) {
          this.activeLayers = this.activeLayers.filter(id => id !== 'transit');
          if (this.transitLayer) {
            this.transitLayer.setMap(null);
          }
        }
      }
    },

    /**
     * Toggle wildfire layer on/off
     * @param {Boolean} enabled - Whether to enable or disable wildfire layer
     */
    toggleWildfireLayer(enabled) {
      // Check if map instance is available
      if (!this.map) {
        console.warn('Map instance not available, cannot toggle wildfire layer');
        return;
      }

      try {
        this.layerLoading = true;

        if (enabled) {
          console.log('Enabling wildfire layer...');

          // Load wildfire data
          this.loadWildfireData();

          // Add to active layers if not already present
          if (!this.activeLayers.includes('wildfires')) {
            this.activeLayers.push('wildfires');
          }
        } else {
          console.log('Disabling wildfire layer...');

          // Remove all wildfire markers from map
          this.wildfireMarkers.forEach(marker => {
            marker.setMap(null);
          });

          // Clear markers array
          this.wildfireMarkers = [];

          console.log('Wildfire layer disabled');

          // Remove from active layers
          this.activeLayers = this.activeLayers.filter(id => id !== 'wildfires');
        }

        // Hide loader after a short delay
        setTimeout(() => {
          this.layerLoading = false;
        }, 500);
      } catch (error) {
        console.error('Failed to toggle wildfire layer:', error);
        this.error = 'Failed to toggle wildfire layer. Please try again.';
        this.layerLoading = false;

        // Revert the toggle state
        if (enabled) {
          this.activeLayers = this.activeLayers.filter(id => id !== 'wildfires');
          this.wildfireMarkers.forEach(marker => marker.setMap(null));
          this.wildfireMarkers = [];
        }
      }
    },

    /**
     * Load airport data from API
     */
    async loadAirportData() {
      this.loadingAirports = true;

      try {
        console.log('Loading airport data from API...');
        const response = await axios.get('/api/airports');
        this.airportsData = response.data;
        console.log(`Loaded ${this.airportsData.length} airports from API`);
      } catch (error) {
        console.error('Failed to load airport data:', error);
        throw new Error('Failed to load airport data from server');
      } finally {
        this.loadingAirports = false;
      }
    },

    /**
     * Load team members data from API
     */
    async loadTeamMembersData() {
      this.loadingTeamMembers = true;

      try {
        console.log('Loading team members data from API...');
        const response = await axios.get('/api/employees');
        this.teamMembersData = response.data;
        console.log(`Loaded ${this.teamMembersData.length} team members from API`);
      } catch (error) {
        console.error('Failed to load team members data:', error);
        throw new Error('Failed to load team members data from server');
      } finally {
        this.loadingTeamMembers = false;
      }
    },

    /**
     * Load wildfire data from GeoJSON file
     */
    async loadWildfireData() {
      // Check if data is already cached
      if (this.wildfiresData) {
        console.log('Using cached wildfire data');
        this.createWildfireMarkers();
        return;
      }

      try {
        console.log('Loading wildfire data from GeoJSON file...');
        this.setLayerLoading('wildfires');
        const response = await fetch('/api/cresiteiq2/layers/static?layer=wildfire');

        if (!response.ok) {
          throw new Error(`Failed to load wildfire data: ${response.status} ${response.statusText}`);
        }

        const geojson = await response.json();

        if (!geojson || !geojson.features) {
          throw new Error('Invalid GeoJSON format');
        }

        // Cache the data
        this.wildfiresData = geojson;
        console.log(`Loaded ${geojson.features.length} wildfire locations`);

        // Create markers
        this.createWildfireMarkers();
      } catch (error) {
        console.error('Failed to load wildfire data:', error);
        this.error = 'Failed to load wildfire data. Please try again.';
        this.activeLayers = this.activeLayers.filter(id => id !== 'wildfires');
      } finally {
        this.clearLayerLoading('wildfires');
      }
    },

    /**
     * Create markers for all wildfires
     */
    createWildfireMarkers() {
      if (!this.wildfiresData || !this.map) {
        console.warn('Cannot create wildfire markers: missing data or map');
        return;
      }

      // Clear existing markers
      this.wildfireMarkers.forEach(marker => marker.setMap(null));
      this.wildfireMarkers = [];

      const features = this.wildfiresData.features;
      console.log(`Creating markers for ${features.length} wildfires`);

      features.forEach(feature => {
        try {
          const coordinates = feature.geometry.coordinates;
          const properties = feature.properties;

          if (!coordinates || coordinates.length < 2) {
            console.warn('Invalid coordinates for wildfire:', properties);
            return;
          }

          const marker = new google.maps.Marker({
            position: {
              lat: coordinates[1], // GeoJSON uses [lon, lat]
              lng: coordinates[0]
            },
            map: this.map,
            title: properties.fire_name || 'Wildfire',
            icon: {
              url: '/img/map/wildfire-icon.png',
              scaledSize: new google.maps.Size(24, 24)
            }
          });

          // Add click listener for popup
          marker.addListener('click', () => {
            this.onWildfireMarkerClick(properties);
          });

          // Add hover listeners for tooltip
          marker.addListener('mouseover', (event) => {
            this.onWildfireMarkerMouseOver(properties, event);
          });

          marker.addListener('mouseout', () => {
            this.onWildfireMarkerMouseOut();
          });

          // Store marker
          this.wildfireMarkers.push(marker);
        } catch (error) {
          console.warn('Failed to create marker for wildfire:', error);
        }
      });

      console.log(`Created ${this.wildfireMarkers.length} wildfire markers`);
    },

    /**
     * Create markers for all airports
     */
    createAirportMarkers() {
      if (!this.airportsData || !this.map) {
        console.warn('Cannot create airport markers: missing data or map');
        return;
      }

      // Filter airports with valid coordinates
      const validAirports = this.airportsData.filter(airport =>
        airport.latitude && airport.longitude
      );

      console.log(`Creating markers for ${validAirports.length} airports with valid coordinates`);

      validAirports.forEach(airport => {
        try {
          const marker = new google.maps.Marker({
            position: {
              lat: parseFloat(airport.latitude),
              lng: parseFloat(airport.longitude)
            },
            map: this.map,
            title: airport.name || 'Airport',
            icon: {
              url: '/img/theme/v1/icons/airplane.svg',
              scaledSize: new google.maps.Size(32, 32)
            }
          });

          // Add click listener for popup
          marker.addListener('click', () => {
            this.selectedAirport = airport;
            this.showAirportPopup = true;
          });

          this.airportMarkers.push(marker);
        } catch (error) {
          console.error(`Failed to create marker for airport: ${airport.name}`, error);
        }
      });

      console.log(`Successfully created ${this.airportMarkers.length} airport markers`);
    },

    /**
     * Clear all airport markers from map
     */
    clearAirportMarkers() {
      console.log(`Clearing ${this.airportMarkers.length} airport markers`);

      // Close any open info window
      if (this.currentAirportInfoWindow) {
        this.currentAirportInfoWindow.close();
        this.currentAirportInfoWindow = null;
      }

      this.airportMarkers.forEach(marker => {
        marker.setMap(null);
        google.maps.event.clearInstanceListeners(marker);
      });

      this.airportMarkers = [];
      console.log('Airport markers cleared');
    },

    /**
     * Create markers for all team members
     */
    createTeamMarkers() {
      if (!this.teamMembersData || !this.map) {
        console.warn('Cannot create team markers: missing data or map');
        return;
      }

      // Filter team members with valid coordinates
      const validTeamMembers = this.teamMembersData.filter(member =>
        member.latitude && member.longitude
      );

      console.log(`Creating markers for ${validTeamMembers.length} team members with valid coordinates`);

      validTeamMembers.forEach(member => {
        try {
          const marker = new google.maps.Marker({
            position: {
              lat: parseFloat(member.latitude),
              lng: parseFloat(member.longitude)
            },
            map: this.map,
            title: member.name || 'Team Member',
            icon: {
              url: '/img/map/marker-employee.png',
              scaledSize: new google.maps.Size(32, 32)
            }
          });

          // Add click listener for popup
          marker.addListener('click', () => {
            this.selectedTeamMember = member;
            this.showTeamPopup = true;
          });

          this.teamMarkers.push(marker);
        } catch (error) {
          console.error(`Failed to create marker for team member: ${member.name}`, error);
        }
      });

      console.log(`Successfully created ${this.teamMarkers.length} team markers`);
    },

    /**
     * Show team member info window
     * @param {Object} member - Team member data
     * @param {google.maps.Marker} marker - Google Maps marker instance
     */
    showTeamMemberInfo(member, marker) {
      // Close any previously open info window
      if (this.currentTeamInfoWindow) {
        this.currentTeamInfoWindow.close();
      }

      // Center map on the clicked team member
      this.map.panTo(marker.getPosition());

      // Generate themed content
      const content = this.generateTeamInfoWindowContent(member);

      // Create info window
      const infoWindow = new google.maps.InfoWindow({
        content: content,
        disableAutoPan: false,
        maxWidth: 500
      });

      // Store reference to current info window
      this.currentTeamInfoWindow = infoWindow;

      // Clear reference when info window is closed
      infoWindow.addListener('closeclick', () => {
        this.currentTeamInfoWindow = null;
      });

      infoWindow.open(this.map, marker);
    },

    /**
     * Generate themed HTML content for team member info window
     * @param {Object} member - Team member data
     * @returns {String} HTML content for info window
     */
    generateTeamInfoWindowContent(member) {
      // Determine theme colors
      const isDark = this.isDarkMode;
      const bgColor = isDark ? '#101a2f' : '#ffffff';
      const textColor = isDark ? '#f1f5f9' : '#1f2937';
      const labelColor = isDark ? '#94a3b8' : '#6b7280';

      return `
        <div style="
          padding: 20px;
          min-width: 350px;
          max-width: 450px;
          background-color: ${bgColor} !important;
          color: ${textColor};
          border-radius: 12px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          margin: -8px;
        ">
          <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px;">
            <div>
              <h1 style="margin: 0 0 4px 0; font-weight: 600; font-size: 18px; color: ${textColor} !important;">
                ${member.name || 'Team Member'}
              </h1>
              ${member.job_function ? `
                <p style="margin: 0; font-size: 14px; color: ${labelColor} !important;">
                  ${member.job_function}
                </p>
              ` : ''}
            </div>
          </div>

          <div style="margin-top: 16px;">
            <h3 style="margin: 0 0 8px 0; font-size: 14px; font-weight: 600; color: ${labelColor} !important;">Location</h3>
            <p style="margin: 0 0 4px 0; font-size: 14px; font-weight: 600; color: ${textColor} !important;">
              ${member.address_line_1 || 'Address not available'}
            </p>
            <p style="margin: 0; font-size: 14px; color: ${textColor} !important;">
              ${member.city || ''}${member.city && member.state ? ', ' : ''}${member.state || ''}
            </p>
          </div>
        </div>
      `;
    },

    /**
     * Clear all team markers from map
     */
    clearTeamMarkers() {
      console.log(`Clearing ${this.teamMarkers.length} team markers`);

      // Close any open info window
      if (this.currentTeamInfoWindow) {
        this.currentTeamInfoWindow.close();
        this.currentTeamInfoWindow = null;
      }

      this.teamMarkers.forEach(marker => {
        marker.setMap(null);
        google.maps.event.clearInstanceListeners(marker);
      });

      this.teamMarkers = [];
      console.log('Team markers cleared');
    },

    /**
     * Toggle customer layer on/off
     * @param {Boolean} enabled - Whether to enable or disable customer layer
     */
    async toggleCustomerLayer(enabled) {
      // Check if map instance is available
      if (!this.map) {
        console.warn('Map instance not available, cannot toggle customer layer');
        return;
      }

      try {
        this.layerLoading = true;

        if (enabled) {
          console.log('Enabling customer layer...');

          // Load customer data
          await this.loadCustomerLayer();

          // Add to active layers if not already present
          if (!this.activeLayers.includes('customers')) {
            this.activeLayers.push('customers');
          }

          console.log('Customer layer enabled');
        } else {
          console.log('Disabling customer layer...');

          // Clear customer layer
          this.clearCustomerLayer();

          // Remove from active layers
          this.activeLayers = this.activeLayers.filter(id => id !== 'customers');

          console.log('Customer layer disabled');
        }
      } catch (error) {
        console.error('Error toggling customer layer:', error);
        this.error = error.message || 'Failed to toggle customer layer';

        // Revert the toggle state
        if (enabled) {
          this.activeLayers = this.activeLayers.filter(id => id !== 'customers');
          this.clearCustomerLayer();
        }
      } finally {
        this.layerLoading = false;
      }
    },

    /**
     * Load customer data and render markers
     */
    async loadCustomerLayer() {
      this.customerLayerLoading = true;

      try {
        console.log('Loading customer data from API...');

        // Fetch customer data from existing API endpoint
        const response = await axios.get('/api/customers');

        // The existing API returns the customers array directly
        this.customerData = response.data || [];
        console.log(`Loaded ${this.customerData.length} customers from API`);

        // Render markers
        this.renderCustomerMarkers();
      } catch (error) {
        console.error('Failed to load customer data:', error);
        throw new Error('Failed to load customer data from server');
      } finally {
        this.customerLayerLoading = false;
      }
    },

    /**
     * Render customer markers on map
     */
    renderCustomerMarkers() {
      if (!this.customerData || !this.map) {
        console.warn('Cannot render customer markers: missing data or map');
        return;
      }

      // Clear existing markers
      this.clearCustomerMarkers();

      // Filter customers with valid coordinates
      const validCustomers = this.customerData.filter(customer =>
        customer.latitude && customer.longitude
      );

      console.log(`Creating markers for ${validCustomers.length} customers with valid coordinates`);

      // Create markers for each customer
      this.customerMarkers = validCustomers.map(customer => {
        try {
          const marker = new google.maps.Marker({
            position: {
              lat: parseFloat(customer.latitude),
              lng: parseFloat(customer.longitude)
            },
            map: this.map,
            title: customer.name || 'Customer',
            icon: {
              url: '/img/theme/v1/icons/customer-marker.svg',
              scaledSize: new google.maps.Size(32, 40),
              anchor: new google.maps.Point(16, 40)
            }
          });

          // Add click listener
          marker.addListener('click', () => {
            this.onCustomerMarkerClick(customer);
          });

          return marker;
        } catch (error) {
          console.error(`Failed to create marker for customer: ${customer.name}`, error);
          return null;
        }
      }).filter(marker => marker !== null);

      console.log(`Successfully created ${this.customerMarkers.length} customer markers`);

      // Initialize marker clustering
      this.initializeCustomerClustering();
    },

    /**
     * Initialize marker clustering for customers
     */
    initializeCustomerClustering() {
      // Check if MarkerClusterer is available
      if (typeof MarkerClusterer === 'undefined') {
        console.warn('MarkerClusterer not available, skipping clustering');
        return;
      }

      // Clear existing clusterer
      if (this.customerMarkerClusterer) {
        this.customerMarkerClusterer.clearMarkers();
      }

      // Create new clusterer
      this.customerMarkerClusterer = new MarkerClusterer(this.map, this.customerMarkers, {
        imagePath: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m',
        gridSize: 50,
        maxZoom: 15
      });

      console.log('Customer marker clustering initialized');
    },

    /**
     * Handle customer marker click
     */
    onCustomerMarkerClick(customer) {
      console.log('Customer marker clicked:', customer.name);
      this.selectedCustomer = customer;
      this.showCustomerPopup = true;
    },

    /**
     * Clear customer markers from map
     */
    clearCustomerMarkers() {
      console.log(`Clearing ${this.customerMarkers.length} customer markers`);

      this.customerMarkers.forEach(marker => {
        marker.setMap(null);
        google.maps.event.clearInstanceListeners(marker);
      });

      this.customerMarkers = [];

      // Clear clusterer
      if (this.customerMarkerClusterer) {
        this.customerMarkerClusterer.clearMarkers();
        this.customerMarkerClusterer = null;
      }

      console.log('Customer markers cleared');
    },

    /**
     * Clear entire customer layer
     */
    clearCustomerLayer() {
      this.clearCustomerMarkers();
      this.customerData = [];
      this.selectedCustomer = null;
      this.showCustomerPopup = false;
    },

    /**
     * Close customer popup
     */
    closeCustomerPopup() {
      this.showCustomerPopup = false;
      this.selectedCustomer = null;
    },

    /**
     * Load site data and render markers
     */
    async loadSiteMarkers() {
      // KAN-173: Guard — skip fetch if data already loaded (prevents 10x calls)
      if (this.sitesData && this.sitesData.length > 0) {
        console.log('[Load Site Markers] Data already loaded, skipping fetch');
        this.renderSiteMarkers();
        return;
      }

      this.siteLayerLoading = true;

      try {
        console.log('[Load Site Markers] Loading site data from API...');

        const response = await axios.get('/api/sites/map');

        // KAN-173: Object.freeze removed — site objects have computed appends
        // (status_color, image, etc.) that Vue components read; freezing breaks that.
        this.sitesData = response.data || [];
        console.log(`[Load Site Markers] Loaded ${this.sitesData.length} sites from API`);

        this.renderSiteMarkers();
      } catch (error) {
        console.error('[Load Site Markers] Failed to load site data:', error);
        this.$toast?.error('Failed to load site markers');
      } finally {
        this.siteLayerLoading = false;
      }
    },

    /**
     * Render site markers on map
     */
    renderSiteMarkers() {
      const activeSites = this.getActiveSitesData();

      if (!activeSites || activeSites.length === 0 || !this.map) {
        console.warn('Cannot render site markers: missing data or map');
        this.clearSiteMarkers();
        return;
      }

      // Clear existing markers
      this.clearSiteMarkers();

      // Filter sites with valid building coordinates
      const validSites = activeSites.filter(site =>
        site.building?.latitude && site.building?.longitude
      );

      console.log(`Creating markers for ${validSites.length} sites with valid coordinates`);

      // Create markers for each site
      this.siteMarkers = validSites.map(site => {
        try {
          const marker = new google.maps.Marker({
            position: {
              lat: parseFloat(site.building.latitude),
              lng: parseFloat(site.building.longitude)
            },
            map: this.map,
            title: site.building.address_line_1 || 'Site',
            icon: {
              url: this.getSiteMarkerIcon(),
              scaledSize: new google.maps.Size(32, 40),
              anchor: new google.maps.Point(16, 40)
            },
            zIndex: 100 // Ensure sites appear above polygons
          });

          // Store site data on marker for click handler
          marker.set('siteData', site);

          // Add click listener
          marker.addListener('click', () => {
            this.onSiteMarkerClick(site);
          });

          return marker;
        } catch (error) {
          console.error(`Failed to create marker for site: ${site.id}`, error);
          return null;
        }
      }).filter(marker => marker !== null);

      console.log(`Successfully created ${this.siteMarkers.length} site markers`);

      // Initialize marker clustering if needed
      if (this.siteMarkers.length >= 100) {
        this.initializeSiteClustering();
      }
    },

    /**
     * Get site marker icon URL
     */
    getSiteMarkerIcon() {
      // Use existing site marker icon
      return '/img/map/marker-site.png';
    },

    /**
     * Get comp marker icon URL based on score (same logic as old CRESiteIQ)
     */
    getCompMarkerIcon(comp) {
      if (!comp || !comp.pivot) {
        return '/img/map/marker-comp-unresearched.png';
      }

      const score = comp.pivot.score || 0;
      const isRejected = comp.pivot.is_rejected || comp.isRejected;

      // Unresearched (score = 0 and not rejected)
      if (score === 0 && !isRejected) {
        return '/img/map/marker-comp-unresearched.png';
      }

      // Rejected or low score (< 70)
      if (isRejected || score < 70) {
        return '/img/map/marker-comp-rejected.png';
      }

      // High score (>= 90) - Grade A
      if (score >= 90) {
        return '/img/map/marker-comp-a.png';
      }

      // Medium score (70-89) - Grade B
      return '/img/map/marker-comp-b.png';
    },

    /**
     * Initialize marker clustering for sites
     */
    initializeSiteClustering() {
      // Check if MarkerClusterer is available
      if (typeof MarkerClusterer === 'undefined') {
        console.warn('MarkerClusterer not available, skipping clustering');
        return;
      }

      // Clear existing clusterer
      if (this.siteMarkerClusterer) {
        this.siteMarkerClusterer.clearMarkers();
      }

      // Create new clusterer
      this.siteMarkerClusterer = new MarkerClusterer(this.map, this.siteMarkers, {
        imagePath: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m',
        gridSize: 50,
        maxZoom: 15
      });

      console.log('Site marker clustering initialized');
    },

    /**
     * Handle site marker click
     */
    onSiteMarkerClick(site) {
      console.log('Site marker clicked:', site.building.address_line_1);
      this.selectedSite = site;
      this.showSitePopup = true;
    },

    /**
     * Clear site markers from map
     */
    clearSiteMarkers() {
      console.log(`Clearing ${this.siteMarkers.length} site markers`);

      this.siteMarkers.forEach(marker => {
        marker.setMap(null);
        google.maps.event.clearInstanceListeners(marker);
      });

      this.siteMarkers = [];

      // Clear clusterer
      if (this.siteMarkerClusterer) {
        this.siteMarkerClusterer.clearMarkers();
        this.siteMarkerClusterer = null;
      }

      console.log('Site markers cleared');
    },

    /**
     * Close site popup
     */
    closeSitePopup() {
      this.showSitePopup = false;
      this.selectedSite = null;
    },

    /**
     * Close comp popup
     */
    closeCompPopup() {
      this.showCompPopup = false;
      this.selectedComp = null;
    },

    /**
     * Load projects from API
     */
    async loadProjects() {
      try {
        console.log('Loading projects from API...');

        const response = await axios.get('/api/projects');
        // API returns data in response.data.data format
        this.projectsData = response.data.data || response.data || [];

        console.log(`Loaded ${this.projectsData.length} projects from API`, this.projectsData);
      } catch (error) {
        console.error('Failed to load projects:', error);
        this.$toast?.error('Failed to load projects');
      }
    },

    /**
     * Get active sites data (filtered or all)
     */
    getActiveSitesData() {
      return this.filteredSitesData || this.sitesData || [];
    },

    /**
     * Handle Site/Project mode toggle
     */
    onModeChanged(mode) {
      console.log('Mode changed to:', mode);
      this.siteProjectMode = mode;
      this.selectedProjectId = null;

      if (mode === 'projects') {
        // Load projects if not already loaded
        if (this.projectsData.length === 0) {
          this.loadProjects();
        }
        // Clear all site markers when switching to Projects mode
        this.clearSiteMarkers();
      } else if (mode === 'sites') {
        // Reset to show all sites when switching to Sites mode
        this.filteredSitesData = null;
        this.renderSiteMarkers();
      }
    },

    /**
     * Handle project selection from dropdown
     */
    async onProjectSelected(projectId) {
      console.log('[Project Selection] Project selected:', projectId);
      this.selectedProjectId = projectId;

      if (!projectId) {
        // No project selected, clear comp markers
        console.log('[Project Selection] No project selected, clearing markers');
        this.clearProjectCompsMarkers();
        this.clearProjectMarker();
        this.selectedProjectId = null;
        this.selectedProjectData = null;
        return;
      }

      this.loadingComps = true;

      try {
        // Fetch full project details with comps from API
        console.log(`[Project Selection] Fetching project details from /api/project/${projectId}...`);
        const response = await axios.get(`/api/project/${projectId}`);
        const selectedProject = response.data;

        console.log('[Project Selection] Project loaded:', selectedProject);
        console.log('[Project Selection] Project has comps:', selectedProject.comps ? selectedProject.comps.length : 0);

        // Store project data
        this.selectedProjectData = selectedProject;

        // Clear any existing comp markers
        this.clearProjectCompsMarkers();

        // Check if project has comps
        if (selectedProject && selectedProject.comps && selectedProject.comps.length > 0) {
          console.log(`[Project Selection] Rendering ${selectedProject.comps.length} comps for project "${selectedProject.name}"`);

          // Render comp markers on the map
          this.renderProjectComps(selectedProject.comps);

          // Don't auto-zoom - keep current map view
        } else {
          console.warn('[Project Selection] Project has no comps:', projectId);
          this.$toast?.warning('This project has no comparable properties to display on the map');
        }
      } catch (error) {
        console.error('[Project Selection] Failed to load project:', error);
        this.$toast?.error('Failed to load project details');

        // Clear any partial data
        this.clearProjectMarker();
        this.clearProjectCompsMarkers();
      } finally {
        // Clear loading state
        this.loadingComps = false;
      }
    },

    /**
     * Render a single marker for the selected project
     */
    renderProjectMarker(project, sites) {
      if (!this.map || !sites || sites.length === 0) {
        return;
      }

      // Clear any existing project marker
      this.clearProjectMarker();

      // Calculate centroid of all project sites
      let totalLat = 0;
      let totalLng = 0;
      let validSites = 0;

      sites.forEach(site => {
        if (site.building?.latitude && site.building?.longitude) {
          totalLat += parseFloat(site.building.latitude);
          totalLng += parseFloat(site.building.longitude);
          validSites++;
        }
      });

      if (validSites === 0) {
        console.warn('No valid coordinates found for project sites');
        return;
      }

      const centroidLat = totalLat / validSites;
      const centroidLng = totalLng / validSites;

      // Create project marker
      this.projectMarker = new google.maps.Marker({
        position: {
          lat: centroidLat,
          lng: centroidLng
        },
        map: this.map,
        title: project.name,
        icon: {
          url: this.getProjectMarkerIcon(),
          scaledSize: new google.maps.Size(40, 40),
          anchor: new google.maps.Point(20, 40)
        },
        zIndex: 150 // Higher than site markers
      });

      // Store project data on marker
      this.projectMarker.set('projectData', project);

      // Add click listener to show project info
      this.projectMarker.addListener('click', () => {
        this.onProjectMarkerClick(project);
      });

      console.log(`Created project marker for "${project.name}" at (${centroidLat}, ${centroidLng})`);
    },

    /**
     * Get project marker icon URL
     */
    getProjectMarkerIcon() {
      // Use orange site marker for projects
      return '/img/map/marker-site-orange.png';
    },

    /**
     * Handle project marker click
     */
    onProjectMarkerClick(project) {
      console.log('Project marker clicked:', project.name);
      // You can implement a project popup here if needed
      // For now, just log it
    },

    /**
     * Center and zoom map to fit all provided sites
     */
    centerMapOnSites(sites) {
      if (!sites || sites.length === 0 || !this.map) {
        return;
      }

      const bounds = new google.maps.LatLngBounds();

      sites.forEach(site => {
        if (site.building?.latitude && site.building?.longitude) {
          bounds.extend({
            lat: parseFloat(site.building.latitude),
            lng: parseFloat(site.building.longitude)
          });
        }
      });

      this.map.fitBounds(bounds);
      console.log('Map centered on filtered sites');
    },

    /**
     * Close airport popup
     */
    closeAirportPopup() {
      this.showAirportPopup = false;
      this.selectedAirport = null;
    },

    /**
     * Close port popup
     */
    closePortPopup() {
      this.showPortPopup = false;
      this.selectedPort = null;
    },

    /**
     * Close team popup
     */
    closeTeamPopup() {
      this.showTeamPopup = false;
      this.selectedTeamMember = null;
    },

    /**
     * Handle wildfire marker click
     * @param {Object} wildfire - Wildfire data from GeoJSON properties
     */
    onWildfireMarkerClick(wildfire) {
      // Close any other open popups
      this.showCustomerPopup = false;
      this.showAirportPopup = false;
      this.showPortPopup = false;
      this.showTeamPopup = false;

      // Open wildfire popup
      this.selectedWildfire = wildfire;
      this.showWildfirePopup = true;
    },

    /**
     * Close wildfire popup
     */
    closeWildfirePopup() {
      this.showWildfirePopup = false;
      this.selectedWildfire = null;
    },

    /**
     * Handle wildfire marker hover - show tooltip
     * @param {Object} wildfire - Wildfire data from GeoJSON properties
     * @param {Object} event - Mouse event
     */
    onWildfireMarkerMouseOver(wildfire, event) {
      this.hoverWildfire = wildfire;

      // Position tooltip near cursor
      const x = event.domEvent.clientX;
      const y = event.domEvent.clientY;

      this.wildfireTooltipStyle = {
        left: `${x + 15}px`,
        top: `${y + 15}px`
      };

      // Show tooltip
      this.showWildfireTooltip = true;
    },

    /**
     * Handle wildfire marker mouse out - hide tooltip
     */
    onWildfireMarkerMouseOut() {
      this.showWildfireTooltip = false;
      this.hoverWildfire = null;
    },

    /**
     * Toggle airport layer on/off
     * @param {Boolean} enabled - Whether to enable or disable airport layer
     */
    async toggleAirportLayer(enabled) {
      // Check if map instance is available
      if (!this.map) {
        console.warn('Map instance not available, cannot toggle airport layer');
        return;
      }

      try {
        this.layerLoading = true;

        if (enabled) {
          console.log('Enabling airport layer...');

          // Load airport data if not already loaded
          if (!this.airportsData) {
            await this.loadAirportData();
          } else {
            console.log('Using cached airport data');
          }

          // Create markers
          this.createAirportMarkers();

          // Add to active layers if not already present
          if (!this.activeLayers.includes('airports')) {
            this.activeLayers.push('airports');
          }

          console.log('Airport layer enabled');
        } else {
          console.log('Disabling airport layer...');

          // Remove markers
          this.clearAirportMarkers();

          // Remove from active layers
          this.activeLayers = this.activeLayers.filter(id => id !== 'airports');

          console.log('Airport layer disabled');
        }

        this.layerLoading = false;
      } catch (error) {
        console.error('Failed to toggle airport layer:', error);
        this.error = 'Failed to load airport data. Please try again.';
        this.layerLoading = false;

        // Revert the toggle state
        if (enabled) {
          this.activeLayers = this.activeLayers.filter(id => id !== 'airports');
          this.clearAirportMarkers();
        }
      }
    },

    /**
     * Toggle team layer on/off
     * @param {Boolean} enabled - Whether to enable or disable team layer
     */
    async toggleTeamLayer(enabled) {
      // Check if map instance is available
      if (!this.map) {
        console.warn('Map instance not available, cannot toggle team layer');
        return;
      }

      try {
        this.layerLoading = true;

        if (enabled) {
          console.log('Enabling team layer...');

          // Load team members data if not already loaded
          if (!this.teamMembersData) {
            await this.loadTeamMembersData();
          } else {
            console.log('Using cached team members data');
          }

          // Create markers
          this.createTeamMarkers();

          // Add to active layers if not already present
          if (!this.activeLayers.includes('team')) {
            this.activeLayers.push('team');
          }

          console.log('Team layer enabled');
        } else {
          console.log('Disabling team layer...');

          // Remove markers
          this.clearTeamMarkers();

          // Remove from active layers
          this.activeLayers = this.activeLayers.filter(id => id !== 'team');

          console.log('Team layer disabled');
        }
      } catch (error) {
        console.error('Error toggling team layer:', error);
        this.error = error.message || 'Failed to toggle team layer';

        // Revert the toggle state
        if (enabled) {
          this.activeLayers = this.activeLayers.filter(id => id !== 'team');
          this.clearTeamMarkers();
        }
      } finally {
        this.layerLoading = false;
      }
    },

    /**
     * Load port data from API
     */
    async loadPortData() {
      try {
        console.log('Loading port data from API...');
        const response = await axios.get('/api/ports');
        this.portsData = response.data;
        console.log(`Loaded ${this.portsData.length} ports from API`);
      } catch (error) {
        console.error('Failed to load port data:', error);
        throw new Error('Failed to load port data from server');
      }
    },

    /**
     * Create markers for all ports
     */
    createPortMarkers() {
      if (!this.portsData || !this.map) {
        console.warn('Cannot create port markers: missing data or map');
        return;
      }

      // Filter ports with valid coordinates
      const validPorts = this.portsData.filter(port =>
        port.latitude && port.longitude
      );

      console.log(`Creating markers for ${validPorts.length} ports with valid coordinates`);

      validPorts.forEach(port => {
        try {
          const marker = new google.maps.Marker({
            position: {
              lat: parseFloat(port.latitude),
              lng: parseFloat(port.longitude)
            },
            map: this.map,
            title: port.name || 'Port',
            icon: {
              url: '/img/map/marker-port.png',
              scaledSize: new google.maps.Size(32, 32)
            }
          });

          // Add click listener for popup
          marker.addListener('click', () => {
            this.selectedPort = port;
            this.showPortPopup = true;
          });

          this.portMarkers.push(marker);
        } catch (error) {
          console.error(`Failed to create marker for port: ${port.name}`, error);
        }
      });

      console.log(`Successfully created ${this.portMarkers.length} port markers`);
    },

    /**
     * Show info window for a port
     */
    showPortInfoWindow(port, marker) {
      // Close any previously open info window
      if (this.currentPortInfoWindow) {
        this.currentPortInfoWindow.close();
      }

      // Center map on the clicked port
      this.map.panTo(marker.getPosition());

      // Determine theme colors
      const isDark = this.isDarkMode;
      const bgColor = isDark ? '#101a2f' : '#ffffff';
      const textColor = isDark ? '#f1f5f9' : '#1f2937';
      const labelColor = isDark ? '#94a3b8' : '#6b7280';
      const tableHeaderBg = isDark ? '#334155' : '#f3f4f6';
      const tableBorder = isDark ? 'rgba(148, 163, 184, 0.2)' : '#e5e7eb';

      // Get port statistics (using dummy data if not available)
      const domestic = port.domestic || '0';
      const foreign = port.foreign || '0';
      const total = port.total || '0';
      const foreignImports = port.foreign_imports || '0';
      const foreignExports = port.foreign_exports || '0';
      const rank = port.rank || 'N/A';

      const infoWindow = new google.maps.InfoWindow({
        content: `
          <div style="
            padding: 20px;
            min-width: 500px;
            max-width: 600px;
            background-color: ${bgColor} !important;
            color: ${textColor};
            border-radius: 12px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            margin: -8px;
          ">
            <h1 style="margin: 0 0 8px 0; font-weight: 600; font-size: 18px; color: ${textColor} !important;">
              ${port.name || 'Port'}
            </h1>

            <p style="margin: 0 0 16px 0; font-size: 14px; color: ${labelColor} !important;">
              Rank: #${rank}
            </p>

            <table style="width: 100%; border-collapse: collapse; margin-top: 8px; background-color: transparent;">
              <thead>
                <tr style="background-color: ${tableHeaderBg} !important;">
                  <th style="padding: 10px; text-align: center; font-weight: 600; font-size: 12px; text-transform: uppercase; color: ${textColor} !important; border: 1px solid ${tableBorder};">DOMESTIC</th>
                  <th style="padding: 10px; text-align: center; font-weight: 600; font-size: 12px; text-transform: uppercase; color: ${textColor} !important; border: 1px solid ${tableBorder};">FOREIGN</th>
                  <th style="padding: 10px; text-align: center; font-weight: 600; font-size: 12px; text-transform: uppercase; color: ${textColor} !important; border: 1px solid ${tableBorder};">TOTAL</th>
                  <th style="padding: 10px; text-align: center; font-weight: 600; font-size: 12px; text-transform: uppercase; color: ${textColor} !important; border: 1px solid ${tableBorder};">FOREIGN IMPORTS</th>
                  <th style="padding: 10px; text-align: center; font-weight: 600; font-size: 12px; text-transform: uppercase; color: ${textColor} !important; border: 1px solid ${tableBorder};">FOREIGN EXPORTS</th>
                </tr>
              </thead>
              <tbody style="background-color: transparent;">
                <tr style="background-color: transparent;">
                  <td style="padding: 12px; text-align: center; font-size: 16px; font-weight: 600; color: ${textColor} !important; border: 1px solid ${tableBorder}; background-color: transparent;">${domestic}</td>
                  <td style="padding: 12px; text-align: center; font-size: 16px; font-weight: 600; color: ${textColor} !important; border: 1px solid ${tableBorder}; background-color: transparent;">${foreign}</td>
                  <td style="padding: 12px; text-align: center; font-size: 16px; font-weight: 600; color: ${textColor} !important; border: 1px solid ${tableBorder}; background-color: transparent;">${total}</td>
                  <td style="padding: 12px; text-align: center; font-size: 16px; font-weight: 600; color: ${textColor} !important; border: 1px solid ${tableBorder}; background-color: transparent;">${foreignImports}</td>
                  <td style="padding: 12px; text-align: center; font-size: 16px; font-weight: 600; color: ${textColor} !important; border: 1px solid ${tableBorder}; background-color: transparent;">${foreignExports}</td>
                </tr>
              </tbody>
            </table>
          </div>
        `,
        disableAutoPan: false,
        maxWidth: 650
      });

      // Store reference to current info window
      this.currentPortInfoWindow = infoWindow;

      // Clear reference when info window is closed
      infoWindow.addListener('closeclick', () => {
        this.currentPortInfoWindow = null;
      });

      infoWindow.open(this.map, marker);
    },

    /**
     * Clear all port markers from map
     */
    clearPortMarkers() {
      console.log(`Clearing ${this.portMarkers.length} port markers`);

      // Close any open info window
      if (this.currentPortInfoWindow) {
        this.currentPortInfoWindow.close();
        this.currentPortInfoWindow = null;
      }

      this.portMarkers.forEach(marker => {
        marker.setMap(null);
        google.maps.event.clearInstanceListeners(marker);
      });

      this.portMarkers = [];
      console.log('Port markers cleared');
    },

    /**
     * Toggle ports layer on/off
     * @param {Boolean} enabled - Whether to enable or disable ports layer
     */
    async togglePortsLayer(enabled) {
      // Check if map instance is available
      if (!this.map) {
        console.warn('Map instance not available, cannot toggle ports layer');
        return;
      }

      try {
        this.layerLoading = true;

        if (enabled) {
          console.log('Enabling ports layer...');

          // Load port data if not already loaded
          if (!this.portsData) {
            await this.loadPortData();
          } else {
            console.log('Using cached port data');
          }

          // Create markers
          this.createPortMarkers();

          // Add to active layers if not already present
          if (!this.activeLayers.includes('ports')) {
            this.activeLayers.push('ports');
          }

          console.log('Ports layer enabled');
        } else {
          console.log('Disabling ports layer...');

          // Remove markers
          this.clearPortMarkers();

          // Remove from active layers
          this.activeLayers = this.activeLayers.filter(id => id !== 'ports');

          console.log('Ports layer disabled');
        }

        this.layerLoading = false;
      } catch (error) {
        console.error('Failed to toggle ports layer:', error);
        this.error = 'Failed to load port data. Please try again.';
        this.layerLoading = false;

        // Revert the toggle state
        if (enabled) {
          this.activeLayers = this.activeLayers.filter(id => id !== 'ports');
          this.clearPortMarkers();
        }
      }
    },

    /**
     * Load protest data from GeoJSON file
     */
    async loadProtestData() {
      try {
        console.log('Loading protest data from GeoJSON file...');
        const response = await fetch('/api/cresiteiq2/layers/static?layer=protests');

        if (!response.ok) {
          throw new Error(`Failed to load protest data: ${response.status} ${response.statusText}`);
        }

        const geojson = await response.json();

        if (!geojson || !geojson.features) {
          throw new Error('Invalid GeoJSON format');
        }

        // Cache the data
        this.protestsData = geojson;
        console.log(`Loaded ${geojson.features.length} protest locations`);
      } catch (error) {
        console.error('Failed to load protest data:', error);
        throw error;
      }
    },

    /**
     * Filter and render protest markers based on current filters
     */
    filterAndRenderProtestMarkers() {
      if (!this.protestsData || !this.map) {
        console.warn('Cannot render protest markers: missing data or map');
        return;
      }

      // Clear existing markers
      this.clearProtestMarkers();

      const features = this.protestsData.features;
      console.log(`Filtering ${features.length} protests with filters:`, this.protestFilters);

      // Filter features based on current filters
      const filteredFeatures = features.filter(feature => {
        const events = feature.properties?.events || [];

        // If no events, skip this feature
        if (events.length === 0) return false;

        // Apply date range filter
        if (this.protestFilters.dateRange && this.protestFilters.dateRange.startDate && this.protestFilters.dateRange.endDate) {
          const startDate = new Date(this.protestFilters.dateRange.startDate);
          const endDate = new Date(this.protestFilters.dateRange.endDate);

          const hasEventInRange = events.some(event => {
            if (!event.event_date) return false;
            const eventDate = new Date(event.event_date);
            return eventDate >= startDate && eventDate <= endDate;
          });

          if (!hasEventInRange) return false;
        }

        // Apply search text filter
        if (this.protestFilters.searchText && this.protestFilters.searchText.trim() !== '') {
          const searchLower = this.protestFilters.searchText.toLowerCase();
          const hasMatch = events.some(event => {
            const text = `${event.event_date || ''} ${event.event_type || ''} ${event.claims_summary || ''}`.toLowerCase();
            return text.includes(searchLower);
          });

          if (!hasMatch) return false;
        }

        // Apply factor filter
        if (this.protestFilters.selectedFactors && this.protestFilters.selectedFactors.length > 0) {
          const hasMatchingFactor = events.some(event => {
            const text = `${event.event_type || ''} ${event.claims_summary || ''}`.toLowerCase();
            return this.protestFilters.selectedFactors.some(factor => text.includes(factor.toLowerCase()));
          });

          if (!hasMatchingFactor) return false;
        }

        return true;
      });

      console.log(`Creating markers for ${filteredFeatures.length} filtered protests`);

      // Create markers for filtered features
      filteredFeatures.forEach(feature => {
        try {
          const coordinates = feature.geometry.coordinates;
          const properties = feature.properties;

          if (!coordinates || coordinates.length < 2) {
            console.warn('Invalid coordinates for protest:', properties);
            return;
          }

          const marker = new google.maps.Marker({
            position: {
              lat: coordinates[1], // GeoJSON uses [lon, lat]
              lng: coordinates[0]
            },
            map: this.map,
            title: properties.location || 'Protest',
            icon: {
              url: '/img/map/protest-icon.png',
              scaledSize: new google.maps.Size(24, 24)
            }
          });

          // Add click listener for popup
          marker.addListener('click', () => {
            this.selectedProtest = properties;
            this.showProtestPopup = true;
          });

          // Store marker
          this.protestMarkers.push(marker);
        } catch (error) {
          console.warn('Failed to create marker for protest:', error);
        }
      });

      console.log(`Created ${this.protestMarkers.length} protest markers`);
    },

    /**
     * Update traffic filters
     * @param {Object} filters - Updated filter values
     */
    updateTrafficFilters(filters) {
      console.log('Updating traffic filters:', filters);
      this.trafficFilters = { ...filters };

      // Apply traffic layer settings if active
      if (this.activeLayers.includes('traffic') && this.trafficLayer) {
        // Google Maps TrafficLayer only supports live traffic
        // If user selects "typical", we show a notice that it's not available

        if (filters.trafficType === 'live') {
          // Ensure traffic layer is visible for live traffic
          if (!this.trafficLayer.getMap()) {
            this.trafficLayer.setMap(this.map);
          }
          console.log('Live traffic enabled');
        } else if (filters.trafficType === 'typical') {
          // Keep showing live traffic since typical is not available
          // The UI will show a notice explaining this limitation
          console.log('Typical traffic requested but not available - showing live traffic with notice');
        }
      }
    },

    /**
     * Initialize available US states for highway filter
     */
    initializeAvailableStates() {
      this.availableStates = [
        { code: 'AL', name: 'Alabama' },
        { code: 'AK', name: 'Alaska' },
        { code: 'AZ', name: 'Arizona' },
        { code: 'AR', name: 'Arkansas' },
        { code: 'CA', name: 'California' },
        { code: 'CO', name: 'Colorado' },
        { code: 'CT', name: 'Connecticut' },
        { code: 'DE', name: 'Delaware' },
        { code: 'FL', name: 'Florida' },
        { code: 'GA', name: 'Georgia' },
        { code: 'HI', name: 'Hawaii' },
        { code: 'ID', name: 'Idaho' },
        { code: 'IL', name: 'Illinois' },
        { code: 'IN', name: 'Indiana' },
        { code: 'IA', name: 'Iowa' },
        { code: 'KS', name: 'Kansas' },
        { code: 'KY', name: 'Kentucky' },
        { code: 'LA', name: 'Louisiana' },
        { code: 'ME', name: 'Maine' },
        { code: 'MD', name: 'Maryland' },
        { code: 'MA', name: 'Massachusetts' },
        { code: 'MI', name: 'Michigan' },
        { code: 'MN', name: 'Minnesota' },
        { code: 'MS', name: 'Mississippi' },
        { code: 'MO', name: 'Missouri' },
        { code: 'MT', name: 'Montana' },
        { code: 'NE', name: 'Nebraska' },
        { code: 'NV', name: 'Nevada' },
        { code: 'NH', name: 'New Hampshire' },
        { code: 'NJ', name: 'New Jersey' },
        { code: 'NM', name: 'New Mexico' },
        { code: 'NY', name: 'New York' },
        { code: 'NC', name: 'North Carolina' },
        { code: 'ND', name: 'North Dakota' },
        { code: 'OH', name: 'Ohio' },
        { code: 'OK', name: 'Oklahoma' },
        { code: 'OR', name: 'Oregon' },
        { code: 'PA', name: 'Pennsylvania' },
        { code: 'RI', name: 'Rhode Island' },
        { code: 'SC', name: 'South Carolina' },
        { code: 'SD', name: 'South Dakota' },
        { code: 'TN', name: 'Tennessee' },
        { code: 'TX', name: 'Texas' },
        { code: 'UT', name: 'Utah' },
        { code: 'VT', name: 'Vermont' },
        { code: 'VA', name: 'Virginia' },
        { code: 'WA', name: 'Washington' },
        { code: 'WV', name: 'West Virginia' },
        { code: 'WI', name: 'Wisconsin' },
        { code: 'WY', name: 'Wyoming' }
      ];
    },



    /**
     * Load highways for selected states (called from accordion)
     * @param {Array} stateCodes - Array of state codes to load
     */
    async loadHighwaysForStates(stateCodes) {
      this.selectedHighwayStates = [...stateCodes];

      try {
        this.layerLoading = true;
        this.setLayerLoading('highways');

        // Load highway data for each state that hasn't been loaded yet
        for (const stateCode of stateCodes) {
          if (!this.loadedHighwayStates[stateCode]) {
            await this.loadHighwayDataForState(stateCode);
          }
        }

        // Filter and draw highways
        this.filterAndDrawHighways();

        console.log('Highways loaded successfully');
      } catch (error) {
        console.error('Failed to load highways:', error);
        this.error = 'Failed to load highway data. Please try again.';
      } finally {
        this.layerLoading = false;
        this.clearLayerLoading('highways');
      }
    },

    /**
     * Load highway data for a specific state
     * @param {String} stateCode - State code (e.g., 'CA', 'NY')
     */
    async loadHighwayDataForState(stateCode) {
      try {
        console.log(`Loading highway data for ${stateCode}...`);
        const response = await fetch(`/api/cresiteiq2/layers/highways?state=${stateCode.toUpperCase()}`);

        if (!response.ok) {
          throw new Error(`Failed to load highway data for ${stateCode}: ${response.status}`);
        }

        const geojson = await response.json();

        // Add features to allHighwaysData, tagging each with type
        geojson.features.forEach(f => {
          if (!f.properties) f.properties = {};
          f.properties.type = 'highway';
        });
        this.allHighwaysData.features.push(...geojson.features);

        // Mark state as loaded
        this.loadedHighwayStates[stateCode] = true;

        console.log(`Loaded ${geojson.features.length} highway features for ${stateCode}`);
      } catch (error) {
        console.error(`Failed to load highway data for ${stateCode}:`, error);
        throw error;
      }
    },

    /**
     * Update selected highway states and re-render
     * @param {Array} states - Array of state codes
     */
    updateHighwayStates(states) {
      this.loadHighwaysForStates(states);
    },

    /**
     * Persist the selected AGS category so it survives panel mode switches
     */
    onAgsCategoryChanged(category) {
      this.selectedAgsCategory = category;
    },

    /**
     * Handle removal of a single dataset variable from the left panel
     */
    onRemoveDatasetVariable(variable) {      const updated = this.selectedAgsVariables.filter(v => {
        if (variable.category_id !== undefined) {
          return !(v.path === variable.path && v.category_id === variable.category_id);
        }
        return v.path !== variable.path;
      });
      this.onAgsVariablesUpdated({ variables: updated });
    },

    /**
     * Handle AGS variables updated
     */
    onAgsVariablesUpdated({ variables }) {
      console.log('=== AGS VARIABLES UPDATED ===');
      console.log('Received variables:', variables);
      console.log('Variables count:', variables.length);
      console.log('Variable details:', variables.map(v => ({
        path: v.path,
        label: v.label,
        category_name: v.category_name,
        category_id: v.category_id,
        geographic_levels: v.geographic_levels
      })));

      // Close popup when variables change to avoid showing stale data
      if (this.showAgsPopup) {
        console.log('Closing AGS popup due to variable change');
        this.closeAgsPopup();
      }

      // Clear API cache when variables change significantly
      const oldVariablePaths = this.selectedAgsVariables.map(v => v.path).sort().join(',');
      const newVariablePaths = variables.map(v => v.path).sort().join(',');
      
      if (oldVariablePaths !== newVariablePaths) {
        console.log('Variable paths changed - clearing API cache');
        this.clearApiCache();
        this.lastSuccessfulParams = null;
      }

      this.selectedAgsVariables = variables;

      console.log('this.selectedAgsVariables after update:', this.selectedAgsVariables);
      console.log('Active tab:', this.activeTab);

      // Auto-switch right panel: show active filters when first variable added,
      // return to controls when all cleared
      if (variables.length > 0 && this.rightPanelMode !== 'filters') {
        this.rightPanelMode = 'filters';
        this.controlPanelCollapsed = false;
      } else if (variables.length === 0) {
        this.rightPanelMode = 'controls';
      }

      // Auto-zoom to appropriate geographic level based on variable's supported levels
      if (variables.length > 0 && this.map) {
        const zoomChanged = this.autoZoomToGeographicLevel(variables);
        
        // If we auto-zoomed, set a flag to prevent handleDataAvailability from changing zoom
        if (zoomChanged) {
          this.skipNextDataAvailabilityZoom = true;
        }
      }

      // The consolidated watcher will handle updating map layers when selectedAgsVariables changes
      if (variables.length === 0) {
        console.log('⚠ No variables selected, clearing AGS layers');
        this.clearAgsLayers();
      } else {
        console.log('✓ Variables updated, watcher will trigger map update');
      }
      console.log('=== END AGS VARIABLES UPDATED ===\n');
    },

    /**
     * Auto-zoom map to appropriate geographic level based on variable's supported levels
     * @returns {boolean} True if zoom was changed, false otherwise
     */
    autoZoomToGeographicLevel(variables) {
      if (!variables || variables.length === 0) {
        return false;
      }

      // Get geographic levels from the first variable's category
      const firstVariable = variables[0];
      const geographicLevels = firstVariable.geographic_levels || [];

      console.log('[MapPage] Auto-zoom: Geographic levels for variable:', geographicLevels);

      if (geographicLevels.length === 0) {
        console.log('[MapPage] Auto-zoom: No geographic levels defined, skipping');
        return false;
      }

      // Determine the most detailed (finest) geographic level available
      // Priority order: block > block_group > tract > zip > county > state
      const levelPriority = ['block', 'block_group', 'tract', 'zip', 'county', 'state'];
      let targetLevel = null;

      for (const level of levelPriority) {
        if (geographicLevels.includes(level)) {
          targetLevel = level;
          break;
        }
      }

      if (!targetLevel) {
        console.log('[MapPage] Auto-zoom: No recognized geographic level found');
        return false;
      }

      // Map geographic level to zoom level using existing ZOOM_LEVELS constants
      const zoomLevelMap = {
        'state': ZOOM_LEVELS.STATES.min,
        'county': ZOOM_LEVELS.COUNTIES.min,
        'zip': ZOOM_LEVELS.ZIP_CODES.min,
        'tract': ZOOM_LEVELS.TRACTS.min,
        'block_group': ZOOM_LEVELS.BLOCK_GROUPS.min,
        'block': ZOOM_LEVELS.BLOCKS.min
      };

      const targetZoom = zoomLevelMap[targetLevel];

      if (targetZoom && targetZoom !== this.currentZoom) {
        console.log(`[MapPage] Auto-zoom: Zooming to ${targetLevel} level (zoom ${targetZoom})`);
        
        // Zoom at current map center (preserving location from AI search or current view)
        this.map.setZoom(targetZoom);
        this.currentZoom = targetZoom;

        // Show notification
        const levelNames = {
          'block': 'Block',
          'block_group': 'Block Group',
          'tract': 'Tract',
          'zip': 'ZIP Code',
          'county': 'County',
          'state': 'State'
        };

        this.$toast?.info(`Zoomed to ${levelNames[targetLevel]} level for selected variable`);
        return true; // Zoom was changed
      } else {
        console.log(`[MapPage] Auto-zoom: Already at appropriate zoom level (${this.currentZoom})`);
        return false; // Zoom was not changed
      }
    },

    /**
     * Handle location detected from AI search
     * Geocode the location and zoom the map to it
     */
    async onLocationDetected(locationData) {
      const { location, scope } = locationData;
      console.log('[MapPage] Location detected:', location, 'Scope:', scope);

      // Validate inputs
      if (!location) {
        console.warn('[MapPage] No location provided');
        return;
      }

      // Check if map is ready
      if (!this.map) {
        console.error('[MapPage] Map not initialized');
        this.$toast?.error('Map not ready. Please try again.');
        return;
      }

      // Check if Google Maps is loaded
      if (typeof google === 'undefined' || !google.maps) {
        console.error('[MapPage] Google Maps not loaded');
        this.$toast?.error('Maps API not loaded. Please refresh the page.');
        return;
      }

      // Store that we detected a location from AI
      this.aiDetectedLocation = location;

      try {
        console.log('[MapPage] Starting geocoding for:', location);
        
        // Use Google Maps Geocoding API
        const geocoder = new google.maps.Geocoder();
        
        geocoder.geocode({ address: location }, (results, status) => {
          console.log('[MapPage] Geocoding status:', status);
          console.log('[MapPage] Geocoding results:', results);
          
          if (status === 'OK' && results[0]) {
            const { lat, lng } = results[0].geometry.location;
            const latLng = new google.maps.LatLng(lat(), lng());
            
            console.log('[MapPage] Geocoded location:', location, '→', [lng(), lat()]);
            
            // Get zoom level from constants
            const zoomLevel = LOCATION_ZOOM_LEVELS[scope] || LOCATION_ZOOM_LEVELS.default;
            
            console.log('[MapPage] Zooming to level:', zoomLevel, 'for scope:', scope);
            
            // Animate map to location using Google Maps API
            this.map.panTo(latLng);
            this.map.setZoom(zoomLevel);
            
            // Update internal state
            this.mapCenter = { lat: lat(), lng: lng() };
            this.currentZoom = zoomLevel;
            
            console.log('[MapPage] Map navigation complete');
            // Show notification
            this.$toast?.success(`Zoomed to ${location}`);
          } else {
            console.error('[MapPage] Geocoding failed:', status);
            this.$toast?.error(`Could not find location: ${location}`);
            // Clear the detected location if geocoding failed
            this.aiDetectedLocation = null;
          }
        });
      } catch (error) {
        console.error('[MapPage] Error geocoding location:', error);
        this.$toast?.error('Failed to zoom to location');
        // Clear the detected location on error
        this.aiDetectedLocation = null;
      }
    },

    /**
     * Handle opacity changes
     */
    onOpacityChanged({ type, value }) {
      if (type === 'ags') {
        this.agsDataOpacity = value;
        this.updateAgsLayerOpacity();
      }
    },

    /**
     * Handle comparison mode toggle
     */
    onComparisonToggled({ enabled, scope }) {
      // Close popup when comparison mode changes to avoid showing stale data
      if (this.showAgsPopup || this.showCrimePopup) {
        console.log('Closing popups due to comparison mode change');
        this.closeAgsPopup();
        this.closeCrimePopup();
      }

      this.comparisonEnabled = enabled;
      if (scope) {
        this.comparisonScope = scope;
      }
    },

    /**
     * Handle reverse colors toggle
     */
    onReverseColorsToggled({ reversed }) {
      this.reverseColors = reversed;

      // The consolidated watcher will handle updating map layers when reverseColors changes
    },

    /**
     * Handle scale direction change for AGS variables
     */
    onScaleDirectionChanged({ variablePath, higherIsBetter, isSingleVariable }) {
      this.$set(this.agsScaleDirections, variablePath, higherIsBetter);

      // For single variable mode, just reverse colors locally without API call
      if (isSingleVariable) {
        this.reverseColors = higherIsBetter;

        // Update existing polygons with reversed colors
        this.updateAgsPolygonColors();
      }
      // For multi-variable mode, the consolidated watcher will handle reloading data
    },

    /**
     * Handle weights change for AGS variables
     */
    onWeightsChanged(weights) {
      this.agsWeights = { ...weights };

      // The consolidated watcher will handle reloading map data when weights change
    },

    /**
     * Handle data availability metadata from API
     * Automatically zooms to appropriate level when data is not available at current level
     */
    handleDataAvailability(availability) {
      console.log('Data availability check:', availability);

      if (!availability) {
        return;
      }

      // If data is available at current level, no action needed
      if (availability.has_data_at_current_level) {
        return;
      }

      // Check if we should skip this zoom (because we just auto-zoomed for variable selection)
      if (this.skipNextDataAvailabilityZoom) {
        console.log('[MapPage] Skipping data availability zoom (already auto-zoomed for variable)');
        this.skipNextDataAvailabilityZoom = false;
        return;
      }

      // If no data available at any level
      if (!availability.suggested_level || availability.available_levels.length === 0) {
        this.$toast.warning(availability.message || 'No data available for selected categories', {
          timeout: 5000
        });
        return;
      }

      // Data available at other levels - automatically zoom to suggested level
      const suggestedZoom = availability.zoom_suggestions[availability.suggested_level];

      if (suggestedZoom && this.map) {
        const levelNames = {
          'block': 'Block',
          'block_group': 'Block Group',
          'tract': 'Tract',
          'zip': 'ZIP Code',
          'state': 'State'
        };

        const currentLevelName = levelNames[availability.current_level] || availability.current_level;
        const suggestedLevelName = levelNames[availability.suggested_level] || availability.suggested_level;

        // CRITICAL: Check if location came from AI prompt
        if (this.aiDetectedLocation) {
          // Location was specified in AI prompt - zoom at current map center (don't ask for user's location)
          console.log('[MapPage] AI location detected, zooming at current center:', this.aiDetectedLocation);
          this.map.setZoom(suggestedZoom.recommended);
          this.currentZoom = suggestedZoom.recommended;

          this.$toast.info(
            `No data at ${currentLevelName} level. Zoomed to ${suggestedLevelName} level.`,
            { timeout: 4000 }
          );
          
          // Clear the AI detected location after handling
          this.aiDetectedLocation = null;
        } else {
          // No location in AI prompt - use existing behavior (try user's geolocation first)
          console.log('[MapPage] No AI location, trying user geolocation');
          
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                const userLatLng = new google.maps.LatLng(
                  position.coords.latitude,
                  position.coords.longitude
                );
                
                this.map.setCenter(userLatLng);
                this.map.setZoom(suggestedZoom.recommended);
                this.currentZoom = suggestedZoom.recommended;

                this.$toast.info(
                  `No data at ${currentLevelName} level. Zoomed to ${suggestedLevelName} level at your location.`,
                  { timeout: 4000 }
                );
              },
              (error) => {
                // Geolocation failed - fallback to current map center
                console.log('[MapPage] Geolocation failed, using map center:', error);
                this.map.setZoom(suggestedZoom.recommended);
                this.currentZoom = suggestedZoom.recommended;

                this.$toast.info(
                  `No data at ${currentLevelName} level. Zoomed to ${suggestedLevelName} level.`,
                  { timeout: 4000 }
                );
              },
              {
                enableHighAccuracy: false,
                timeout: 3000,
                maximumAge: 60000
              }
            );
          } else {
            // Geolocation not supported - zoom at current map center
            console.log('[MapPage] Geolocation not supported, using map center');
            this.map.setZoom(suggestedZoom.recommended);
            this.currentZoom = suggestedZoom.recommended;

            this.$toast.info(
              `No data at ${currentLevelName} level. Zoomed to ${suggestedLevelName} level.`,
              { timeout: 4000 }
            );
          }
        }
      }
    },

    /**
     * Handle zoom changed from controls
     */
    onZoomChanged({ zoom }) {
      if (this.map && zoom !== this.currentZoom) {
        this.map.setZoom(zoom);
      }
    },

    /**
     * Handle center changed from controls
     */
    onCenterChanged({ center }) {
      if (this.map) {
        this.map.setCenter(center);
      }
    },

    /**
     * Handle zoom level changed from ZoomControl
     * This is triggered when zoom changes and determines if data should be loaded
     */
    onZoomLevelChanged(data) {
      console.log('Zoom level changed:', data);

      // Update current layer type based on zone level
      this.currentLayerType = data.zoneLevel.toLowerCase().replace(' ', '_');

      // The consolidated watcher will handle loading data when zoom changes
    },

    /**
     * Handle AGS variables changed from ZoomControl
     * This is triggered when AGS variables are selected/changed in FilterPanel
     */
    onAgsVariablesLoadData(data) {
      console.log('AGS variables changed, loading data:', data);

      // The consolidated watcher will handle loading map data when AGS variables change
    },

    /**
     * Update map layers based on current state
     * OPTIMIZED: Includes request deduplication, caching, and better error handling
     */
    async updateMapLayers() {
      if (!this.map || !this.currentBounds) {
        return;
      }

      // Cancel any pending request
      if (this.currentRequestController) {
        this.currentRequestController.abort();
        this.currentRequestController = null;
      }

      // Only fetch data on filters tab
      if (this.activeTab !== 'filters') {
        return;
      }

      try {
        // Build API parameters
        const params = this.buildApiParams();
        if (!params) {
          return;
        }

        // Generate cache key
        const cacheKey = this.generateCacheKey(params);

        // Check if we already have this exact data (avoid redundant fetches)
        if (this.lastSuccessfulParams === cacheKey) {
          console.log('[MapPage API] Skipping - already have this data');
          return;
        }

        // Check cache first
        const cachedData = this.getCachedResponse(cacheKey);
        if (cachedData) {
          console.log('[MapPage API] Using cached response');
          await this.updateAgsLayer(cachedData);
          this.lastSuccessfulParams = cacheKey;
          return;
        }

        // Check if there's already a pending request for the same params
        if (this.pendingApiRequest && this.lastApiParams === cacheKey) {
          console.log('[MapPage API] Reusing pending request');
          try {
            const data = await this.pendingApiRequest;
            await this.updateAgsLayer(data);
            this.lastSuccessfulParams = cacheKey;
          } catch (error) {
            // Ignore if aborted
            if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
              throw error;
            }
          }
          return;
        }

        // Create abort controller for this request
        this.currentRequestController = new AbortController();

        // Fetch data from API and store the promise for deduplication
        this.pendingApiRequest = this.fetchMapData(params, this.currentRequestController.signal);
        const data = await this.pendingApiRequest;
        
        this.currentRequestController = null;
        this.pendingApiRequest = null;

        // Cache the response
        this.cacheResponse(cacheKey, data);

        // Handle data availability notification
        if (data.metadata?.ags_data_availability) {
          this.handleDataAvailability(data.metadata.ags_data_availability);
        }

        // Render polygons with AGS data
        if (data.features) {
          await this.updateAgsLayer(data);
          this.lastSuccessfulParams = cacheKey;
        }
      } catch (error) {
        // Ignore cancellation errors
        if (error.name === 'AbortError' || error.name === 'CanceledError' || error.message === 'canceled') {
          return;
        }
        console.error('Failed to update map layers:', error);
        this.error = error.message || ERROR_MESSAGES.NETWORK_ERROR;
      } finally {
        this.pendingApiRequest = null;
      }
    },

    /**
     * Generate a unique cache key from API parameters
     */
    generateCacheKey(params) {
      return JSON.stringify(params);
    },

    /**
     * Get cached API response if available and not expired
     */
    getCachedResponse(cacheKey) {
      const cached = this.apiResponseCache.get(cacheKey);
      
      if (!cached) {
        return null;
      }

      // Check if cache entry has expired
      const now = Date.now();
      if (now - cached.timestamp > this.cacheExpiryMs) {
        this.apiResponseCache.delete(cacheKey);
        return null;
      }

      return cached.data;
    },

    /**
     * Cache API response with timestamp
     */
    cacheResponse(cacheKey, data) {
      // Implement LRU cache eviction if cache is full
      if (this.apiResponseCache.size >= this.maxCacheSize) {
        // Remove oldest entry
        const firstKey = this.apiResponseCache.keys().next().value;
        this.apiResponseCache.delete(firstKey);
      }

      this.apiResponseCache.set(cacheKey, {
        data: data,
        timestamp: Date.now()
      });
    },

    /**
     * Clear API response cache
     */
    clearApiCache() {
      this.apiResponseCache.clear();
      console.log('[MapPage] API cache cleared');
    },

    /**
     * Build API parameters from current state
     */
    buildApiParams() {
      if (!this.currentBounds) {
        return null;
      }

      const layerType = cresiteiq2Api.getLayerForZoom(this.currentZoom);
      this.currentLayerType = layerType;

      const bboxString = cresiteiq2Api.buildBboxString(this.currentBounds);
      if (!bboxString || bboxString === 'undefined,undefined,undefined,undefined') {
        return null;
      }

      const params = {
        zoom: this.currentZoom,
        bbox: bboxString,
        layer: layerType
      };

      // Add AGS parameters if variables are selected
      if (this.selectedAgsVariables.length > 0) {
        const categoryNames = [...new Set(this.selectedAgsVariables.map(v => v.category_name))].filter(Boolean);
        const variablePaths = this.selectedAgsVariables.map(v => v.path);

        if (categoryNames.length > 0 && variablePaths.length > 0) {
          params.agsCategories = categoryNames.join(',');
          params.agsVariable = variablePaths.join(',');
          params.agsTemporalPeriod = '2025';
          params.agsComparisonMode = this.comparisonEnabled ? this.comparisonScope : 'viewport';

          // Multi-variable mode: add scale directions and weights
          if (variablePaths.length >= 2) {
            params.scaleDirections = {};
            params.weights = {};
            variablePaths.forEach(path => {
              params.scaleDirections[path] = this.agsScaleDirections[path] ? 1 : 0;
              params.weights[path] = this.agsWeights[path] || (1.0 / variablePaths.length);
            });
          }
        }
      }

      return params;
    },

    _startProgress() {
      this.loadProgress = 0;
      clearInterval(this._progressTimer);
      this._progressTimer = setInterval(() => {
        if (this.loadProgress < 90) {
          this.loadProgress += (90 - this.loadProgress) * 0.06;
        }
      }, 80);
    },

    _completeProgress() {
      clearInterval(this._progressTimer);
      this._progressTimer = null;
      this.loadProgress = 100;
      setTimeout(() => {
        this.loadProgress = 0;
      }, 400);
    },

    _resetProgress() {
      clearInterval(this._progressTimer);
      this._progressTimer = null;
    },

    /**
     * Fetch map data from API
     * OPTIMIZED: Reduced logging, better error handling
     */
    async fetchMapData(params, signal = null) {
      // Generate a unique key for this request
      const paramsKey = JSON.stringify(params);
      
      // Track API call metrics (only log if different from last call)
      const isDuplicate = paramsKey === this.lastApiParams;
      this.apiCallCount++;
      const callNumber = this.apiCallCount;
      const now = Date.now();
      const timeSinceLastCall = this.lastApiCallTime ? now - this.lastApiCallTime : null;
      
      this.lastApiParams = paramsKey;
      this.lastApiCallTime = now;
      
      // Only log non-duplicate calls or if it's been more than 1 second
      if (!isDuplicate || !timeSinceLastCall || timeSinceLastCall > 1000) {
        console.log(`[MapPage API] Call #${callNumber}:`, {
          layer: params.layer,
          zoom: params.zoom,
          hasAgsVariables: !!params.agsVariable,
          timeSinceLastCall: timeSinceLastCall ? `${timeSinceLastCall}ms` : 'first call',
          isDuplicate
        });
      }

      this.loading = true;
      this._startProgress();

      try {
        const startTime = Date.now();
        const data = await cresiteiq2Api.getLayers(params, signal);
        const duration = Date.now() - startTime;
        
        // Only log slow requests (> 1 second)
        if (duration > 1000) {
          console.log(`[MapPage API] Call #${callNumber} completed in ${duration}ms (SLOW)`);
        }
        
        this.loading = false;
        this._completeProgress();
        return data;
      } catch (error) {
        // Check if this is a cancellation error (expected when aborting requests)
        if (error.name === 'CanceledError' || error.name === 'AbortError' || error.message === 'canceled') {
          this.loading = false;
          this._resetProgress();
          throw error; // Re-throw so updateMapLayers can handle it
        }

        // Only log unexpected errors
        console.error(`[MapPage API] Call #${callNumber} failed:`, error.message);
        this.loading = false;
        this._resetProgress();
        throw error;
      }
    },

    /**
     * Update crime data layer
     */
    async updateCrimeLayer(data) {
      // Clear existing crime layers
      this.clearCrimeLayers();

      if (!data.features || data.features.length === 0) {
        this.showCrimeLegend = false;
        return;
      }

      // Check if any features have crime data
      const hasCrimeData = data.features.some(f => f.properties?.crime_data);

      // Generate color scale for crime data if available
      if (hasCrimeData) {
        const colorScale = this.generateCrimeColorScale(data.features);
        this.crimeColorScale = colorScale;
      }

      // Render crime polygons on map
      data.features.forEach(feature => {
        // Skip features without geometry
        if (!feature.geometry) {
          return;
        }

        const polygon = this.createPolygon(feature, 'crime');
        if (polygon) {
          this.polygonLayers.push({
            type: 'crime',
            polygon: polygon,
            feature: feature
          });
        }
      });

      // Show crime legend only if we have crime data
      this.showCrimeLegend = hasCrimeData;
    },

    /**
     * Update AGS data layer
     */
    async updateAgsLayer(data) {
      console.log('\n=== UPDATE AGS LAYER ===');
      console.log('Features count:', data.features?.length);

      // Clear existing AGS layers
      this.clearAgsLayers();

      if (!data.features || data.features.length === 0) {
        console.warn('No features to render');
        this.showAgsLegend = false;
        return;
      }

      // Check if any features have AGS data
      const hasAgsData = data.features.some(f => f.properties?.ags_data || f.properties?.ags_intensity_level);
      console.log('Has AGS data:', hasAgsData);

      // Count features with AGS data
      const featuresWithData = data.features.filter(f =>
        f.properties?.ags_data || f.properties?.ags_intensity_level
      );
      console.log('Features with AGS data:', featuresWithData.length);

      // Generate color scale for AGS data if available
      if (hasAgsData) {
        const colorScale = this.generateAgsColorScale(data.features);
        this.agsColorScale = colorScale;
        console.log('Generated color scale:', colorScale);
      }

      // Render ALL features (not just those with data)
      // This ensures complete polygon coverage
      let renderedCount = 0;
      data.features.forEach(feature => {
        // Skip features without geometry
        if (!feature.geometry) {
          return;
        }

        const polygon = this.createPolygon(feature, 'ags');
        if (polygon) {
          this.polygonLayers.push({
            type: 'ags',
            polygon: polygon,
            feature: feature
          });
          renderedCount++;

          // Log first few polygons for debugging
          if (renderedCount <= 3) {
            console.log(`Rendered polygon ${renderedCount}:`, {
              name: feature.properties?.name,
              code: feature.properties?.code || feature.properties?.geoid,
              ags_intensity_level: feature.properties?.ags_intensity_level,
              has_ags_data: !!feature.properties?.ags_data,
              fillColor: polygon.get('fillColor')
            });
          }
        }
      });

      console.log('Total polygons rendered:', renderedCount);

      // Show AGS legend only if we have AGS data
      this.showAgsLegend = hasAgsData;
      console.log('Show AGS legend:', this.showAgsLegend);
      console.log('=== END UPDATE AGS LAYER ===\n');
    },

    /**
     * Create a polygon from GeoJSON feature
     */
    createPolygon(feature, layerType) {
      if (!feature.geometry || !feature.geometry.coordinates) {
        console.warn('Feature missing geometry:', feature);
        return null;
      }

      try {
        // Convert GeoJSON coordinates to Google Maps format
        const paths = this.convertGeoJsonToGoogleMaps(feature.geometry);

        if (!paths || paths.length === 0) {
          console.warn('No paths generated for feature:', feature.properties?.name);
          return null;
        }

        // Determine fill color based on layer type
        const fillColor = layerType === 'crime'
          ? this.getCrimeFillColor(feature)
          : this.getAgsFillColor(feature);

        // Determine opacity based on layer type
        const fillOpacity = layerType === 'crime'
          ? this.crimeDataOpacity
          : this.agsDataOpacity;

        // Determine if this feature has data
        const hasData = layerType === 'crime'
          ? feature.properties?.crime_data
          : feature.properties?.ags_data;

        // Show boundaries prominently when no data is present
        // Use higher opacity for better visibility, especially at state level
        const boundaryOpacity = !hasData ? 0.4 : fillOpacity;

        // Use lighter stroke color in dark mode for better visibility
        const strokeColor = this.isDarkMode ? '#94a3b8' : '#475569'; // slate-400 / slate-600

        // Create polygon with appropriate styling
        const polygon = new google.maps.Polygon({
          paths: paths,
          strokeColor: strokeColor,
          strokeOpacity: hasData ? 0.6 : 0.9, // More visible stroke for boundaries without data
          strokeWeight: hasData ? 1.5 : 2, // Thicker stroke for boundaries
          fillColor: fillColor,
          fillOpacity: boundaryOpacity,
          map: this.map
        });

        // Store intensity level on polygon for later color updates (AGS only)
        if (layerType === 'ags' && feature.properties?.ags_intensity_level) {
          polygon.set('intensityLevel', feature.properties.ags_intensity_level);
        }

        // Store feature ID on polygon for lookup (to avoid stale feature references in closures)
        const featureId = feature.properties?.code || feature.properties?.state_code || feature.properties?.id || feature.properties?.geoid;
        polygon.set('featureId', featureId);
        polygon.set('layerType', layerType);

        // Add click listener for popup - look up current feature instead of using closure
        polygon.addListener('click', (event) => {
          // Find the current feature from polygonLayers to avoid stale data
          const currentLayer = this.polygonLayers.find(layer =>
            layer.polygon === polygon && layer.type === layerType
          );
          const currentFeature = currentLayer ? currentLayer.feature : feature;
          this.onPolygonClick(currentFeature, event, layerType);
        });

        // Add hover listeners for tooltip
        polygon.addListener('mouseover', (event) => {
          // Find the current feature from polygonLayers to avoid stale data
          const currentLayer = this.polygonLayers.find(layer =>
            layer.polygon === polygon && layer.type === layerType
          );
          const currentFeature = currentLayer ? currentLayer.feature : feature;
          this.onPolygonMouseOver(currentFeature, event, layerType);
        });

        polygon.addListener('mouseout', () => {
          this.onPolygonMouseOut();
        });

        return polygon;
      } catch (error) {
        console.error('Failed to create polygon for feature:', feature.properties?.name, error);
        return null;
      }
    },

    /**
     * Convert GeoJSON coordinates to Google Maps format
     */
    convertGeoJsonToGoogleMaps(geometry) {
      const type = geometry.type;
      const coordinates = geometry.coordinates;

      if (type === 'Polygon') {
        // Polygon has array of rings (first is outer, rest are holes)
        return coordinates.map(ring =>
          ring.map(coord => ({ lat: coord[1], lng: coord[0] }))
        );
      } else if (type === 'MultiPolygon') {
        // MultiPolygon has array of polygons
        // Flatten all polygons into a single array of paths for Google Maps
        // Google Maps Polygon can handle multiple paths (outer rings and holes)
        const allPaths = [];
        coordinates.forEach(polygon => {
          polygon.forEach(ring => {
            allPaths.push(ring.map(coord => ({ lat: coord[1], lng: coord[0] })));
          });
        });
        return allPaths;
      }

      return [];
    },

    /**
     * Get fill color for crime polygon
     */
    getCrimeFillColor(feature) {
      const crimeData = feature.properties?.crime_data;

      if (!crimeData || !crimeData.risk_level) {
        // Return transparent for features without data so boundaries are visible
        return '#808080'; // Neutral gray for boundaries
      }

      // Map risk level to color
      const riskColors = {
        1: '#10b981', // Very Low - Green
        2: '#84cc16', // Low - Light Green
        3: '#eab308', // Moderate - Yellow
        4: '#f97316', // High - Orange
        5: '#ef4444'  // Very High - Red
      };

      return riskColors[crimeData.risk_level] || '#808080';
    },

    /**
     * Get fill color for AGS polygon
     */
    getAgsFillColor(feature) {
      // Read intensity level from feature properties
      // The API returns ags_intensity_level directly in properties
      const intensityLevel = feature.properties?.ags_intensity_level;

      // If no intensity level, check if there's AGS data at all

      if (!intensityLevel || intensityLevel === 'no_data') {
        // Return neutral gray for features without data so boundaries are visible
        return '#9ca3af'; // Gray-400 for no data
      }

      // Map intensity level to colors as specified in design document
      // Default scale (higher is worse): very_low (green) → very_high (red)
      const defaultColors = {
        'very_low': '#059669',      // Dark green (emerald-600)
        'low': '#22c55e',           // Green (green-500)
        'low_moderate': '#84cc16',  // Yellow-green (lime-500)
        'moderate': '#eab308',      // Yellow (yellow-500)
        'moderate_high': '#f97316', // Orange (orange-500)
        'high': '#ef4444',          // Red (red-500)
        'very_high': '#dc2626',     // Dark red (red-600)
        'no_data': '#9ca3af'        // Gray (gray-400)
      };

      // Reversed scale (higher is better): very_low (red) → very_high (green)
      const reversedColors = {
        'very_low': '#dc2626',      // Dark red (red-600)
        'low': '#ef4444',           // Red (red-500)
        'low_moderate': '#f97316',  // Orange (orange-500)
        'moderate': '#eab308',      // Yellow (yellow-500)
        'moderate_high': '#84cc16', // Yellow-green (lime-500)
        'high': '#22c55e',          // Green (green-500)
        'very_high': '#059669',     // Dark green (emerald-600)
        'no_data': '#9ca3af'        // Gray (gray-400)
      };

      // Use reversed colors if reverseColors is true
      const colorMap = this.reverseColors ? reversedColors : defaultColors;

      return colorMap[intensityLevel] || '#9ca3af';
    },

    /**
     * Generate color scale for crime data
     */
    generateCrimeColorScale(features) {
      const scale = [
        { color: '#10b981', label: 'Very Low', value: 1 },
        { color: '#84cc16', label: 'Low', value: 2 },
        { color: '#eab308', label: 'Moderate', value: 3 },
        { color: '#f97316', label: 'High', value: 4 },
        { color: '#ef4444', label: 'Very High', value: 5 }
      ];

      return scale;
    },

    /**
     * Generate color scale for AGS data
     */
    generateAgsColorScale(features) {
      // Use 7-level intensity scale as specified in design document
      // Default scale (higher is worse): very_low (green) → very_high (red)
      const defaultScale = [
        { color: '#059669', label: 'Very Low', value: 'very_low' },
        { color: '#22c55e', label: 'Low', value: 'low' },
        { color: '#84cc16', label: 'Low-Moderate', value: 'low_moderate' },
        { color: '#eab308', label: 'Moderate', value: 'moderate' },
        { color: '#f97316', label: 'Moderate-High', value: 'moderate_high' },
        { color: '#ef4444', label: 'High', value: 'high' },
        { color: '#dc2626', label: 'Very High', value: 'very_high' },
        { color: '#9ca3af', label: 'No Data', value: 'no_data' }
      ];

      // Reversed scale (higher is better): very_low (red) → very_high (green)
      const reversedScale = [
        { color: '#dc2626', label: 'Very Low', value: 'very_low' },
        { color: '#ef4444', label: 'Low', value: 'low' },
        { color: '#f97316', label: 'Low-Moderate', value: 'low_moderate' },
        { color: '#eab308', label: 'Moderate', value: 'moderate' },
        { color: '#84cc16', label: 'Moderate-High', value: 'moderate_high' },
        { color: '#22c55e', label: 'High', value: 'high' },
        { color: '#059669', label: 'Very High', value: 'very_high' },
        { color: '#9ca3af', label: 'No Data', value: 'no_data' }
      ];

      return this.reverseColors ? reversedScale : defaultScale;
    },

    /**
     * Handle polygon click - show appropriate popup
     */
    onPolygonClick(feature, event, layerType) {
      // Close any existing popups
      this.closeCrimePopup();
      this.closeAgsPopup();

      // Store selected feature
      this.selectedFeature = feature;

      // Get the polygon color for debug display
      const polygon = this.polygonLayers.find(
        layer => layer.feature === feature && layer.type === layerType
      );
      this.selectedPolygonColor = polygon ? polygon.polygon.get('fillColor') : null;

      // Show appropriate popup based on layer type and available data
      // Show AGS popup if AGS variables are selected OR AGS layer is enabled
      if (layerType === 'ags' && (this.selectedAgsVariables.length > 0)) {
        this.showAgsPopup = true;
      } else if (layerType === 'crime') {
        this.showCrimePopup = true;
      } else if (this.selectedAgsVariables.length > 0) {
        // If AGS variables are selected but layerType is not 'ags', still show AGS popup
        this.showAgsPopup = true;
      }
    },

    /**
     * Handle polygon hover - show tooltip
     */
    onPolygonMouseOver(feature, event, layerType) {
      // Store hover feature
      this.hoverFeature = feature;

      // Position tooltip near cursor
      const x = event.domEvent.clientX;
      const y = event.domEvent.clientY;

      this.hoverTooltipStyle = {
        left: `${x + 15}px`,
        top: `${y + 15}px`
      };

      // Show tooltip
      this.showHoverTooltip = true;
    },

    /**
     * Handle polygon mouse out - hide tooltip
     */
    onPolygonMouseOut() {
      this.showHoverTooltip = false;
      this.hoverFeature = null;
    },

    /**
     * Clear crime layers
     */
    clearCrimeLayers() {
      // Remove crime polygons from map
      this.polygonLayers
        .filter(layer => layer.type === 'crime')
        .forEach(layer => {
          if (layer.polygon) {
            layer.polygon.setMap(null);
          }
        });

      // Remove from array
      this.polygonLayers = this.polygonLayers.filter(layer => layer.type !== 'crime');

      // Hide legend
      this.showCrimeLegend = false;
    },

    /**
     * Clear AGS layers
     */
    clearAgsLayers() {
      // Remove AGS polygons from map
      this.polygonLayers
        .filter(layer => layer.type === 'ags')
        .forEach(layer => {
          if (layer.polygon) {
            layer.polygon.setMap(null);
          }
        });

      // Remove from array
      this.polygonLayers = this.polygonLayers.filter(layer => layer.type !== 'ags');

      // Hide legend
      this.showAgsLegend = false;
    },

    /**
     * Clear all layers
     */
    clearAllLayers() {
      this.clearCrimeLayers();
      this.clearAgsLayers();
    },

    /**
     * Update AGS layer opacity
     */
    updateAgsLayerOpacity() {
      this.polygonLayers
        .filter(layer => layer.type === 'ags')
        .forEach(layer => {
          if (layer.polygon) {
            layer.polygon.setOptions({
              fillOpacity: this.agsDataOpacity
            });
          }
        });
    },

    /**
     * Update AGS polygon colors (for scale direction changes)
     */
    updateAgsPolygonColors() {
      this.polygonLayers
        .filter(layer => layer.type === 'ags')
        .forEach(layer => {
          if (layer.polygon && layer.feature) {
            // Get the stored intensity level from the polygon
            const intensityLevel = layer.polygon.get('intensityLevel');

            if (intensityLevel) {
              // Create a temporary feature object with the intensity level
              // to pass to getAgsFillColor
              const tempFeature = {
                properties: {
                  ags_intensity_level: intensityLevel,
                  ags_data: layer.feature.properties?.ags_data
                }
              };

              const newColor = this.getAgsFillColor(tempFeature);
              layer.polygon.setOptions({
                fillColor: newColor
              });
            }
          }
        });

      // Update legend colors
      if (this.showAgsLegend) {
        const features = this.polygonLayers
          .filter(layer => layer.type === 'ags')
          .map(layer => layer.feature);

        if (features.length > 0) {
          this.agsColorScale = this.generateAgsColorScale(features);
        }
      }
    },

    /**
     * Hide AGS legend
     */
    hideAgsLegend() {
      this.showAgsLegend = false;
    },

    /**
     * Show AGS legend (called from FilterPanel "Show Legend" button)
     */
    showLegend() {
      this.showAgsLegend = true;
      console.log('[MapPage] Legend shown via FilterPanel button');
    },

    /**
     * Dismiss error notification
     */
    dismissError() {
      this.error = null;
    },

    /**
     * Close crime popup
     */
    closeCrimePopup() {
      this.showCrimePopup = false;
      this.selectedFeature = null;
      this.selectedPolygonColor = null;
    },

    /**
     * Close AGS popup
     */
    closeAgsPopup() {
      this.showAgsPopup = false;
      this.selectedFeature = null;
      this.selectedPolygonColor = null;
    },

    /**
     * Get display name for a feature
     */
    getFeatureDisplayName(feature) {
      if (!feature || !feature.properties) {
        return 'Unknown';
      }

      const props = feature.properties;
      const code = props.geoid || props.code || '';

      // Generate friendly name based on layer type
      if (this.currentLayerType === 'states') {
        return `State ${code}`;
      } else if (this.currentLayerType === 'counties') {
        return `County ${code}`;
      } else if (this.currentLayerType === 'zips') {
        return `ZIP ${code}`;
      } else if (this.currentLayerType === 'tracts') {
        return `Tract ${code}`;
      } else if (this.currentLayerType === 'block_group') {
        return `Block Group ${code}`;
      } else if (this.currentLayerType === 'block') {
        return `Block ${code}`;
      }

      return code;
    },

    /**
     * Format risk level for display
     */
    formatRiskLevel(riskLevel) {
      if (!riskLevel) return 'N/A';

      const riskLevels = {
        1: 'Very Low',
        2: 'Low',
        3: 'Moderate',
        4: 'High',
        5: 'Very High',
        'very_low': 'Very Low',
        'low': 'Low',
        'moderate': 'Moderate',
        'high': 'High',
        'very_high': 'Very High'
      };

      return riskLevels[riskLevel] || riskLevel;
    },

    /**
     * Get risk badge class
     */
    getRiskBadgeClass(riskLevel) {
      return getRiskLevelBadgeClass(riskLevel);
    },

    /**
     * Format AGS value for display
     */
    formatAgsValue(value) {
      // Use the helper function from mapHelpers
      const variableType = this.selectedAgsVariables.length > 0
        ? this.selectedAgsVariables[0].format || 'number'
        : 'number';

      return formatAgsValue(value, variableType);
    },

    /**
     * Format number with abbreviations (K, M, B)
     */
    formatNumber(value) {
      if (value === null || value === undefined) {
        return 'N/A';
      }

      const num = parseFloat(value);
      if (isNaN(num)) {
        return 'N/A';
      }

      if (num >= 1000000000) {
        return (num / 1000000000).toFixed(1) + 'B';
      } else if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
      } else if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'k';
      }

      return num.toLocaleString();
    },

    /**
     * Format wildfire size for tooltip
     */
    formatWildfireSize(size) {
      if (size === null || size === undefined) return 'N/A';

      const formattedNumber = parseFloat(size).toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });

      return `${formattedNumber} acres`;
    },

    /**
     * Format wildfire date for tooltip (short format)
     */
    formatWildfireDate(dateString) {
      if (!dateString) return 'N/A';

      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'N/A';

        // Format as "Jun 19, 2025"
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch (error) {
        return 'N/A';
      }
    },

    /**
     * Get AGS variable value from ags_data object
     */
    getAgsVariableValue(agsData, variable) {
      const rawValue = this.getAgsVariableRawValue(agsData, variable);
      if (rawValue === null) {
        return 'N/A';
      }
      return this.formatAgsVariableValue(rawValue, variable.format);
    },

    /**
     * Get raw AGS variable value (unformatted) for debug display
     */
    getAgsVariableRawValue(agsData, variable) {
      if (!agsData) {
        return null;
      }

      // Access data using category_name as key: agsData[category_name].extracted_values[variable_path]
      const categoryData = agsData[variable.category_name];

      if (categoryData && categoryData.extracted_values) {
        const value = categoryData.extracted_values[variable.path];
        if (value !== undefined && value !== null) {
          return value;
        }
      }

      // Fallback to flat structure (single variable case)
      const value = agsData[variable.path];
      if (value !== undefined && value !== null) {
        return value;
      }

      return null;
    },

    /**
     * Get AGS data category display name
     */
    getAgsDataCategoryName(agsData) {
      if (!agsData) return 'AGS Data';

      // Use category_name from first selected variable to access the data
      if (this.selectedAgsVariables.length > 0) {
        const categoryName = this.selectedAgsVariables[0].category_name;
        const categoryData = agsData[categoryName];

        if (categoryData && categoryData.category_display_name) {
          return categoryData.category_display_name;
        }

        // Fallback to category_name itself
        return categoryName;
      }

      return 'AGS Data';
    },

    /**
     * Format intensity level for display
     */
    formatIntensityLevel(level) {
      if (!level) return 'N/A';

      const levels = {
        'very_low': 'Very Low',
        'low': 'Low',
        'low_moderate': 'Low-Moderate',
        'moderate': 'Moderate',
        'moderate_high': 'Moderate-High',
        'high': 'High',
        'very_high': 'Very High',
        'no_data': 'No Data'
      };

      return levels[level] || level;
    },

    /**
     * Get ranking text from feature properties
     */
    getRankingText(properties) {
      const ref = properties.ags_comparison_reference;
      if (!ref || !ref.total_entities) {
        return 'N/A';
      }

      // Calculate ranking based on intensity level
      // This is a simplified version - the actual ranking would come from the API
      const intensityLevels = ['very_low', 'low', 'low_moderate', 'moderate', 'moderate_high', 'high', 'very_high'];
      const currentLevel = properties.ags_intensity_level;
      const levelIndex = intensityLevels.indexOf(currentLevel);

      if (levelIndex === -1) return 'N/A';

      // Estimate ranking (this is approximate)
      const totalEntities = ref.total_entities;
      const estimatedRank = Math.floor((levelIndex / intensityLevels.length) * totalEntities) + 1;
      const percentile = Math.floor((levelIndex / intensityLevels.length) * 100);

      return `${estimatedRank} out of ${totalEntities} in ${ref.type} (${percentile}%)`;
    },

    /**
     * Format AGS variable value based on its type
     */
    formatAgsVariableValue(value, format) {
      if (value === null || value === undefined) {
        return 'N/A';
      }

      const num = parseFloat(value);
      if (isNaN(num)) {
        return 'N/A';
      }

      // Format based on variable type
      if (format === 'currency') {
        if (num >= 1000000) {
          return '$' + (num / 1000000).toFixed(1) + 'M';
        } else if (num >= 1000) {
          return '$' + (num / 1000).toFixed(1) + 'k';
        }
        return '$' + num.toLocaleString();
      } else if (format === 'percent') {
        return (num * 100).toFixed(1) + '%';
      } else {
        // Number format with abbreviations
        return this.formatNumber(num);
      }
    },

    /**
     * Check if viewport is mobile
     */
    checkMobile() {
      this.isMobile = window.innerWidth < 768;
    },

    /**
     * Detect dark mode from document
     */
    detectDarkMode() {
      // Check if dark class is on html or body element
      const htmlElement = document.documentElement;
      const bodyElement = document.body;

      this.isDarkMode = htmlElement.classList.contains('dark') ||
                        bodyElement.classList.contains('dark') ||
                        htmlElement.getAttribute('data-theme') === 'dark';

      console.log('Dark mode detected:', this.isDarkMode);
    },

    /**
     * Observe theme changes using MutationObserver
     */
    observeThemeChanges() {
      const observer = new MutationObserver(() => {
        const wasDark = this.isDarkMode;
        this.detectDarkMode();

        // Update map theme if it changed
        if (wasDark !== this.isDarkMode && this.map) {
          console.log('Theme changed to:', this.isDarkMode ? 'dark' : 'light');
          this.updateMapTheme(this.isDarkMode);
        }
      });

      // Observe both html and body for class/attribute changes
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class', 'data-theme']
      });

      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ['class', 'data-theme']
      });

      // Store observer for cleanup
      this._themeObserver = observer;
    },

    /**
     * Toggle protests layer on/off
     * @param {Boolean} enabled - Whether to enable or disable protests layer
     */
    async toggleProtestsLayer(enabled) {
      if (!this.map) {
        console.warn('Map instance not available, cannot toggle protests layer');
        return;
      }

      try {
        this.protestLayerLoading = true;

        if (enabled) {
          console.log('Enabling protests layer...');

          // Load protest data if not already loaded
          if (!this.protestsData) {
            await this.loadProtestsData();
          } else {
            console.log('Using cached protest data');
          }

          // Create markers
          this.createProtestMarkers();

          // Add to active layers
          if (!this.activeLayers.includes('protests')) {
            this.activeLayers.push('protests');
          }

          // Show filter panel when layer is enabled
          this.showProtestFilters = true;

          console.log('Protests layer enabled');
        } else {
          console.log('Disabling protests layer...');

          // Remove markers
          this.clearProtestMarkers();

          // Remove from active layers
          this.activeLayers = this.activeLayers.filter(id => id !== 'protests');

          // Hide filter panel
          this.showProtestFilters = false;

          console.log('Protests layer disabled');
        }
      } catch (error) {
        console.error('Failed to toggle protests layer:', error);
        this.error = 'Failed to load protest data. Please try again.';

        // Revert the toggle state
        if (enabled) {
          this.activeLayers = this.activeLayers.filter(id => id !== 'protests');
          this.clearProtestMarkers();
        }
      } finally {
        this.protestLayerLoading = false;
      }
    },

    /**
     * Load protests data from GeoJSON file
     */
    async loadProtestsData() {
      try {
        console.log('Loading protests data from GeoJSON...');

        this.setLayerLoading('protests');
        const response = await fetch('/api/cresiteiq2/layers/static?layer=protests');
        if (!response.ok) {
          throw new Error(`Failed to load protests data: ${response.statusText}`);
        }

        this.protestsData = await response.json();
        console.log(`Loaded ${this.protestsData.features?.length || 0} protest locations`);
        this.clearLayerLoading('protests');
      } catch (error) {
        console.error('Failed to load protests data:', error);
        this.clearLayerLoading('protests');
        throw new Error('Failed to load protest data from server');
      }
    },

    /**
     * Create protest markers on map
     */
    createProtestMarkers() {
      if (!this.protestsData || !this.map) {
        console.warn('Cannot create protest markers: missing data or map');
        return;
      }

      // Clear existing markers
      this.clearProtestMarkers();

      const features = this.protestsData.features || [];
      console.log(`Creating markers for ${features.length} protest locations`);

      // Create markers for each protest location
      this.protestMarkers = features.map((feature, index) => {
        try {
          const coords = feature.geometry.coordinates;
          const marker = new google.maps.Marker({
            position: {
              lat: coords[1],
              lng: coords[0]
            },
            map: this.map,
            title: `Protest Events (${feature.properties.events?.length || 0})`,
            icon: {
              url: '/img/map/protest.png',
              scaledSize: new google.maps.Size(32, 32),
              anchor: new google.maps.Point(16, 32)
            },
            zIndex: 1000
          });

          // Add click listener
          marker.addListener('click', () => {
            this.onProtestMarkerClick(feature);
          });

          return marker;
        } catch (error) {
          console.error(`Failed to create marker for protest location ${index}:`, error);
          return null;
        }
      }).filter(marker => marker !== null);

      console.log(`Successfully created ${this.protestMarkers.length} protest markers`);

      // Apply initial filters
      this.applyProtestFilters();
    },

    /**
     * Handle protest marker click
     */
    onProtestMarkerClick(feature) {
      console.log('Protest marker clicked, events:', feature.properties.events?.length);
      this.selectedProtest = feature.properties;
      this.showProtestPopup = true;
      this.showProtestFilters = true;
    },

    /**
     * Close protest popup
     */
    closeProtestPopup() {
      this.showProtestPopup = false;
      this.selectedProtest = null;
    },

    /**
     * Update protest filters
     */
    updateProtestFilters(newFilters) {
      console.log('Updating protest filters:', newFilters);
      this.protestFilters = { ...newFilters };
      this.applyProtestFilters();
    },

    /**
     * Apply filters to protest markers
     */
    applyProtestFilters() {
      if (!this.protestMarkers || this.protestMarkers.length === 0) {
        return;
      }

      const features = this.protestsData.features || [];

      features.forEach((feature, index) => {
        const marker = this.protestMarkers[index];
        if (!marker) return;

        const events = feature.properties.events || [];

        // Filter events
        let filteredEvents = [...events];

        // Apply date range filter
        if (this.protestFilters.dateRange && this.protestFilters.dateRange.startDate && this.protestFilters.dateRange.endDate) {
          filteredEvents = this.filterProtestsByDateRange(filteredEvents, this.protestFilters.dateRange);
        }

        // Apply search text filter
        if (this.protestFilters.searchText && this.protestFilters.searchText.trim()) {
          filteredEvents = this.filterProtestsBySearchText(filteredEvents, this.protestFilters.searchText);
        }

        // Apply factors filter
        if (this.protestFilters.selectedFactors && this.protestFilters.selectedFactors.length > 0) {
          filteredEvents = this.filterProtestsByFactors(filteredEvents, this.protestFilters.selectedFactors);
        }

        // Show/hide marker based on whether it has matching events
        marker.setVisible(filteredEvents.length > 0);
      });
    },

    /**
     * Filter protests by date range
     */
    filterProtestsByDateRange(events, dateRange) {
      const startDate = this.parseFilterDate(dateRange.startDate);
      const endDate = this.parseFilterDate(dateRange.endDate);

      if (!startDate || !endDate) return events;

      endDate.setHours(23, 59, 59, 999);

      return events.filter(event => {
        if (!event.date) return false;

        const eventDate = this.parseEventDate(event.date);
        if (!eventDate) return false;

        return eventDate >= startDate && eventDate <= endDate;
      });
    },

    /**
     * Filter protests by search text
     */
    filterProtestsBySearchText(events, searchText) {
      const search = searchText.toLowerCase().trim();

      return events.filter(event => {
        const searchableText = `${event.date || ''} ${event.event_type || ''} ${event.claims_summary || ''}`.toLowerCase();
        return searchableText.includes(search);
      });
    },

    /**
     * Filter protests by factors
     */
    filterProtestsByFactors(events, selectedFactors) {
      return events.filter(event => {
        const eventText = `${event.event_type || ''} ${event.claims_summary || ''}`.toLowerCase();

        return selectedFactors.some(factor => {
          // Handle both capitalized and lowercase factors
          const keyword = factor.toLowerCase();
          return eventText.includes(keyword);
        });
      });
    },

    /**
     * Parse event date (DD-MM-YYYY format)
     */
    parseEventDate(dateStr) {
      try {
        const [day, month, year] = dateStr.split('-').map(Number);
        if (!day || !month || !year) return null;
        return new Date(year, month - 1, day);
      } catch (e) {
        console.warn('Error parsing event date:', e);
        return null;
      }
    },

    /**
     * Parse filter date (MM-DD-YYYY format)
     */
    parseFilterDate(dateStr) {
      try {
        const [month, day, year] = dateStr.split('-').map(Number);
        if (!day || !month || !year) return null;
        return new Date(year, month - 1, day);
      } catch (e) {
        console.warn('Error parsing filter date:', e);
        return null;
      }
    },

    /**
     * Clear protest markers from map
     */
    clearProtestMarkers() {
      console.log(`Clearing ${this.protestMarkers.length} protest markers`);

      this.protestMarkers.forEach(marker => {
        marker.setMap(null);
        google.maps.event.clearInstanceListeners(marker);
      });

      this.protestMarkers = [];
      console.log('Protest markers cleared');
    },



    /**
     * Filter and draw highways using Google Maps Data Layer (existing CRESiteIQ approach)
     */
    filterAndDrawHighways() {
      if (!this.map) return;

      console.log('[DRAW] filterAndDrawHighways called.');

      // First, remove all existing highway features from the map
      const featuresToRemove = [];
      this.map.data.forEach(feature => {
        if (feature.getProperty('type') === 'highway') {
          featuresToRemove.push(feature);
        }
      });
      console.log(`[DRAW] Removing ${featuresToRemove.length} highway features.`);
      featuresToRemove.forEach(feature => this.map.data.remove(feature));

      // Now, filter and draw highways based on all currently selected states
      const selectedStates = this.selectedHighwayStates;
      console.log(`[DRAW] Drawing for selected states:`, selectedStates);

      if (this.allHighwaysData && selectedStates.length > 0) {
        const selectedStateSet = new Set(selectedStates);

        // Log first feature to see structure
        if (this.allHighwaysData.features.length > 0) {
          console.log('[DRAW] Sample feature properties:', this.allHighwaysData.features[0].properties);
        }

        const filteredFeatures = this.allHighwaysData.features.filter(feature => {
          const featureState = feature.properties.state || feature.properties.STATE;
          const matches = selectedStateSet.has(featureState);
          if (!matches && this.allHighwaysData.features.indexOf(feature) === 0) {
            console.log(`[DRAW] Feature state "${featureState}" not in selected states:`, Array.from(selectedStateSet));
          }
          return matches;
        });
        console.log(`[DRAW] Total features in store: ${this.allHighwaysData.features.length}. Filtered to ${filteredFeatures.length} features for drawing.`);

        if (filteredFeatures.length > 0) {
          const filteredGeoJson = { type: "FeatureCollection", features: filteredFeatures };
          const addedFeatures = this.map.data.addGeoJson(filteredGeoJson);
          console.log(`[DRAW] Added ${addedFeatures.length} features to the map.`);

          // Set the 'type' property on each added feature so we can identify it later
          addedFeatures.forEach(feature => {
            feature.setProperty('type', 'highway');
          });
        }
      }

      // Set style for all highway features on the map
      this.map.data.setStyle(feature => {
        if (feature.getProperty('type') === 'highway') {
          return {
            strokeColor: this.highwayColor,
            strokeOpacity: 1,
            strokeWeight: this.highwayStrokeWeight,
            cursor: 'pointer'
          };
        }
        return null;
      });
    },

    /**
     * Remove highway features from map by type
     */
    removeHighwayDataFeatures() {
      if (!this.map) return;

      const featuresToRemove = [];
      this.map.data.forEach(feature => {
        if (feature.getProperty('type') === 'highway') {
          featuresToRemove.push(feature);
        }
      });

      featuresToRemove.forEach(feature => this.map.data.remove(feature));
    },

    /**
     * Handle click on highway data layer feature
     */
    onHighwayDataLayerClick(event) {
      const feature = event.feature;

      // Check if this is a highway feature
      if (feature && feature.getProperty('type') === 'highway') {
        // Get all properties from the feature
        const properties = {};
        feature.forEachProperty((value, key) => {
          properties[key] = value;
        });

        console.log('Highway clicked:', properties);

        // Show popup with highway data
        this.selectedHighway = properties;
        this.showHighwayPopup = true;
      }
    },

    /**
     * Close highway popup
     */
    closeHighwayPopup() {
      this.showHighwayPopup = false;
      this.selectedHighway = null;
    },

    /**
     * Load available US states for highway filter
     */
    async loadAvailableStates() {
      try {
        // Use a predefined list of US states
        this.availableStates = [
          { code: 'AL', name: 'Alabama' },
          { code: 'AK', name: 'Alaska' },
          { code: 'AZ', name: 'Arizona' },
          { code: 'AR', name: 'Arkansas' },
          { code: 'CA', name: 'California' },
          { code: 'CO', name: 'Colorado' },
          { code: 'CT', name: 'Connecticut' },
          { code: 'DE', name: 'Delaware' },
          { code: 'FL', name: 'Florida' },
          { code: 'GA', name: 'Georgia' },
          { code: 'HI', name: 'Hawaii' },
          { code: 'ID', name: 'Idaho' },
          { code: 'IL', name: 'Illinois' },
          { code: 'IN', name: 'Indiana' },
          { code: 'IA', name: 'Iowa' },
          { code: 'KS', name: 'Kansas' },
          { code: 'KY', name: 'Kentucky' },
          { code: 'LA', name: 'Louisiana' },
          { code: 'ME', name: 'Maine' },
          { code: 'MD', name: 'Maryland' },
          { code: 'MA', name: 'Massachusetts' },
          { code: 'MI', name: 'Michigan' },
          { code: 'MN', name: 'Minnesota' },
          { code: 'MS', name: 'Mississippi' },
          { code: 'MO', name: 'Missouri' },
          { code: 'MT', name: 'Montana' },
          { code: 'NE', name: 'Nebraska' },
          { code: 'NV', name: 'Nevada' },
          { code: 'NH', name: 'New Hampshire' },
          { code: 'NJ', name: 'New Jersey' },
          { code: 'NM', name: 'New Mexico' },
          { code: 'NY', name: 'New York' },
          { code: 'NC', name: 'North Carolina' },
          { code: 'ND', name: 'North Dakota' },
          { code: 'OH', name: 'Ohio' },
          { code: 'OK', name: 'Oklahoma' },
          { code: 'OR', name: 'Oregon' },
          { code: 'PA', name: 'Pennsylvania' },
          { code: 'RI', name: 'Rhode Island' },
          { code: 'SC', name: 'South Carolina' },
          { code: 'SD', name: 'South Dakota' },
          { code: 'TN', name: 'Tennessee' },
          { code: 'TX', name: 'Texas' },
          { code: 'UT', name: 'Utah' },
          { code: 'VT', name: 'Vermont' },
          { code: 'VA', name: 'Virginia' },
          { code: 'WA', name: 'Washington' },
          { code: 'WV', name: 'West Virginia' },
          { code: 'WI', name: 'Wisconsin' },
          { code: 'WY', name: 'Wyoming' }
        ];
      } catch (error) {
        console.error('Failed to load available states:', error);
      }
    },

    /**
     * Toggle highway layer on/off (using Data Layer like existing CRESiteIQ)
     * @param {Boolean} enabled - Whether to enable or disable the layer
     */
    async toggleHighwayLayer(enabled) {
      if (!this.map) {
        console.warn('Map instance not available, cannot toggle highway layer');
        return;
      }

      if (enabled) {
        console.log('Enabling highway layer...');

        // Load available states if not already loaded
        if (this.availableStates.length === 0) {
          await this.loadAvailableStates();
        }

        // Show filter panel
        this.showHighwayFilter = true;

        // Add to active layers
        if (!this.activeLayers.includes('highways')) {
          this.activeLayers.push('highways');
        }

        console.log('Highway layer enabled');
      } else {
        console.log('Disabling highway layer...');

        // Hide filter panel
        this.showHighwayFilter = false;

        // Clear selected states and data
        this.selectedHighwayStates = [];
        this.allHighwaysData = { type: 'FeatureCollection', features: [] };
        this.loadedHighwayStates = {};

        // Remove highway features from map
        this.removeHighwayDataFeatures();

        // Remove from active layers
        this.activeLayers = this.activeLayers.filter(id => id !== 'highways');

        console.log('Highway layer disabled');
      }
    },

    /**
     * Load waterway data from GeoJSON file
     */
    async loadWaterwayData() {
      try {
        // Return cached data if already loaded
        if (this.waterwaysData) {
          console.log('Using cached waterways data');
          return this.waterwaysData;
        }

        console.log('Loading waterways data from GeoJSON...');

        this.setLayerLoading('waterways');
        const response = await fetch('/api/cresiteiq2/layers/static?layer=waterways');
        if (!response.ok) {
          throw new Error(`Failed to load waterways data: ${response.statusText}`);
        }

        this.waterwaysData = await response.json();
        console.log(`Loaded ${this.waterwaysData.features?.length || 0} waterway features`);
        this.clearLayerLoading('waterways');
        return this.waterwaysData;
      } catch (error) {
        console.error('Failed to load waterways data:', error);
        this.clearLayerLoading('waterways');
        throw new Error('Failed to load waterway data from server');
      }
    },

    /**
     * Get waterway color based on classification and theme
     * @param {Number} classification - Waterway classification (1, 2, or 3)
     * @returns {String} - Color hex code
     */
    getWaterwayColor(classification) {
      if (this.isDarkMode) {
        // Dark theme colors
        switch (classification) {
          case 1: return '#06b6d4'; // Cyan-500
          case 2: return '#0891b2'; // Cyan-600
          case 3: return '#0e7490'; // Cyan-700
          default: return '#0ea5e9'; // Sky-500
        }
      } else {
        // Light theme colors
        switch (classification) {
          case 1: return '#0ea5e9'; // Sky-500
          case 2: return '#0284c7'; // Sky-600
          case 3: return '#0369a1'; // Sky-700
          default: return '#0ea5e9'; // Sky-500
        }
      }
    },

    /**
     * Get waterway stroke weight based on classification
     * @param {Number} classification - Waterway classification (1, 2, or 3)
     * @returns {Number} - Stroke weight in pixels
     */
    getWaterwayStrokeWeight(classification) {
      switch (classification) {
        case 1: return 4;
        case 2: return 3;
        case 3: return 2;
        default: return 3;
      }
    },

    /**
     * Create waterway polylines on map
     */
    createWaterwayPolylines() {
      console.log('[WATERWAYS] createWaterwayPolylines called');
      console.log('[WATERWAYS] waterwaysData:', this.waterwaysData);
      console.log('[WATERWAYS] map:', this.map);

      if (!this.waterwaysData || !this.map) {
        console.warn('[WATERWAYS] Cannot create waterway polylines: missing data or map');
        return;
      }

      // Clear existing polylines
      this.clearWaterwayPolylines();

      const features = this.waterwaysData.features || [];
      console.log(`[WATERWAYS] Creating polylines for ${features.length} waterway features`);

      if (features.length === 0) {
        console.warn('[WATERWAYS] No features found in waterwaysData');
        return;
      }

      let successCount = 0;
      let errorCount = 0;

      // Create polylines for each waterway feature
      features.forEach((feature, index) => {
        try {
          if (!feature.geometry || !feature.geometry.coordinates) {
            console.warn(`[WATERWAYS] Feature ${index} missing geometry or coordinates`);
            errorCount++;
            return;
          }

          const coords = feature.geometry.coordinates;
          const properties = feature.properties || {};
          const classification = parseInt(properties.ClassWtrw) || 2;

          // Convert coordinates to Google Maps LatLng format
          const path = coords.map(coord => ({
            lat: coord[1],
            lng: coord[0]
          }));

          if (path.length === 0) {
            console.warn(`[WATERWAYS] Feature ${index} has no valid coordinates`);
            errorCount++;
            return;
          }

          // Create polyline
          const polyline = new google.maps.Polyline({
            path: path,
            strokeColor: this.getWaterwayColor(classification),
            strokeOpacity: 0.7,
            strokeWeight: this.getWaterwayStrokeWeight(classification),
            map: this.map,
            zIndex: 100,
            clickable: true,
            geodesic: false
          });

          // Store waterway data with polyline (normalize property names)
          polyline.set('waterwayData', {
            name: properties.Name || 'Unknown Waterway',
            classification: classification,
            length_miles: properties.MilesGeo || 'N/A',
            link_number: properties.LINKNUM || 'N/A'
          });

          // Add click listener
          polyline.addListener('click', (event) => {
            this.showWaterwayInfo(polyline, event.latLng);
          });

          // Add hover listeners for opacity effect
          polyline.addListener('mouseover', () => {
            polyline.setOptions({ strokeOpacity: 1.0 });
          });

          polyline.addListener('mouseout', () => {
            polyline.setOptions({ strokeOpacity: 0.7 });
          });

          this.waterwayPolylines.push(polyline);
          successCount++;

          // Log progress every 100 features
          if ((index + 1) % 100 === 0) {
            console.log(`[WATERWAYS] Created ${index + 1}/${features.length} waterway polylines`);
          }
        } catch (error) {
          console.error(`[WATERWAYS] Failed to create polyline for waterway feature ${index}:`, error);
          errorCount++;
        }
      });

      console.log(`[WATERWAYS] Successfully created ${successCount} waterway polylines (${errorCount} errors)`);
      console.log(`[WATERWAYS] Total polylines in array: ${this.waterwayPolylines.length}`);

      // Verify polylines are on the map
      if (this.waterwayPolylines.length > 0) {
        const firstPolyline = this.waterwayPolylines[0];
        console.log('[WATERWAYS] First polyline map:', firstPolyline.getMap());
        console.log('[WATERWAYS] First polyline visible:', firstPolyline.getVisible());
      }
    },

    /**
     * Show waterway info popup
     * @param {google.maps.Polyline} polyline - The clicked polyline
     * @param {google.maps.LatLng} latLng - Click position
     */
    showWaterwayInfo(polyline, latLng) {
      const waterwayData = polyline.get('waterwayData');

      if (!waterwayData) {
        console.warn('No waterway data found for polyline');
        return;
      }

      console.log('Waterway clicked:', waterwayData.name);

      this.selectedWaterway = {
        ...waterwayData,
        position: {
          lat: latLng.lat(),
          lng: latLng.lng()
        }
      };

      this.showWaterwayPopup = true;
    },

    /**
     * Close waterway popup
     */
    closeWaterwayPopup() {
      this.showWaterwayPopup = false;
      this.selectedWaterway = null;
    },

    /**
     * Clear waterway polylines from map
     */
    clearWaterwayPolylines() {
      console.log(`Clearing ${this.waterwayPolylines.length} waterway polylines`);

      this.waterwayPolylines.forEach(polyline => {
        polyline.setMap(null);
        google.maps.event.clearInstanceListeners(polyline);
      });

      this.waterwayPolylines = [];
      console.log('Waterway polylines cleared');
    },

    /**
     * Toggle waterways layer on/off
     * @param {Boolean} enabled - Whether to enable or disable the layer
     */
    async toggleWaterwaysLayer(enabled) {
      console.log('[WATERWAYS] toggleWaterwaysLayer called with enabled:', enabled);

      if (!this.map) {
        console.warn('[WATERWAYS] Map instance not available, cannot toggle waterways layer');
        return;
      }

      try {
        this.layerLoading = true;

        if (enabled) {
          console.log('[WATERWAYS] Enabling waterways layer...');

          // Load waterways data if not already loaded
          console.log('[WATERWAYS] Calling loadWaterwayData...');
          await this.loadWaterwayData();
          console.log('[WATERWAYS] loadWaterwayData completed, waterwaysData:', this.waterwaysData ? `${this.waterwaysData.features?.length} features` : 'null');

          // Create polylines
          console.log('[WATERWAYS] Calling createWaterwayPolylines...');
          this.createWaterwayPolylines();
          console.log('[WATERWAYS] createWaterwayPolylines completed');

          // Add to active layers
          if (!this.activeLayers.includes('waterways')) {
            this.activeLayers.push('waterways');
          }

          console.log('[WATERWAYS] Waterways layer enabled successfully');
        } else {
          console.log('[WATERWAYS] Disabling waterways layer...');

          // Clear polylines
          this.clearWaterwayPolylines();

          // Remove from active layers
          this.activeLayers = this.activeLayers.filter(id => id !== 'waterways');

          console.log('[WATERWAYS] Waterways layer disabled');
        }

        this.layerLoading = false;
      } catch (error) {
        console.error('[WATERWAYS] Failed to toggle waterways layer:', error);
        console.error('[WATERWAYS] Error stack:', error.stack);
        this.error = 'Failed to load waterway data. Please try again.';
        this.layerLoading = false;

        // Revert the toggle state
        if (enabled) {
          this.activeLayers = this.activeLayers.filter(id => id !== 'waterways');
          this.clearWaterwayPolylines();
        }
      }
    },

    /**
     * Load rail data from GeoJSON file
     */
    async loadRailData() {
      // Check cache first
      if (this.railData) {
        console.log('[RAIL] Using cached rail data');
        return this.railData;
      }

      try {
        console.log('[RAIL] Loading rail data...');
        this.setLayerLoading('rail');
        const response = await axios.get('/api/cresiteiq2/layers/static?layer=rail');
        this.railData = response.data;
        console.log(`[RAIL] Loaded ${response.data.features.length} rail features`);
        this.clearLayerLoading('rail');
        return response.data;
      } catch (error) {
        console.error('[RAIL] Failed to load rail data:', error);
        this.clearLayerLoading('rail');
        throw new Error('Failed to load rail data');
      }
    },

    /**
     * Get theme-aware color for rail polylines
     */
    getRailColor() {
      // Use theme-aware colors that work in both light and dark modes
      return this.isDarkMode ? '#f59e0b' : '#b45309'; // amber-500 : amber-700
    },

    /**
     * Create rail polylines from GeoJSON data
     */
    createRailPolylines() {
      if (!this.railData || !this.railData.features || !this.map) {
        console.warn('[RAIL] Cannot create rail polylines: missing data or map');
        return;
      }

      const features = this.railData.features;
      console.log(`[RAIL] Creating polylines for ${features.length} rail features`);

      let createdCount = 0;

      features.forEach((feature, index) => {
        try {
          if (feature.geometry.type !== 'LineString' && feature.geometry.type !== 'MultiLineString') {
            return;
          }

          // Handle both LineString and MultiLineString geometries
          const paths = feature.geometry.type === 'LineString'
            ? [feature.geometry.coordinates]
            : feature.geometry.coordinates;

          paths.forEach(coordinates => {
            const path = coordinates.map(coord => ({
              lat: coord[1],
              lng: coord[0]
            }));

            const polyline = new google.maps.Polyline({
              path: path,
              strokeColor: this.getRailColor(),
              strokeOpacity: 0.8,
              strokeWeight: this.railStrokeWeight,
              map: this.map,
              clickable: true
            });

            // Store feature properties with polyline
            polyline.set('railData', {
              name: feature.properties.name || feature.properties.NAME || 'Unknown Rail Line',
              type: feature.properties.type || feature.properties.TYPE || null,
              operator: feature.properties.operator || feature.properties.OPERATOR || null,
              length: feature.properties.length || feature.properties.LENGTH || null,
              properties: feature.properties // Store all properties for flexibility
            });

            // Add click listener
            polyline.addListener('click', (event) => {
              this.showRailInfo(polyline, event.latLng);
            });

            // Add hover effect
            polyline.addListener('mouseover', () => {
              polyline.setOptions({ strokeOpacity: 1.0 });
            });

            polyline.addListener('mouseout', () => {
              polyline.setOptions({ strokeOpacity: 0.8 });
            });

            this.railPolylines.push(polyline);
            createdCount++;
          });

          // Log progress every 100 features
          if ((index + 1) % 100 === 0) {
            console.log(`[RAIL] Created ${index + 1}/${features.length} rail polylines...`);
          }
        } catch (error) {
          console.error(`[RAIL] Failed to create polyline for rail feature:`, error);
        }
      });

      console.log(`[RAIL] Successfully created ${createdCount} rail polylines`);
    },

    /**
     * Show rail line information popup
     */
    showRailInfo(polyline, latLng) {
      const railData = polyline.get('railData');
      if (!railData) return;

      this.selectedRail = {
        ...railData,
        position: latLng
      };
      this.showRailPopup = true;
    },

    /**
     * Clear all rail polylines from the map
     */
    clearRailPolylines() {
      console.log(`[RAIL] Clearing ${this.railPolylines.length} rail polylines`);

      this.railPolylines.forEach(polyline => {
        polyline.setMap(null);
        google.maps.event.clearInstanceListeners(polyline);
      });

      this.railPolylines = [];
      console.log('[RAIL] Rail polylines cleared');
    },

    /**
     * Close rail popup
     */
    closeRailPopup() {
      this.showRailPopup = false;
      this.selectedRail = null;
    },

    /**
     * Toggle rail layer on/off
     * @param {Boolean} enabled - Whether to enable or disable rail layer
     */
    async toggleRailLayer(enabled) {
      if (!this.map) {
        console.warn('[RAIL] Map instance not available, cannot toggle rail layer');
        return;
      }

      try {
        this.layerLoading = true;

        if (enabled) {
          console.log('[RAIL] Enabling rail layer...');

          // Load rail data if not already loaded
          console.log('[RAIL] Calling loadRailData...');
          await this.loadRailData();
          console.log('[RAIL] loadRailData completed, railData:', this.railData ? `${this.railData.features?.length} features` : 'null');

          // Create polylines
          console.log('[RAIL] Calling createRailPolylines...');
          this.createRailPolylines();
          console.log('[RAIL] createRailPolylines completed');

          // Add to active layers
          if (!this.activeLayers.includes('rail')) {
            this.activeLayers.push('rail');
          }

          console.log('[RAIL] Rail layer enabled successfully');
        } else {
          console.log('[RAIL] Disabling rail layer...');

          // Clear polylines
          this.clearRailPolylines();

          // Remove from active layers
          this.activeLayers = this.activeLayers.filter(id => id !== 'rail');

          console.log('[RAIL] Rail layer disabled');
        }

        this.layerLoading = false;
      } catch (error) {
        console.error('[RAIL] Failed to toggle rail layer:', error);
        console.error('[RAIL] Error stack:', error.stack);
        this.error = 'Failed to load rail data. Please try again.';
        this.layerLoading = false;

        // Revert the toggle state
        if (enabled) {
          this.activeLayers = this.activeLayers.filter(id => id !== 'rail');
          this.clearRailPolylines();
        }
      }
    },

    /**
     * Toggle travel time layer on/off
     * @param {Boolean} enabled - Whether to enable or disable travel time layer
     */
    async toggleTravelTimeLayer(enabled) {
      if (!this.map) {
        console.warn('Map instance not available, cannot toggle travel time layer');
        return;
      }

      try {
        this.travelTimeLayerLoading = true;

        if (enabled) {
          console.log('[Travel Time] Enabling travel time layer...');

          // Add to active layers FIRST
          if (!this.activeLayers.includes('travel_time')) {
            this.activeLayers.push('travel_time');
            console.log('[Travel Time] Added to activeLayers:', this.activeLayers);
          }

          // Show filter panel
          this.showTravelTimeFilters = true;

          // Always keep collapsed so it only shows in Active Filters Panel (left side)
          this.travelTimeFiltersCollapsed = true;

          console.log('[Travel Time] State after enable:', {
            activeLayers: this.activeLayers,
            showTravelTimeFilters: this.showTravelTimeFilters,
            travelTimeFiltersCollapsed: this.travelTimeFiltersCollapsed,
            activeFiltersState: Object.keys(this.activeFiltersState)
          });

          // Load available buildings AFTER setting state
          await this.loadAvailableBuildings();

          console.log('[Travel Time] Travel time layer enabled - showing in Active Filters Panel');
        } else {
          console.log('[Travel Time] Disabling travel time layer...');

          // Clear polygons
          this.clearTravelTimePolygons();

          // Remove from active layers
          this.activeLayers = this.activeLayers.filter(id => id !== 'travel_time');

          // Hide filter panel to remove from activeFiltersState
          this.showTravelTimeFilters = false;

          console.log('[Travel Time] Travel time layer disabled - removed from Active Filters Panel');
        }
      } catch (error) {
        console.error('Failed to toggle travel time layer:', error);
        this.error = 'Failed to toggle travel time layer. Please try again.';

        // Revert the toggle state
        if (enabled) {
          this.activeLayers = this.activeLayers.filter(id => id !== 'travel_time');
          this.clearTravelTimePolygons();
          this.showTravelTimeFilters = false;
        }
      } finally {
        this.travelTimeLayerLoading = false;
      }
    },

    /**
     * Load available buildings for travel time dropdown
     */
    async loadAvailableBuildings() {
      try {
        console.log('Loading buildings for travel time layer...');

        // Get current map bounds
        const bounds = this.map ? this.map.getBounds() : null;

        // First, try to use the loaded site markers (same as old cresiteiq)
        if (this.sitesData && this.sitesData.length > 0) {
          let buildings = this.sitesData
            .filter(site => site.building?.latitude && site.building?.longitude)
            .map(site => ({
              id: site.building.id,
              address: this.formatBuildingAddress(site.building),
              latitude: parseFloat(site.building.latitude),
              longitude: parseFloat(site.building.longitude),
              city: site.building.city,
              state: site.building.state
            }));

          // Filter buildings by current map viewport if bounds are available
          if (bounds) {
            buildings = buildings.filter(building => {
              const position = new google.maps.LatLng(building.latitude, building.longitude);
              return bounds.contains(position);
            });
            console.log(`Filtered to ${buildings.length} buildings within current viewport`);
          }

          this.availableBuildings = buildings;
          console.log(`Loaded ${buildings.length} buildings from site markers`);
          return;
        }

        // Fallback: Get buildings from reatlas store
        if (window.$store && window.$store.state && window.$store.state.reatlas) {
          const reatlas = window.$store.state.reatlas;

          if (reatlas.filteredMarkers && reatlas.filteredMarkers.length > 0) {
            let buildings = reatlas.filteredMarkers
              .filter(marker => marker.type !== 'employee')
              .map(marker => {
                const item = marker.item;
                const building = item.building || item;

                return {
                  id: building.id,
                  address: this.formatBuildingAddressFromMarker(marker),
                  latitude: building.latitude || building.lat,
                  longitude: building.longitude || building.lng,
                  city: building.city,
                  state: building.state
                };
              })
              .filter(b => b.latitude && b.longitude);

            // Filter buildings by current map viewport if bounds are available
            if (bounds) {
              buildings = buildings.filter(building => {
                const position = new google.maps.LatLng(building.latitude, building.longitude);
                return bounds.contains(position);
              });
              console.log(`Filtered to ${buildings.length} buildings within current viewport`);
            }

            this.availableBuildings = buildings;
            console.log(`Loaded ${buildings.length} buildings from reatlas store`);
            return;
          }
        }

        // Last resort: Fetch from API
        console.log('No site markers or reatlas store, fetching from API...');
        const response = await axios.get('/api/buildings', {
          params: {
            criteria: '' // Get all buildings
          }
        });

        if (response.data && Array.isArray(response.data)) {
          this.availableBuildings = response.data
            .filter(b => b.latitude && b.longitude)
            .map(b => ({
              id: b.id,
              address: `${b.address_line_1 || ''}, ${b.city || ''}, ${b.state || ''}`.replace(/^,\s*/, '').replace(/,\s*,/g, ',').trim(),
              latitude: parseFloat(b.latitude),
              longitude: parseFloat(b.longitude),
              city: b.city,
              state: b.state
            }));

          console.log(`Loaded ${this.availableBuildings.length} buildings from API`);
        } else {
          console.warn('No buildings returned from API');
          this.availableBuildings = [];
        }
      } catch (error) {
        console.error('Failed to load buildings:', error);
        this.availableBuildings = [];
        this.$toast?.error('Failed to load buildings for travel time layer');
      }
    },

    /**
     * Format building address from building object
     */
    formatBuildingAddress(building) {
      const parts = [];
      if (building.address_line_1) {
        parts.push(building.address_line_1);
      }
      if (building.city) {
        parts.push(building.city);
      }
      if (building.state) {
        parts.push(building.state);
      }
      return parts.join(', ');
    },

    /**
     * Format building address from marker object
     */
    formatBuildingAddressFromMarker(marker) {
      const item = marker.item;
      const building = item.building || item;

      const parts = [];
      if (building.address_line_1 || building.address) {
        parts.push(building.address_line_1 || building.address);
      }
      if (building.city) {
        parts.push(building.city);
      }
      if (building.state) {
        parts.push(building.state);
      }

      return parts.join(', ');
    },

    /**
     * Update travel time filters
     * @param {Object} filters - Updated filter values
     */
    updateTravelTimeFilters(filters) {
      // Store previous search mode to detect changes
      const previousSearchMode = this.travelTimeFilters.searchMode;

      this.travelTimeFilters = { ...filters };

      // Clean up autocomplete when switching away from location mode
      if (previousSearchMode === 'location' && filters.searchMode !== 'location') {
        this.destroyLocationAutocomplete();
      }

      // Handle building mode
      if (filters.searchMode === 'building' && filters.buildingId) {
        this.zoomToBuilding(filters.buildingId);
        this.fetchTravelTimeData();
      }

      // Handle location mode
      if (filters.searchMode === 'location' && filters.locationCoordinates) {
        this.zoomToLocation(filters.locationCoordinates);
        this.fetchTravelTimeData();
      }

      // Clear polygons if neither is selected
      if ((filters.searchMode === 'building' && !filters.buildingId) ||
          (filters.searchMode === 'location' && !filters.locationCoordinates)) {
        this.clearTravelTimePolygons();
      }
    },

    /**
     * Zoom to selected building
     * @param {Number} buildingId - Building ID to zoom to
     */
    zoomToBuilding(buildingId) {
      const building = this.availableBuildings.find(b => b.id === buildingId);
      if (building && building.latitude && building.longitude) {
        this.map.setCenter({
          lat: building.latitude,
          lng: building.longitude
        });
        this.map.setZoom(12); // Appropriate zoom level for isochrone visualization
        console.log(`Zoomed to building ${buildingId}`);
      }
    },

    /**
     * Zoom to selected location
     * @param {Object} coordinates - Location coordinates { lat: Number, lng: Number }
     */
    zoomToLocation(coordinates) {
      if (coordinates && coordinates.lat && coordinates.lng) {
        this.map.setCenter({
          lat: coordinates.lat,
          lng: coordinates.lng
        });
        this.map.setZoom(12); // Appropriate zoom level for isochrone visualization
        console.log(`Zoomed to location: ${coordinates.lat}, ${coordinates.lng}`);
      }
    },

    /**
     * Fetch travel time data from API
     */
    async fetchTravelTimeData() {
      let latitude, longitude;

      // Get coordinates based on search mode
      if (this.travelTimeFilters.searchMode === 'building') {
        if (!this.travelTimeFilters.buildingId) {
          console.log('No building selected, skipping travel time fetch');
          return;
        }

        const building = this.availableBuildings.find(
          b => b.id === this.travelTimeFilters.buildingId
        );

        if (!building || !building.latitude || !building.longitude) {
          console.warn('Selected building has no coordinates');
          return;
        }

        latitude = building.latitude;
        longitude = building.longitude;
      } else if (this.travelTimeFilters.searchMode === 'location') {
        if (!this.travelTimeFilters.locationCoordinates) {
          console.log('No location selected, skipping travel time fetch');
          return;
        }

        latitude = this.travelTimeFilters.locationCoordinates.lat;
        longitude = this.travelTimeFilters.locationCoordinates.lng;
      } else {
        console.warn('Invalid search mode');
        return;
      }

      // Add red marker at origin point
      this.addTravelTimeOriginMarker(latitude, longitude);

      try {
        this.travelTimeLayerLoading = true;
        console.log('Fetching travel time data...', this.travelTimeFilters);

        // Apply traffic condition multiplier to adjust travel time
        // The trafficMultiplier slider allows users to adjust for traffic conditions:
        // - 0.5 = Heavy traffic (slower speeds = LESS area covered = SMALLER polygon)
        // - 0.65 = Normal/Realistic traffic (default, accounts for real-world conditions)
        // - 1.0 = Light traffic (faster speeds = MORE area covered = LARGER polygon)
        //
        // The logic: Slider range is 0.5 to 1.0, multiply directly
        // - Lower slider value (0.5) = heavy traffic = 0.5 multiplier = smaller requested time = larger polygon
        // - Higher slider value (1.0) = light traffic = 1.0 multiplier = larger requested time = smaller polygon
        let adjustedTravelTime = this.travelTimeFilters.travelTime;
        const trafficMultiplier = this.travelTimeFilters.trafficMultiplier || 0.65;

        // Multiply travel time by the traffic multiplier
        // Lower multiplier (0.5) = request less time = API returns larger area (light traffic, faster speeds)
        // Higher multiplier (1.0) = request more time = API returns smaller area (heavy traffic, slower speeds)
        adjustedTravelTime = Math.round(this.travelTimeFilters.travelTime * trafficMultiplier);
        console.log(`[Travel Time] Applying traffic multiplier: ${this.travelTimeFilters.travelTime}s * ${trafficMultiplier} = ${adjustedTravelTime}s`);

        // Ensure adjusted time is within reasonable bounds (minimum 60 seconds, maximum 2 hours)
        adjustedTravelTime = Math.max(60, Math.min(7200, adjustedTravelTime));
        console.log(`[Travel Time] Final adjusted travel time: ${adjustedTravelTime}s`);

        const requestPayload = {
          type: this.travelTimeFilters.travelMode,
          travel_time: adjustedTravelTime,
          latitude: latitude,
          longitude: longitude,
          arrive_or_depart: this.travelTimeFilters.arriveOrDepart,
          arrive_or_depart_time: this.travelTimeFilters.arriveOrDepartTime
        };

        console.log('[Travel Time] API Request Payload:', requestPayload);
        console.log('[Travel Time] Transportation type being sent:', requestPayload.type);

        const response = await axios.post('/api/map/isochrone', requestPayload);

        // The API returns a JSON string, so we need to parse it
        let geoJsonData;
        if (typeof response.data === 'string') {
          geoJsonData = JSON.parse(response.data);
        } else {
          geoJsonData = response.data;
        }

        // Only render if we got valid data
        if (geoJsonData && geoJsonData.results) {
          this.renderTravelTimePolygons(geoJsonData);
          console.log('Travel time data fetched and rendered successfully');
        } else {
          console.warn('Invalid travel time data received');
          this.$toast?.warning('Unable to calculate travel time area');
        }
      } catch (error) {
        console.error('Error fetching travel time data:', error);

        // Check if it's a timeout or connection error
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          this.$toast?.error('Request timed out. Please try again.');
        } else if (error.response) {
          console.error('Response data:', error.response.data);
          console.error('Response status:', error.response.status);

          // Handle specific error codes
          if (error.response.status === 522) {
            this.$toast?.error('Service temporarily unavailable. Please try again in a moment.');
          } else {
            this.$toast?.error('Failed to load travel time data');
          }
        } else {
          this.$toast?.error('Network error. Please check your connection.');
        }

        // Keep existing polygons visible on error - don't clear them
        console.log('Keeping existing polygons visible due to error');
      } finally {
        this.travelTimeLayerLoading = false;
      }
    },

    /**
     * Render isochrone polygons on map
     * @param {Object} geoJsonData - GeoJSON data from API
     */
    renderTravelTimePolygons(geoJsonData) {
      // Clear existing polygons only (keep the marker)
      this.travelTimePolygons.forEach(polygon => {
        polygon.setMap(null);
      });
      this.travelTimePolygons = [];

      console.log('Rendering travel time polygons...', geoJsonData);

      // Parse GeoJSON and create Google Maps polygons
      if (geoJsonData.results && geoJsonData.results.length > 0) {
        geoJsonData.results.forEach(result => {
          if (result.shapes && result.shapes.length > 0) {
            result.shapes.forEach(shape => {
              const coordinates = shape.shell.map(coord => ({
                lat: coord.lat,
                lng: coord.lng
              }));

              const polygon = new google.maps.Polygon({
                paths: coordinates,
                strokeColor: '#3b82f6',
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: '#3b82f6',
                fillOpacity: 0.2,
                map: this.map
              });

              this.travelTimePolygons.push(polygon);
            });
          }
        });

        console.log(`Rendered ${this.travelTimePolygons.length} travel time polygons`);
      } else {
        console.warn('No travel time shapes in response');
      }
    },

    /**
     * Clear all travel time polygons from map
     */
    clearTravelTimePolygons() {
      console.log(`Clearing ${this.travelTimePolygons.length} travel time polygons`);

      this.travelTimePolygons.forEach(polygon => {
        polygon.setMap(null);
      });

      this.travelTimePolygons = [];

      // Clear origin marker
      if (this.travelTimeOriginMarker) {
        this.travelTimeOriginMarker.setMap(null);
        this.travelTimeOriginMarker = null;
      }

      console.log('Travel time polygons and origin marker cleared');
    },

    /**
     * Add or update red marker for travel time origin point
     * @param {Number} lat - Latitude
     * @param {Number} lng - Longitude
     */
    addTravelTimeOriginMarker(lat, lng) {
      // Clear existing marker if present
      if (this.travelTimeOriginMarker) {
        this.travelTimeOriginMarker.setMap(null);
      }

      // Create new red marker at origin point
      this.travelTimeOriginMarker = new google.maps.Marker({
        position: { lat, lng },
        map: this.map,
        title: 'Travel Time Origin',
        icon: {
          url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
          scaledSize: new google.maps.Size(40, 40)
        },
        zIndex: 1000 // Ensure marker appears above polygons
      });

      console.log(`Added travel time origin marker at ${lat}, ${lng}`);
    },

    /**
     * Initialize Google Places Autocomplete for location search
     * @param {HTMLElement} inputElement - The input element to attach autocomplete to
     */
    initializeLocationAutocomplete(inputElement) {
      if (!inputElement || !google || !google.maps || !google.maps.places) {
        console.warn('Google Maps Places API not available');
        return;
      }

      console.log('Initializing Google Places Autocomplete');

      // Create autocomplete instance
      this.locationAutocomplete = new google.maps.places.Autocomplete(inputElement, {
        types: ['geocode', 'establishment'],
        fields: ['formatted_address', 'geometry', 'name']
      });

      // Add place_changed event listener
      this.locationAutocomplete.addListener('place_changed', () => {
        const place = this.locationAutocomplete.getPlace();

        if (!place.geometry || !place.geometry.location) {
          this.$toast.error('Unable to find location');
          console.warn('Place has no geometry');
          return;
        }

        console.log('Place selected:', place);

        // Update filters with location data
        this.travelTimeFilters.locationQuery = place.formatted_address || place.name;
        this.travelTimeFilters.locationCoordinates = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        };

        // Trigger data fetch
        this.updateTravelTimeFilters(this.travelTimeFilters);
      });

      console.log('Google Places Autocomplete initialized');
    },

    /**
     * Destroy Google Places Autocomplete instance
     */
    destroyLocationAutocomplete() {
      if (this.locationAutocomplete) {
        console.log('Destroying Google Places Autocomplete');
        google.maps.event.clearInstanceListeners(this.locationAutocomplete);
        this.locationAutocomplete = null;
      }
    },

    /**
     * Enable directions layer
     */
    enableDirectionsLayer() {
      console.log('[Directions] Enabling directions layer');

      // Initialize directions mode in Vuex store
      if (this.$store && this.$store.dispatch) {
        try {
          this.$store.dispatch('enableDirectionsMode');
          console.log('[Directions] Directions mode enabled in Vuex');
        } catch (error) {
          console.error('[Directions] Failed to enable directions mode:', error);
          this.error = 'Failed to enable directions. Please refresh the page.';
          // Revert layer activation
          const index = this.activeLayers.indexOf('directions');
          if (index > -1) {
            this.activeLayers.splice(index, 1);
          }
          return;
        }
      } else {
        console.warn('[Directions] Vuex store not available');
      }

      // Add to active layers if not already present
      if (!this.activeLayers.includes('directions')) {
        this.activeLayers.push('directions');
        console.log('[Directions] Added to activeLayers array');
      }

      console.log('[Directions] Directions layer enabled successfully');
    },

    /**
     * Disable directions layer
     */
    disableDirectionsLayer() {
      console.log('[Directions] Disabling directions layer');

      // Disable directions mode in Vuex store
      if (this.$store && this.$store.dispatch) {
        try {
          this.$store.dispatch('disableDirectionsMode');
          console.log('[Directions] Directions mode disabled in Vuex');
        } catch (error) {
          console.error('[Directions] Failed to disable directions mode:', error);
          // Continue with cleanup even if this fails
        }
      } else {
        console.warn('[Directions] Vuex store not available');
      }

      // Remove from active layers
      const index = this.activeLayers.indexOf('directions');
      if (index > -1) {
        this.activeLayers.splice(index, 1);
        console.log('[Directions] Removed from activeLayers array');
      }

      // Clean up directions renderer
      if (this.directionsRenderer) {
        try {
          this.directionsRenderer.setMap(null);
          this.directionsRenderer = null;
          console.log('[Directions] DirectionsRenderer cleaned up');
        } catch (error) {
          console.error('[Directions] Failed to clean up DirectionsRenderer:', error);
          // Continue with cleanup even if this fails
        }
      }

      console.log('[Directions] Directions layer disabled successfully');
    },

    /**
     * Clear all active filters and disable associated layers
     */
    clearAllActiveFilters() {
      console.log('[Clear All] Clearing all active filters');

      // If in dataset mode, clear dataset variables instead of layer filters
      if (this.activeTab === 'filters') {
        this.onAgsVariablesUpdated({ variables: [] });
        this.selectedAgsCategory = '';
        return;
      }

      // Create a copy of activeLayers to iterate over (avoid mutation during iteration)
      const layersToClear = [...this.activeLayers];

      // Define cleanup handlers for each filter layer
      const filterHandlers = {
        travel_time: () => {
          console.log('[Clear All] Disabling travel time layer');
          this.showTravelTimeFilters = false;
          this.travelTimePolygons.forEach(polygon => polygon.setMap(null));
          this.travelTimePolygons = [];
        },
        highways: () => {
          console.log('[Clear All] Disabling highway layer');
          this.showHighwayFilter = false;
          this.selectedHighwayStates = [];
          this.removeHighwayDataFeatures();
        },
        protests: () => {
          console.log('[Clear All] Disabling protest layer');
          this.showProtestFilters = false;
          this.protestFilters = {
            dateRange: null,
            searchText: '',
            selectedFactors: []
          };
          this.clearProtestMarkers();
        },
        directions: () => {
          console.log('[Clear All] Disabling directions layer');
          this.disableDirectionsLayer();
          return; // disableDirectionsLayer handles activeLayers removal
        }
      };

      // Process each active filter layer
      layersToClear.forEach(layerId => {
        const handler = filterHandlers[layerId];
        if (handler) {
          handler();
          // Remove from activeLayers (unless handler returns early like directions)
          if (layerId !== 'directions') {
            const index = this.activeLayers.indexOf(layerId);
            if (index > -1) {
              this.activeLayers.splice(index, 1);
            }
          }
        }
      });

      console.log('[Clear All] All active filters cleared');
    },

    /**
     * Handle mouse move over map for tooltip positioning
     */
    onMapMouseMove(e) {
      this.tooltipX = e.clientX;
      this.tooltipY = e.clientY;
      this.tooltipVisible = true;
    },

    /**
     * Handle mouse leave from map to hide tooltip
     */
    onMapMouseLeave() {
      this.tooltipVisible = false;
    },

    /**
     * Create a Google Maps marker for the selected project
     * @param {Object} projectData - The project data object
     * @param {Object} coords - The coordinates {lat, lng}
     */
    createProjectMarker(projectData, coords) {
      console.log('[Create Project Marker] Creating marker for project:', projectData.name);

      // Clear existing project marker
      this.clearProjectMarker();

      // Validate coordinates
      if (!coords || !coords.lat || !coords.lng) {
        console.warn('[Create Project Marker] Invalid coordinates provided');
        return;
      }

      // Create marker
      const marker = new google.maps.Marker({
        position: coords,
        map: this.map,
        title: projectData.name,
        icon: {
          url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png',
          scaledSize: new google.maps.Size(40, 40)
        },
        zIndex: 1000
      });

      // Create info window content
      const infoWindowContent = `
        <div style="padding: 10px; min-width: 200px;">
          <h3 style="margin: 0 0 10px 0; font-size: 16px; font-weight: 600;">${projectData.name}</h3>
          <p style="margin: 5px 0;"><strong>Type:</strong> ${projectData.project_type || 'N/A'}</p>
          <p style="margin: 5px 0;"><strong>Status:</strong> ${projectData.status || 'N/A'}</p>
          <p style="margin: 5px 0;"><strong>Comps:</strong> ${projectData.comps ? projectData.comps.length : 0}</p>
          <a href="/projects/${projectData.id}" style="color: #3b82f6; text-decoration: none; margin-top: 10px; display: inline-block;">View Details →</a>
        </div>
      `;

      // Add click listener
      marker.addListener('click', () => {
        // Close any open info windows
        if (this.currentProjectInfoWindow) {
          this.currentProjectInfoWindow.close();
        }
        if (this.currentCompInfoWindow) {
          this.currentCompInfoWindow.close();
        }

        // Create and open new info window
        this.currentProjectInfoWindow = new google.maps.InfoWindow({
          content: infoWindowContent
        });

        this.currentProjectInfoWindow.open(this.map, marker);
      });

      // Store marker
      this.projectMarker = marker;

      console.log('[Create Project Marker] Project marker created successfully');
    },

    /**
     * Render Google Maps markers for all project comps
     * @param {Array} comps - Array of comp objects
     */
    renderProjectComps(comps) {
      console.log('[Render Comps] METHOD CALLED');

      // Validate comps parameter first
      if (!comps || !Array.isArray(comps)) {
        console.warn('[Render Comps] Invalid comps parameter:', comps);
        return;
      }

      console.log('[Render Comps] Rendering', comps.length, 'comps');

      // Log first comp structure safely
      if (comps.length > 0) {
        try {
          console.log('[Render Comps] First comp keys:', Object.keys(comps[0]));
        } catch (e) {
          console.error('[Render Comps] Error logging comp keys:', e);
        }
      }

      // Clear existing comp markers
      this.clearProjectCompsMarkers();

      // Filter comps with valid coordinates
      // Note: Comps have latitude/longitude directly on them, not in a building property
      let validComps = [];
      try {
        console.log('[Render Comps] About to filter comps...');
        validComps = comps.filter(comp => {
          if (!comp) return false;

          const hasLat = comp.latitude !== null && comp.latitude !== undefined;
          const hasLng = comp.longitude !== null && comp.longitude !== undefined;

          try {
            console.log('[Render Comps] Comp validation:', {
              compId: comp.id || 'unknown',
              address: comp.address_line_1 || 'no address',
              hasLat,
              hasLng,
              latitude: comp.latitude,
              longitude: comp.longitude
            });
          } catch (logError) {
            console.error('[Render Comps] Error logging comp:', logError);
          }

          return hasLat && hasLng;
        });
        console.log('[Render Comps] Filter completed successfully');
      } catch (filterError) {
        console.error('[Render Comps] Error during filter:', filterError);
        console.error('[Render Comps] Comps value at error:', comps);
        return;
      }

      console.log('[Render Comps] Valid comps with coordinates:', validComps.length);

      if (validComps.length === 0) {
        console.warn('[Render Comps] No comps have valid coordinates!');
        if (comps && comps.length > 0) {
          console.log('[Render Comps] Sample comp structure:', comps[0]);
        }
      }

      // Create marker for each comp
      validComps.forEach((comp, index) => {
        if (!comp) {
          console.warn('[Render Comps] Skipping null/undefined comp at index', index);
          return;
        }

        try {
          const coords = {
            lat: parseFloat(comp.latitude),
            lng: parseFloat(comp.longitude)
          };

          // Get marker icon based on comp score (same as old CRESiteIQ)
          const markerIcon = this.getCompMarkerIcon(comp);

          // Create marker
          const marker = new google.maps.Marker({
            position: coords,
            map: this.map,
            title: (comp && comp.address_line_1) || 'Comp Property',
            icon: {
              url: markerIcon,
              scaledSize: new google.maps.Size(32, 40),
              anchor: new google.maps.Point(16, 40)
            },
            zIndex: 900
          });

        // Removed info window content - using CompPopup instead


        // Add click listener to show CompPopup
        marker.addListener('click', () => {
          console.log('[Render Comps] Comp marker clicked:', comp.address_line_1);
          this.selectedComp = comp;
          this.showCompPopup = true;
        });

          // Store marker with comp data
          this.projectCompsMarkers.push({
            marker: marker,
            comp: comp
          });
        } catch (markerError) {
          console.error('[Render Comps] Error creating marker for comp:', comp.id, markerError);
        }
      });

      console.log('[Render Comps] Rendered', this.projectCompsMarkers.length, 'comp markers');
    },

    /**
     * Clear project marker from map
     */
    clearProjectMarker() {
      if (this.projectMarker) {
        console.log('[Clear Project Marker] Removing project marker');
        // setMap(null) detaches the marker but leaves its click listener
        // registered, which leaks over a long session. Every other marker path
        // in this file clears listeners first.
        google.maps.event.clearInstanceListeners(this.projectMarker);
        this.projectMarker.setMap(null);
        this.projectMarker = null;
      }

      if (this.currentProjectInfoWindow) {
        this.currentProjectInfoWindow.close();
        this.currentProjectInfoWindow = null;
      }
    },

    /**
     * Clear all comp markers from map
     */
    clearProjectCompsMarkers() {
      console.log('[Clear Comp Markers] Clearing', this.projectCompsMarkers.length, 'comp markers');

      this.projectCompsMarkers.forEach(item => {
        if (item.marker) {
          item.marker.setMap(null);
        }
      });

      this.projectCompsMarkers = [];

      if (this.currentCompInfoWindow) {
        this.currentCompInfoWindow.close();
        this.currentCompInfoWindow = null;
      }
    },

    /**
     * Fit map bounds to show all comp markers
     */
    fitBoundsToCompMarkers() {
      if (!this.projectCompsMarkers || this.projectCompsMarkers.length === 0) {
        console.warn('[Fit Bounds] No comp markers to fit bounds to');
        return;
      }

      console.log('[Fit Bounds] Fitting bounds to', this.projectCompsMarkers.length, 'comp markers');

      const bounds = new google.maps.LatLngBounds();

      this.projectCompsMarkers.forEach(item => {
        if (item.marker) {
          bounds.extend(item.marker.getPosition());
        }
      });

      this.map.fitBounds(bounds);

      // Ensure zoom doesn't get too close for single marker
      if (this.projectCompsMarkers.length === 1) {
        google.maps.event.addListenerOnce(this.map, 'bounds_changed', () => {
          if (this.map.getZoom() > 15) {
            this.map.setZoom(15);
          }
        });
      }
    },



  }
};
</script>

<style scoped>
/* Directions Panel Styles - Clean and Simple */
.directions-panel-container {
  margin-top: 0.5rem;
  padding: 0;
  background-color: transparent;
  border: none;
}

/* Hide the purple icon/logo */
.directions-panel-container :deep(.flex.items-center > div:first-child) {
  display: none;
}

.directions-panel-container :deep(.form-input),
.directions-panel-container :deep(input[type="text"]),
.directions-panel-container :deep(input[type="date"]),
.directions-panel-container :deep(input[type="time"]),
.directions-panel-container :deep(input[type="number"]) {
  background-color: var(--input);
  border-color: var(--border);
  color: var(--foreground);
}

.directions-panel-container :deep(.form-input:focus),
.directions-panel-container :deep(input:focus) {
  border-color: var(--primary);
  outline: none;
}

.directions-panel-container :deep(input::placeholder) {
  color: var(--muted-foreground);
}

.directions-panel-container :deep(select),
.directions-panel-container :deep(.form-select) {
  background-color: var(--input);
  border-color: var(--border);
  color: var(--foreground);
}

.directions-panel-container :deep(select:focus) {
  border-color: var(--primary);
  outline: none;
}

.directions-panel-container :deep(.bg-green-600),
.directions-panel-container :deep(.bg-blue-600) {
  background-color: var(--primary);
  color: var(--primary-foreground);
}

.directions-panel-container :deep(.bg-green-600:hover:not(:disabled)),
.directions-panel-container :deep(.bg-blue-600:hover:not(:disabled)) {
  opacity: 0.9;
}

/* Add Stop Button - Simple and Clean */
.directions-panel-container :deep(.add-stop-link),
.directions-panel-container :deep(button.add-stop-link) {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background-color: transparent;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  color: var(--foreground);
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  cursor: pointer;
  text-decoration: none;
}

.directions-panel-container :deep(.add-stop-link:hover),
.directions-panel-container :deep(button.add-stop-link:hover) {
  background-color: var(--accent);
  border-color: var(--primary);
}

.directions-panel-container :deep(.text-red-600) {
  color: var(--destructive);
}

.directions-panel-container :deep(.text-blue-600) {
  color: var(--primary);
}

.directions-panel-container :deep(.border-gray-200),
.directions-panel-container :deep(.bg-gray-100),
.directions-panel-container :deep(.bg-gray-50) {
  border-color: var(--border);
  background-color: var(--muted);
  color: var(--foreground);
}

.directions-panel-container :deep(.bg-red-100) {
  background-color: var(--destructive);
  color: var(--destructive-foreground);
  border-color: var(--destructive);
}

.directions-panel-container :deep(.text-red-700) {
  color: var(--destructive-foreground);
}

/* Marker Icons (Green and Red dots) */
.directions-panel-container :deep(.bg-blue-100) {
  background-color: #8b5cf6;
  border-color: #a78bfa;
}

.directions-panel-container :deep(.text-blue-700),
.directions-panel-container :deep(.text-blue-600) {
  color: #ffffff;
}

.directions-panel-container :deep(.border-blue-300) {
  border-color: #a78bfa;
}

/* Route Planning Icon */
.directions-panel-container :deep(.flex.items-center > div:first-child) {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  border-radius: 0.75rem;
  padding: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Spacing between sections */
.directions-panel-container :deep(.flex-col.gap-y-2) {
  gap: 1rem;
}

/* Route details box */
.directions-panel-container :deep(.border.border-gray-200) {
  background-color: rgba(60, 60, 60, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.75rem;
  padding: 1rem;
  color: #ffffff;
}

.directions-panel-container :deep(.text-blue-700) {
  color: var(--primary);
}

.directions-panel-container :deep(.border-blue-300) {
  border-color: var(--primary);
  opacity: 0.5;
}

.directions-panel-container :deep(.text-gray-700),
.directions-panel-container :deep(.text-gray-800) {
  color: var(--foreground);
}

.directions-panel-container :deep(.text-gray-600) {
  color: var(--muted-foreground);
}

.directions-panel-container :deep(.text-gray-400) {
  color: var(--muted-foreground);
  opacity: 0.7;
}

.map-page-container {
  position: relative;
  width: 100%;
  height: calc(100vh - 5rem); /* Viewport height minus header and bottom spacing (64px header + 16px bottom = 5rem) */
  background-color: var(--background);
  overflow: hidden;
  margin-bottom: 1rem; /* 16px bottom spacing as per Figma design */
}

.map-loading {
  filter: grayscale(0.5);
  opacity: 0.7;
}

/* Ensure map container fills the space */
#cresiteiq2-map {
  background-color: #e5e7eb;
  z-index: 1;
}

/* Hover Tooltip */
.hover-tooltip {
  position: fixed;
  z-index: 1000;
  pointer-events: none;
  max-width: 300px;
}

.tooltip-content {
  background-color: var(--background);
  border: 2px solid var(--border);
  border-radius: 0.5rem;
  padding: 0.75rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(12px);
  background-color: rgba(var(--background-rgb), 0.95);
}

.tooltip-title {
  font-weight: 600;
  color: var(--foreground);
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.tooltip-data {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
  font-size: 0.75rem;
}

.tooltip-label {
  color: var(--muted-foreground);
  margin-right: 0.5rem;
}

.tooltip-value {
  color: var(--foreground);
  font-weight: 500;
}

.tooltip-hint {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--border);
  font-size: 0.625rem;
  color: var(--muted-foreground);
  font-style: italic;
}

/* Risk badge classes for tooltip */
.tooltip-value.bg-green-100 {
  background-color: #d1fae5;
  color: #065f46;
  padding: 0.125rem 0.5rem;
  border-radius: 0.25rem;
}

.tooltip-value.bg-blue-100 {
  background-color: #dbeafe;
  color: #1e40af;
  padding: 0.125rem 0.5rem;
  border-radius: 0.25rem;
}

.tooltip-value.bg-yellow-100 {
  background-color: #fef3c7;
  color: #92400e;
  padding: 0.125rem 0.5rem;
  border-radius: 0.25rem;
}

.tooltip-value.bg-orange-100 {
  background-color: #ffedd5;
  color: #9a3412;
  padding: 0.125rem 0.5rem;
  border-radius: 0.25rem;
}

.tooltip-value.bg-red-100 {
  background-color: #fee2e2;
  color: #991b1b;
  padding: 0.125rem 0.5rem;
  border-radius: 0.25rem;
}

/* Zoom Controls Positioning - Left Side */
.map-page-container :deep(.zoom-controls-container) {
  position: absolute;
  top: 7rem;
  left: 1rem; /* Changed from right to left */
  z-index: 10;
  pointer-events: auto;
}

/* AGS Popup (Theme v1) */
.ags-popup-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.ags-popup-content {
  background-color: var(--background);
  border-radius: 0.75rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
}

.ags-popup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border);
}

.ags-popup-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--foreground);
  margin: 0;
}

.ags-popup-subtitle {
  font-size: 0.875rem;
  color: var(--muted-foreground);
  margin-top: 0.25rem;
}

.ags-popup-close {
  background: none;
  border: none;
  color: var(--muted-foreground);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.375rem;
  transition: all 0.2s;
}

.ags-popup-close:hover {
  background-color: var(--muted);
  color: var(--foreground);
}

.ags-popup-body {
  padding: 1.5rem;
}

.ags-popup-section {
  margin-bottom: 1.5rem;
}

.ags-popup-section:last-child {
  margin-bottom: 0;
}

.ags-popup-section-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--foreground);
  margin-bottom: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.ags-popup-data-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--border);
}

.ags-popup-data-row:last-child {
  border-bottom: none;
}

.ags-popup-label {
  font-size: 0.875rem;
  color: var(--muted-foreground);
  font-weight: 500;
}

.ags-popup-value {
  font-size: 0.875rem;
  color: var(--foreground);
  font-weight: 600;
}

/* AGS Popup Table */
.ags-popup-table {
  border: 1px solid var(--border);
  border-radius: 0.375rem;
  overflow: hidden;
}

.ags-popup-table-header {
  display: flex;
  background-color: var(--muted);
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.ags-popup-table-row {
  display: flex;
  border-top: 1px solid var(--border);
}

.ags-popup-table-cell {
  flex: 1;
  padding: 0.75rem;
  font-size: 0.875rem;
  color: var(--foreground);
}

.ags-popup-table-cell.text-right {
  text-align: right;
}

.ags-popup-table-cell.font-semibold {
  font-weight: 600;
}

/* AGS Intensity Badge */
.ags-intensity-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.ags-intensity-badge.intensity-very_low {
  background-color: #d1fae5;
  color: #065f46;
}

.ags-intensity-badge.intensity-low {
  background-color: #dcfce7;
  color: #166534;
}

.ags-intensity-badge.intensity-low_moderate {
  background-color: #fef9c3;
  color: #854d0e;
}

.ags-intensity-badge.intensity-moderate {
  background-color: #fef3c7;
  color: #92400e;
}

.ags-intensity-badge.intensity-moderate_high {
  background-color: #ffedd5;
  color: #9a3412;
}

.ags-intensity-badge.intensity-high {
  background-color: #fee2e2;
  color: #991b1b;
}

.ags-intensity-badge.intensity-very_high {
  background-color: #fecaca;
  color: #7f1d1d;
}

/* AGS Classification Badge */
.ags-classification-badge {
  padding: 0.375rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: 0.05em;
}

.ags-classification-badge.classification-very_low,
.ags-classification-badge.classification-low {
  background-color: #22c55e;
  color: white;
}

.ags-classification-badge.classification-low_moderate,
.ags-classification-badge.classification-moderate {
  background-color: #eab308;
  color: white;
}

.ags-classification-badge.classification-moderate_high,
.ags-classification-badge.classification-high,
.ags-classification-badge.classification-very_high {
  background-color: #ef4444;
  color: white;
}

/* AGS Debug Section */
.ags-debug-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background-color: #fef3c7;
  border: 2px solid #f59e0b;
  border-radius: 0.5rem;
  color: #92400e;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.ags-debug-toggle:hover {
  background-color: #fde68a;
}

.ags-debug-content {
  margin-top: 1rem;
  padding: 1rem;
  background-color: #fffbeb;
  border: 2px solid #fbbf24;
  border-radius: 0.5rem;
}

.ags-debug-step {
  margin-bottom: 1.5rem;
}

.ags-debug-step:last-child {
  margin-bottom: 0;
}

.ags-debug-step-title {
  font-size: 0.75rem;
  font-weight: 700;
  color: #92400e;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #fbbf24;
}

.ags-debug-item {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  font-size: 0.875rem;
}

.ags-debug-label {
  color: #78350f;
  font-weight: 500;
}

.ags-debug-value {
  color: #92400e;
  font-weight: 600;
  font-family: monospace;
}

.rotate-180 {
  transform: rotate(180deg);
}
</style>
